import React, { useState, useEffect, useCallback, useMemo, Suspense, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import DraggableModal from './components/DraggableModal';
import uiSchemaData from './components/Basic-Interface.json';
import './App.css';
import BatchProgressBar from "./components/BatchProgressBar";
import DragDropUploader from "./components/DragDropUploader";
import SideMenu, { AnimatedToggle } from './components/SideMenu';
const MapView = React.lazy(() => import('./components/MapView'));
import useBatchListener from './hooks/useBatchListener'; // Importa il nuovo hook
import UnmarkedImagesModal from './components/UnmarkedImagesModal';
import { useBatchRestore } from './hooks/useBatchRestore'; // Importa il hook per batch restore
import useResponsiveLayout from './hooks/useResponsiveLayout';
import ImageModal from './components/ImageModal';
import DownloadBar, { DOWNLOAD_BAR_COLLAPSED_WIDTH } from './components/DownloadBar';
const DownloadSection = React.lazy(() => import('./components/DownloadSection'));
import ResponsiveTopbar from './components/ResponsiveTopbar';
import ResponsiveModal from './components/ResponsiveModal';
import DirectWritePickerModal, { type DirectWriteSelection } from './components/DirectWritePickerModal';
const GeminiProModal = React.lazy(() => import('./components/GeminiProModal'));
import type { GeminiFallbackState } from './components/GeminiProModal';
import InsufficientCreditsModal from './components/InsufficientCreditsModal';
import Toolbar from './components/Toolbar';
const Footer = React.lazy(() => import('./components/Footer'));
const DocModal = React.lazy(() => import('./components/DocModal'));
const PurchaseModal = React.lazy(() => import('./components/PurchaseModal'));
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import { HTTP_API_URL } from './config/api';
import logoImg from './assets/logo.jpg';
import { detectDirectWriteSupport, type DirectWriteSupport } from './utils/directWrite';
import { clearDirectoryHandle, ensureWritePermission, requestWritePermission, loadDirectoryHandle, saveDirectoryHandle, type PermissionCheckResult } from './utils/directWriteStorage';
import { streamDirectWriteEntries, type DirectWriteEntry, type DirectWriteIgnoredEntry } from './utils/directWriteTraversal';
import { writeDirectToFileSystem, type DirectWriteResult, type DirectWriteTarget } from './utils/directWriteWriter';
import {
  promptForWritableHandles,
  writeToAndroidFileSystem,
  getAndroidReselectionMessage,
  type AndroidWriteTarget,
  type AndroidWriteResult,
} from './utils/directWriteAndroidWriter';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Import componenti autenticazione
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { AuthComplete } from './components/AuthComplete';
import { ProtectedRoute } from './components/ProtectedRoute';
const BetaSignupModal = React.lazy(() => import('./components/BetaSignupModal'));
const AuthModal = lazyWithRetry(
  () =>
    import('./components/PasswordlessLogin').then((module) => ({
      default: module.AuthModal,
    })),
  'lazy-retry:auth-modal'
);
import { authService, type MapMarkerResponse, type DuplicateGroupResponse, type PhotoSearchFilters } from './services/authService';
import type { MarkerData, MarkerLocationMeta, MarkerDateMeta, DuplicateGroup, DeleteMode } from './types/markers';
import { buildStateKey } from './types/forms';
import type { FormValue, FormState, UiField } from './types/forms';
import BatchRestoreModal from './components/BatchRestoreModal';

type PromptChoice = string | number | boolean | null | Record<string, unknown>;

type UploadItem = {
  name: string;
  size: number;
  content_type: string;
  file?: File;
  getFile?: () => Promise<File>;
};

type BatchPromptOption =
  | {
    value?: string;
    label?: string;
    token?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
  }
  | string;

interface BatchPrompt {
  id?: string;
  type?: string;
  message?: string;
  selected_option?: string;
  max_length?: number;
  allow_manual?: boolean;
  current_file?: string;
  hint_source?: string;
  options?: BatchPromptOption[];
  locations?: { address: string }[];
  modes?: Array<{ value?: string; label?: string } | string>;
  valid_options?: string[];
  full_image_url?: string;
  full_image_blob?: string;
  full_image?: string;
  thumbnail?: string;
  thumbnail_url?: string;
}

interface PromptRenderOption {
  value: string;
  label: string;
}

interface BatchUserPromptModalProps {
  prompt: BatchPrompt | null;
  promptError?: string;
  onSend: (type: string, choice: PromptChoice) => void | Promise<void>;
  selected: string;
  setSelected: (val: string) => void;
  setFullImageModal: (val: string | null) => void;
  processedCount?: number | null;
  totalCount?: number | null;
}

interface BatchError {
  file?: string;
  message?: string;
}

interface BatchStatus {
  user_prompt?: BatchPrompt | null;
  progress?: number;
  processed?: number;
  total?: number;
  currentFile?: string;
  errors?: BatchError[];
  compression_progress?: number;
  zip_path?: string;
  zip_ready_job_id?: string | null;
  files_status?: Record<string, FileStatusEntry>;
  uploaded_files?: Array<{ name?: string; path?: string; size?: number }>;
  ai_fallback_state?: GeminiFallbackState | null;
  original_folder_name?: string;
  cleanup_state?: BatchCleanupState;
  downloaded_at?: string;
  direct_write_completed_at?: string;
  user_id?: string;
  uploads_total_bytes?: number;
  uploads_present?: boolean;
  output_folder?: string;
}

function describePermissionState(res?: PermissionCheckResult | null): string {
  if (!res) return "";
  const parts = [
    `query=${res.queryState ?? "n/a"}`,
    `request=${res.requestState ?? "n/a"}`,
    `final=${res.state}`,
  ];
  const suffix = res.errorMessage ? `, error=${res.errorMessage}` : "";
  return `${parts.join(", ")}${suffix}`;
}

function logPermissionState(context: string, res?: PermissionCheckResult | null) {
  const desc = describePermissionState(res);
  console.info(`[DirectWrite] ${context}`, desc || "(no data)");
}

interface BatchCleanupState {
  uploads_deleted?: boolean;
  processed_deleted?: boolean;
  processed_for_ai_deleted?: boolean;
  pending_deletion?: boolean;
}

type UploadMode = 'download' | 'direct';

interface MarkerStateMeta {
  id?: string;
  lat?: number;
  lng?: number;
  filename?: string;
  original_filename?: string;
  photo_path?: string;
  imageUrl?: string;
  thumbnail?: string;
  thumbnail_url?: string;
  content_hash?: string;
  processed_at?: string;
  finalLocation?: MarkerLocationMeta;
  finalDate?: MarkerDateMeta;
  final_location?: MarkerLocationMeta; // Backend snake_case compatibility
  final_date?: MarkerDateMeta; // Backend snake_case compatibility
}

interface FileProcessingState {
  final_location?: MarkerLocationMeta;
  final_date?: MarkerDateMeta;
  marker?: MarkerStateMeta;
  output_blob?: string;
  output_relative_path?: string;
  status?: string;
  content_hash?: string;
  original_content_hash?: string;
  marker_hidden?: boolean;
  unmarked?: boolean;
  thumbnail_url?: string;
}

interface FileStatusEntry {
  original_path?: string;
  processing_state?: FileProcessingState;
  processing_step?: string;
  order_index?: number;
}

interface DownloadMarkerTargetPayload {
  batch_id: string;
  photo_id: string;
}

type DeleteDialogState =
  | { kind: "single"; marker: MarkerData }
  | { kind: "group"; marker: MarkerData };

const uiSchema = uiSchemaData as UiField[];
const DOWNLOAD_LIMIT_BYTES = 20 * 1024 * 1024 * 1024; // 20 GB
const DEFAULT_DOWNLOAD_LIMIT_MESSAGE = "Download limits reached. Wait until next month to download more files.";
const UPLOAD_STORAGE_CAP_BYTES =
  (Number(import.meta.env.VITE_UPLOAD_STORAGE_CAP_GB || "100") || 100) *
  1024 *
  1024 *
  1024;

const formatBytesForDisplay = (bytes?: number | null): string => {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return "—";
  }
  const GB = 1024 * 1024 * 1024;
  const MB = 1024 * 1024;
  const KB = 1024;
  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(2)} GB`;
  }
  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
  if (bytes >= KB) {
    return `${(bytes / KB).toFixed(0)} KB`;
  }
  return `${bytes} bytes`;
};


// --- Default initial state for each field (helper)
function getDefaultValue(field: UiField): FormValue {
  if (field.default !== undefined) {
    return field.default;
  }
  if (field.type === 'checkbox') return true;
  if (field.type === 'radio' && field.options && field.options.length > 0) return field.options[0].value;
  if (field.type === "number" && typeof field.default === "number") return field.default;
  if (field.type === "dropdown" && field.options && field.options.length > 0) {
    if (typeof field.default === "string") return field.default;
    return field.options[0].value;
  }
  if (field.type === "date" && field.default) return field.default;
  if (field.type === "modeSlider") {
    if (typeof field.default === "string") return field.default;
    const first = field.options && field.options[0];
    return first ? first.value : "balanced";
  }
  return '';
}

// --- Canonical mapping (only fallback for odd labels)
const canonicalMap: Record<string, string> = {
  "input_folder_foldertoanalyze": "input_folder",
  "ai_settings_aianalysismode": "analysis_mode",
  "output_mode_createnewfilesoroverwriteexistingones": "output_mode",
  "ai_settings_enableairecognitiononimages": "abilita_ai",
  "ai_settings_contextualrecognition": "riconoscimento_contestuale",
  "ai_settings_minimumaireliabilitythreshold": "soglia_confidenza_api",
  "ai_settings_minimumaireliabilitythresholdforcontextualanalysis": "soglia_confidenza_contestuale",
  "gps_metadata_overwriteexistinggpsmetadata": "sovrascrivi_gps",
  "date_metadata_overwriteexistingdatemetadata": "sovrascrivi_data",
  "date_metadata_manuallyenterglobaldate": "data_manuale",
  "detection_mode_automaticlocationsearch": "ricerca_automatica",
  "rename_automaticfilerenamingaccordingtocityanddate": "rinomina_file",
  "logging_savedetaillogs": "salva_log",
};

const MAP_AUTOZOOM_PREF_KEY = 'map_autozoom_enabled';
const MAP_AUTOFIT_BATCH_KEY = 'map_autofit_batch';

// --- Mappatura dai field UI al payload canonico backend (auto+robusto)
function mapUiSchemaToPayload(fields: UiField[], formState: FormState) {
  const CANONICAL_KEYS = [
    "input_folder", "analysis_mode", "output_mode", "abilita_ai", "riconoscimento_contestuale",
    "soglia_confidenza_api", "soglia_confidenza_contestuale", "sovrascrivi_gps",
    "sovrascrivi_data", "data_manuale", "ricerca_automatica", "rinomina_file", "salva_log"
  ];

  const payload: Partial<Record<string, FormValue>> = {};

  fields.forEach(field => {
    const stateKey = buildStateKey(field.section, field.label);
    const finalKey =
      canonicalMap[stateKey] ||
      canonicalMap[stateKey.replace(/\s+/g, '')] ||
      null;

    if (finalKey && CANONICAL_KEYS.includes(finalKey)) {
      const value = formState[stateKey];
      if (value !== undefined && value !== null && value !== "") {
        payload[finalKey] = value;
      }
    }
  });
  // Ensure output_mode is included and has a default
  if (!payload.output_mode) {
    const defaultKey = buildStateKey(
      'output_mode',
      'Create new files or overwrite existing ones?'
    );
    payload.output_mode = formState[defaultKey] ?? 'new';
  }
  return payload;
}

// Componente Dashboard protetto con l'app esistente
const Dashboard: React.FC = () => {
  const isPrerendering = (window as any).__PRERENDER_INJECTED?.isPrerendering;
  const initialForm: FormState = {};
  uiSchema.forEach((field: UiField) => {
    const key = buildStateKey(field.section, field.label);
    initialForm[key] = getDefaultValue(field);
  });

  const [formState, setFormState] = useState<FormState>(initialForm);
  const mapSectionRef = useRef<HTMLElement>(null);
  const startAnalysisRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [startMsg, setStartMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [batchId, setBatchId] = useState<string | null>(null);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [uploadedTotalSize, setUploadedTotalSize] = useState(0);
  const [uploadedTotalBytes, setUploadedTotalBytes] = useState<number | null>(null);
  const [showAnalysisModePopup, setShowAnalysisModePopup] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'done'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadValidationError, setUploadValidationError] = useState<string | null>(null);
  const [uploadValidationTitle, setUploadValidationTitle] = useState<string | null>(null);
  const [uploadClearToken, setUploadClearToken] = useState(0);
  const [uploadSectionResetBatch, setUploadSectionResetBatch] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<UploadMode | null>(null);
  const [showAdvancedUploader, setShowAdvancedUploader] = useState(false);
  const [uploadPaused, setUploadPaused] = useState(false);
  const uploadPausedRef = useRef(false);
  const activeUploadControllerRef = useRef<AbortController | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBetaSignupModal, setShowBetaSignupModal] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [creditsInfo, setCreditsInfo] = useState({ required: 0, available: 0 });
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const purchaseModalPrefetched = useRef(false);
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState(true);
  const [authModalIntent, setAuthModalIntent] = useState<'start' | 'upload' | 'account' | null>(null);
  const [sessionMarkers, setSessionMarkers] = useState<MarkerData[]>([]);
  const [historicalMarkers, setHistoricalMarkers] = useState<MarkerData[]>([]);
  const [historicalMarkersLoading, setHistoricalMarkersLoading] = useState(false);
  const [historicalMarkersError, setHistoricalMarkersError] = useState<string | null>(null);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [duplicateGroupsLoading, setDuplicateGroupsLoading] = useState(false);
  const [duplicateGroupsError, setDuplicateGroupsError] = useState<string | null>(null);
  const [duplicateGroupsFetched, setDuplicateGroupsFetched] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [activeDuplicateHash, setActiveDuplicateHash] = useState<string | null>(null);
  const [pendingDuplicateModeExitCheck, setPendingDuplicateModeExitCheck] = useState(false);
  const [pendingMoveTarget, setPendingMoveTarget] = useState<MarkerData | null>(null);
  const [draggedMarkerInfo, setDraggedMarkerInfo] = useState<{
    marker: MarkerData;
    source: 'map' | 'list';
    markerKey?: string;
    newPosition?: { lat: number; lng: number };
    originalPosition?: { lat: number; lng: number };
  } | null>(null);
  const [autoZoomAfterAnalysis, setAutoZoomAfterAnalysis] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem(MAP_AUTOZOOM_PREF_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const [mapAutoFitToken, setMapAutoFitToken] = useState(0);
  const [mapAutoFitMarkers, setMapAutoFitMarkers] = useState<MarkerData[]>([]);
  const lastAutoFitBatchRef = useRef<string | null>(null);
  const lastAnalysisCompleteBatchRef = useRef<string | null>(null);
  const [directWriteSupport, setDirectWriteSupport] = useState<DirectWriteSupport>({
    supported: false,
    reason: "This option is not supported by your current device/browser",
  });
  const directWriteHandleRef = useRef<FileSystemDirectoryHandle | null>(null);
  const [directWriteHandleName, setDirectWriteHandleName] = useState<string | null>(null);
  const [directWriteStatus, setDirectWriteStatus] = useState<string | null>(null);
  const [directWriteLoading, setDirectWriteLoading] = useState<boolean>(false);
  const [directWriteWriting, setDirectWriteWriting] = useState<boolean>(false);
  const [directWriteWriteProgress, setDirectWriteWriteProgress] = useState<number>(0);
  const [directWriteWriterMessage, setDirectWriteWriterMessage] = useState<string | null>(null);
  const [directWriteWriteReport, setDirectWriteWriteReport] = useState<DirectWriteResult[]>([]);
  const [directWriteHadFailure, setDirectWriteHadFailure] = useState<boolean>(false);
  const [directWriteReportOpen, setDirectWriteReportOpen] = useState<boolean>(false);
  const [analysisCompleteModalOpen, setAnalysisCompleteModalOpen] = useState<boolean>(false);
  const [directWriteHighlight, setDirectWriteHighlight] = useState<boolean>(false);
  const [directWritePickerOpen, setDirectWritePickerOpen] = useState<boolean>(false);
  const [cancelUploadsConfirmOpen, setCancelUploadsConfirmOpen] = useState(false);
  const [cancelUploadsLoading, setCancelUploadsLoading] = useState(false);
  const [cancelUploadsError, setCancelUploadsError] = useState<string | null>(null);
  const [directWriteScanItems, setDirectWriteScanItems] = useState<DirectWriteEntry[]>([]);
  const [directWriteScanWarnings, setDirectWriteScanWarnings] = useState<string[]>([]);
  const [directWriteScanStatus, setDirectWriteScanStatus] = useState<'idle' | 'scanning' | 'done' | 'error'>('idle');
  const [directWriteScanCount, setDirectWriteScanCount] = useState<number>(0);
  const [directWriteIgnoredFiles, setDirectWriteIgnoredFiles] = useState<DirectWriteIgnoredEntry[]>([]);
  const [directWriteIgnoredCount, setDirectWriteIgnoredCount] = useState<number>(0);
  const [directWriteSelectedCount, setDirectWriteSelectedCount] = useState<number>(0);
  const [directWritePreparing, setDirectWritePreparing] = useState<boolean>(false);
  const [directWritePrepareProgress, setDirectWritePrepareProgress] = useState<number>(0);
  const directWriteScanAbortRef = useRef<AbortController | null>(null);
  const directWriteDefaultAppliedRef = useRef(false);
  const DIRECT_WRITE_KEY = buildStateKey('upload_settings', 'Direct Write (in-place)');
  const directWriteEnabled = !!formState[DIRECT_WRITE_KEY];
  const normalizeDirectWritePath = useCallback(
    (rel?: string | null) => {
      if (!rel) return null;
      const trimmed = rel.replace(/^\/+/, "");
      const parts = trimmed.split("/").filter(Boolean);
      if (directWriteHandleName && parts.length) {
        const lower = directWriteHandleName.toLowerCase();
        const idx = parts.findIndex((part) => part.toLowerCase() === lower);
        if (idx >= 0) {
          parts.splice(0, idx + 1);
        }
      }
      return parts.join("/");
    },
    [directWriteHandleName]
  );

  useEffect(() => {
    if (directWriteEnabled) {
      setShowAdvancedUploader(false);
    }
  }, [directWriteEnabled]);

  useEffect(() => {
    if (!directWriteSupport.supported) {
      setShowAdvancedUploader(false);
    }
  }, [directWriteSupport.supported]);

  // --- HOOKS & DERIVED STATE (Moved Up) ---
  const { isCompactLayout, isMobileLayout } = useResponsiveLayout();
  const isDesktopLayout = !isCompactLayout;
  const { isAuthenticated, profile, user, currentUser, loading: authLoading, fetchUserProfile } = useAuthContext();
  const navigate = useNavigate();
  const { docId: urlDocId } = useParams<{ docId: string }>();
  const location = useLocation();

  const subscriptionStatus = (profile?.subscription as { status?: string } | undefined)?.status;
  const hasActiveSubscription = subscriptionStatus === 'active';
  const availableCredits = profile?.credits?.balance ?? 0;
  const monthlyDownloadUsage = profile?.profile?.monthlyDownloadUsage;
  const UPLOAD_CANCEL_THRESHOLD_UI = 3;
  const UPLOAD_CANCEL_COOLDOWN_MINUTES = 15;
  const uploadCancelState = profile?.profile?.upload_cancel as
    | { consecutive?: number; blocked_until?: number }
    | undefined;
  const uploadCancelRemainingAfterConfirm = useMemo(() => {
    if (!UPLOAD_CANCEL_THRESHOLD_UI) return null;
    const consecutive = uploadCancelState?.consecutive;
    const consecutiveSafe = typeof consecutive === 'number' && consecutive >= 0 ? consecutive : 0;
    return Math.max(0, UPLOAD_CANCEL_THRESHOLD_UI - consecutiveSafe - 1);
  }, [UPLOAD_CANCEL_THRESHOLD_UI, uploadCancelState?.consecutive]);
  const uploadCancelCooldownMinutes = useMemo(() => {
    const blockedUntil = uploadCancelState?.blocked_until;
    if (typeof blockedUntil !== 'number') return null;
    const now = Math.floor(Date.now() / 1000);
    if (blockedUntil <= now) return null;
    return Math.max(1, Math.ceil((blockedUntil - now) / 60));
  }, [uploadCancelState?.blocked_until]);

  const prefetchPurchaseModal = useCallback(() => {
    if (purchaseModalPrefetched.current) {
      return;
    }
    purchaseModalPrefetched.current = true;
    import('./components/PurchaseModal');
  }, []);
  const checkCreditsBeforeUpload = useCallback(
    (requiredCount: number) => {
      if (requiredCount <= 0) {
        return true;
      }
      const balance = profile?.credits?.balance;
      if (typeof balance !== 'number') {
        // Profilo non ancora caricato (o fetch fallito): non fare fail-open.
        // Senza il saldo non possiamo sapere se i crediti bastano; meglio
        // bloccare la selezione con un messaggio chiaro che invitare a riprovare
        // tra qualche secondo (quando il profilo sarà disponibile).
        setUploadValidationTitle("Credit check");
        setUploadValidationError(
          "Credit verification in progress. Please retry in a few seconds."
        );
        return false;
      }
      if (requiredCount <= balance) {
        return true;
      }
      setCreditsInfo({ required: requiredCount, available: balance });
      setShowInsufficientCreditsModal(true);
      return false;
    },
    [profile?.credits?.balance, setShowInsufficientCreditsModal, setCreditsInfo, setUploadValidationTitle, setUploadValidationError]
  );
  const downloadAllowanceBytes =
    typeof monthlyDownloadUsage === 'number'
      ? Math.max(DOWNLOAD_LIMIT_BYTES - monthlyDownloadUsage, 0)
      : null;

  const enableSemanticSearch = import.meta.env.VITE_ENABLE_SEMANTIC_SEARCH === 'true';
  // Unmarked images features state
  // Unmarked images features state

  // ... existing imports ...

  // Inside App component
  const [showUnmarkedDropdown, setShowUnmarkedDropdown] = useState(false);
  const [showUnmarkedImagesModal, setShowUnmarkedImagesModal] = useState(false); // NEW STATE
  const [downloadUnmarkedOnly, setDownloadUnmarkedOnly] = useState(false);
  const [showDeleteUnmarkedConfirm, setShowDeleteUnmarkedConfirm] = useState(false);
  const [isDeletingUnmarked, setIsDeletingUnmarked] = useState(false);
  const [markerChangeCounts, setMarkerChangeCounts] = useState<{ [key: string]: number }>({});
  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  const [deleteConfirmAction, setDeleteConfirmAction] = useState<"all" | "session" | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [pendingCreditCharges, setPendingCreditCharges] = useState<{ [key: string]: boolean }>({});
  const [historicalUnlocked, setHistoricalUnlocked] = useState<{ [key: string]: boolean }>({});
  const [showLimitReached, setShowLimitReached] = useState<{ marker: MarkerData; markerKey: string; newPosition: { lat: number; lng: number } } | null>(null);
  const [historicalMovePrompt, setHistoricalMovePrompt] = useState<{ marker: MarkerData; markerKey: string; resolver: (allowed: boolean) => void } | null>(null);
  const [historicalMoveCreditsWarning, setHistoricalMoveCreditsWarning] = useState(false);
  const [downloadLimitModalMessage, setDownloadLimitModalMessage] = useState<string | null>(null);
  const [showDownloadQuotaWarning, setShowDownloadQuotaWarning] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeletingMarker, setIsDeletingMarker] = useState(false);
  const [isSessionFilterActive, setIsSessionFilterActive] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [includeUnknownDates, setIncludeUnknownDates] = useState<boolean>(true);

  const [semanticQuery, setSemanticQuery] = useState<string>("");
  const [semanticResults, setSemanticResults] = useState<MarkerData[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [semanticOffset, setSemanticOffset] = useState<number>(0);
  const [semanticHasMore, setSemanticHasMore] = useState<boolean>(false);
  const [semanticModeActive, setSemanticModeActive] = useState(false);
  const [semanticPageToken, setSemanticPageToken] = useState<string | null>(null);
  const [semanticModeType, setSemanticModeType] = useState<'text' | 'image' | 'geo' | null>(null);
  const [moreLikeAnchor, setMoreLikeAnchor] = useState<MarkerData | null>(null);
  const [moreLikeThisCandidates, setMoreLikeThisCandidates] = useState<MarkerData[]>([]);
  const [visibleMoreLikeCount, setVisibleMoreLikeCount] = useState<number>(0);
  const [showSemanticSearch, setShowSemanticSearch] = useState(
    enableSemanticSearch && !isDuplicateMode && hasActiveSubscription && isDesktopLayout
  );
  const [semanticFilters, setSemanticFilters] = useState<{ country?: string; locality?: string }>({});
  type ThemePreference = 'system' | 'light' | 'dark';
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem('ui_theme_preference');
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  });
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [downloadAllIncludingHidden, setDownloadAllIncludingHidden] = useState(false);
  const [downloadBatchOverride, setDownloadBatchOverride] = useState<string | null>(null);
  const [pendingDownloadJob, setPendingDownloadJob] = useState<{ batchId: string; jobId?: string; shardIndex: number } | null>(null);
  const [isSemanticSearchExpanded, setIsSemanticSearchExpanded] = useState(false);

  // --- ADDED MISSING STATE (Layout Refactor Fixes) ---
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setDirectWriteSupport(detectDirectWriteSupport());
    } catch {
      setDirectWriteSupport({
        supported: false,
        reason: "This option is not supported by your current device/browser",
      });
    }
  }, []);

  useEffect(() => {
    if (!directWriteSupport.supported || !directWriteSupport.isWindows) return;
    if (directWriteDefaultAppliedRef.current) return;
    setFormState((prev) => {
      if (prev[DIRECT_WRITE_KEY] === true) {
        return prev;
      }
      return { ...prev, [DIRECT_WRITE_KEY]: true };
    });
    directWriteDefaultAppliedRef.current = true;
  }, [DIRECT_WRITE_KEY, directWriteSupport.supported, directWriteSupport.isWindows]);

  useEffect(() => {
    // Try to restore previously selected directory handle (if permission still granted)
    const restoreHandle = async () => {
      if (!directWriteSupport.supported) return;
      try {
        const handle = await loadDirectoryHandle();
        if (!handle) return;
        directWriteHandleRef.current = handle;
        const permission = await ensureWritePermission(handle);
        logPermissionState("Restored handle permission", permission);
        if (permission.state === "granted") {
          setDirectWriteHandleName(handle.name || "Selected folder");
          setDirectWriteStatus("Folder ready. Choose files to upload.");
        } else if (permission.state === "denied") {
          await clearDirectoryHandle();
          directWriteHandleRef.current = null;
          setDirectWriteHandleName(null);
          setDirectWriteStatus("Permission required to use Direct Write");
        } else {
          directWriteHandleRef.current = null;
          setDirectWriteHandleName(null);
          setDirectWriteStatus("Permission required to use Direct Write");
        }
      } catch (err) {
        console.warn("Unable to restore direct write handle", err);
        setDirectWriteHandleName(null);
        setDirectWriteStatus("Permission required to use Direct Write");
      }
    };
    restoreHandle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directWriteSupport.supported]);


  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.sessionStorage.getItem(MAP_AUTOFIT_BATCH_KEY);
      if (stored) {
        lastAutoFitBatchRef.current = stored;
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(MAP_AUTOZOOM_PREF_KEY, String(autoZoomAfterAnalysis));
    } catch {
      // ignore storage errors
    }
  }, [autoZoomAfterAnalysis]);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else {
        (mq as any).addListener(handler);
      }
      return () => {
        if (mq.removeEventListener) {
          mq.removeEventListener('change', handler);
        } else {
          (mq as any).removeListener(handler);
        }
      };
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const effectiveTheme = themePreference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themePreference;
    if (themePreference === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', effectiveTheme);
    }
    localStorage.setItem('ui_theme_preference', themePreference);
  }, [themePreference, systemPrefersDark]);

  // Documentation Modal State
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const shouldHideShellForPrerender = isPrerendering && isDocModalOpen;
  const [currentDoc, setCurrentDoc] = useState<{ name: string; title: string } | null>(null);

  const handleOpenDoc = (docName: string, title: string) => {
    setCurrentDoc({ name: docName, title });
    setIsDocModalOpen(true);
    // Update URL if not already there
    if (window.location.pathname !== `/guide/${docName}`) {
      navigate(`/guide/${docName}`, { replace: false });
    }
  };

  const handleCloseDoc = () => {
    setIsDocModalOpen(false);
    setCurrentDoc(null);
    // Restore homepage URL
    if (window.location.pathname !== '/') {
      navigate('/', { replace: false });
    }
  };

  // Sync modal state with URL on mount or URL change
  useEffect(() => {
    if (urlDocId) {
      // Find the title for the docId if possible, or use a default one
      // We can improve this by mapping docIds to titles
      const docTitles: Record<string, string> = {
        'what-is-smartgeotag': 'What is Smartgeotag.ai',
        'why-subscribe': 'Why Subscribe?',
        'how-to': 'How to',
        'tips-and-tricks': 'Tips & Tricks',
        'faq': 'FAQ',
        'credits': 'Credits',
        'support': 'Support',
        'privacy-policy': 'Privacy Policy',
        'terms-of-service': 'Terms of Service',
        'gdpr-compliant': 'GDPR Compliant'
      };

      const title = docTitles[urlDocId] || urlDocId.replace(/-/g, ' ');
      if (!isDocModalOpen || currentDoc?.name !== urlDocId) {
        setCurrentDoc({ name: urlDocId, title });
        setIsDocModalOpen(true);
      }
    } else if (isDocModalOpen && !urlDocId && location.pathname.startsWith('/guide/')) {
      // Handle case where user navigated back to / but modal is still open? 
      // Actually if URL is / the else if below handles it.
    } else if (isDocModalOpen && !location.pathname.startsWith('/guide/')) {
      setIsDocModalOpen(false);
      setCurrentDoc(null);
    }
  }, [urlDocId, location.pathname]);

  const handleClearDirectWrite = useCallback(async (options?: { silent?: boolean; preserveReport?: boolean }) => {
    const silent = options?.silent ?? false;
    const preserveReport = options?.preserveReport ?? false;
    try {
      await clearDirectoryHandle();
    } catch {
      // ignore
    }
    directWriteScanAbortRef.current?.abort();
    directWriteScanAbortRef.current = null;
    directWriteHandleRef.current = null;
    setDirectWriteHandleName(null);
    setDirectWriteStatus(silent ? null : "Direct Write selection cleared");
    setDirectWriteWriteProgress(0);
    if (!preserveReport) {
      setDirectWriteWriterMessage(null);
      setDirectWriteWriteReport([]);
    }
    setDirectWriteHadFailure(false);
    if (!preserveReport) {
      setDirectWriteReportOpen(false);
    }
    setDirectWriteHighlight(false);
    setDirectWritePickerOpen(false);
    setDirectWriteScanItems([]);
    setDirectWriteScanWarnings([]);
    setDirectWriteScanStatus('idle');
    setDirectWriteScanCount(0);
    setDirectWriteIgnoredFiles([]);
    setDirectWriteIgnoredCount(0);
    setDirectWriteSelectedCount(0);
    setDirectWritePreparing(false);
    setDirectWritePrepareProgress(0);
  }, []);

  const resetUploadSection = useCallback(
    async (options?: { preserveDirectWriteReport?: boolean }) => {
      if (batchId) {
        setUploadSectionResetBatch(batchId);
      }
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
      setUploadStatus('idle');
      setUploadProgress(0);
      setUploadPaused(false);
      uploadPausedRef.current = false;
      setUploadMode(null);
      setShowAdvancedUploader(false);
      setUploadValidationError(null);
      setUploadValidationTitle(null);
      setUploadClearToken((prev) => prev + 1);
      setStartMsg(null);
      setErrorMsg(null);
      setAnalysisCompleteModalOpen(false);
      await handleClearDirectWrite({ silent: true, preserveReport: options?.preserveDirectWriteReport ?? true });
    },
    [batchId, handleClearDirectWrite]
  );

  // Hook per accedere allo stato di autenticazione (Moved Up)
  const storageUsageBytes = profile?.profile?.storageUsageBytes ?? 0;
  const resolvedUserId = useMemo(() => {
    if (user?.uid) {
      return user.uid;
    }
    const currentUser = authService.getCurrentUser();
    return currentUser?.uid ?? null;
  }, [user?.uid]);
  const batchStorageKey = useMemo(() => {
    if (!resolvedUserId) {
      return null;
    }
    return `geotag_active_batch_${resolvedUserId}`;
  }, [resolvedUserId]);
  const uploadModeStorageKey = useMemo(() => {
    if (!resolvedUserId) {
      return null;
    }
    return `geotag_upload_mode_${resolvedUserId}`;
  }, [resolvedUserId]);
  const readStoredBatchId = useCallback((): string | null => {
    if (!batchStorageKey || typeof window === 'undefined') {
      return null;
    }
    try {
      return localStorage.getItem(batchStorageKey);
    } catch (error) {
      console.error('Unable to read stored batch id:', error);
      return null;
    }
  }, [batchStorageKey]);
  const readStoredUploadMode = useCallback((): { batchId: string; mode: UploadMode } | null => {
    if (!uploadModeStorageKey || typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = localStorage.getItem(uploadModeStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { batchId?: string; mode?: UploadMode };
      if (!parsed?.batchId || (parsed.mode !== 'download' && parsed.mode !== 'direct')) {
        return null;
      }
      return { batchId: parsed.batchId, mode: parsed.mode };
    } catch (error) {
      console.error('Unable to read stored upload mode:', error);
      return null;
    }
  }, [uploadModeStorageKey]);
  const writeStoredUploadMode = useCallback(
    (value: { batchId: string; mode: UploadMode } | null) => {
      if (!uploadModeStorageKey || typeof window === 'undefined') {
        return;
      }
      try {
        if (value) {
          localStorage.setItem(uploadModeStorageKey, JSON.stringify(value));
        } else {
          localStorage.removeItem(uploadModeStorageKey);
        }
      } catch (error) {
        console.error('Unable to persist upload mode:', error);
      }
    },
    [uploadModeStorageKey]
  );

  // Hook per batch restore sicuro
  const {
    showBatchRestoreModal,
    storedBatchInfo,
    isCheckingBatchStatus,
    handleSecureBatchRestore,
    handleCloseBatchRestoreModal,
    handleIgnoreStoredBatch,
    clearStoredBatch
  } = useBatchRestore({
    batchStorageKey,
    readStoredBatchId,
    setBatchId,
    onBatchStatusChecked: (info) => {
      if (!info) {
        return;
      }
      const storedMode = readStoredUploadMode();
      const storedBatchId = readStoredBatchId();
      if (storedMode && storedBatchId && storedMode.batchId === storedBatchId) {
        setUploadMode(storedMode.mode);
      } else {
        setUploadMode(null);
      }
      const inferredCount = info.total_files ?? info.uploads_count;
      if (typeof inferredCount === 'number' && inferredCount > 0) {
        setFilesUploaded(true);
        setUploadedFileCount(inferredCount);
      }
      if (typeof info.uploads_total_bytes === 'number') {
        const bytes = info.uploads_total_bytes;
        setUploadedTotalBytes(bytes);
        setUploadedTotalSize(Number((bytes / (1024 * 1024)).toFixed(2)));
      }
      if (info.status === 'waiting_start') {
        // Il restore di un batch caricato ma mai avviato deve riattivare il
        // bottone "Start analysis". Senza questo flag, dopo il refresh seguito
        // dell'acquisto di crediti e del ripristino, uploadStatus resta 'idle'
        // e il bottone (disabled={uploadStatus !== 'done'}) rimane disattivato,
        // costringendo l'utente a "Clear uploads"/"Delete batch".
        // Per i batch già in processing o completati NON abilitiamo Start:
        // il primo continua via recoverStaleBatch, il secondo è già finito..
        setUploadStatus('done');
        setUploadProgress(100);
      }
    }
  });

  // Hook per responsive layout (Moved Up)
  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false);
  const [isDownloadDrawerOpen, setIsDownloadDrawerOpen] = useState(false);
  const [isAccountCollapsed, setIsAccountCollapsed] = useState(() => !isCompactLayout);

  const writeStoredBatchId = useCallback((value: string | null) => {
    if (!batchStorageKey || typeof window === 'undefined') {
      return;
    }
    try {
      if (value) {
        localStorage.setItem(batchStorageKey, value);
      } else {
        localStorage.removeItem(batchStorageKey);
      }
    } catch (error) {
      console.error('Unable to persist batch id:', error);
    }
  }, [batchStorageKey]);
  const clearPersistedBatch = useCallback(() => {
    writeStoredBatchId(null);
  }, [writeStoredBatchId]);
  useEffect(() => {
    if (!batchId || !uploadMode) {
      writeStoredUploadMode(null);
      return;
    }
    writeStoredUploadMode({ batchId, mode: uploadMode });
  }, [batchId, uploadMode, writeStoredUploadMode]);
  const handleConfirmUploadCancel = useCallback(async () => {
    setCancelUploadsError(null);
    setCancelUploadsLoading(true);
    try {
      if (batchId) {
        await authService.deleteBatchStorage(batchId);
      }
      await fetchUserProfile(true).catch(() => null);
      clearPersistedBatch();
      setBatchId(null);
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
      setUploadStatus('idle');
      setUploadProgress(0);
      setUploadPaused(false);
      uploadPausedRef.current = false;
      setUploadValidationError(null);
      setUploadValidationTitle(null);
      setUploadClearToken((prev) => prev + 1);
      if (activeUploadControllerRef.current) {
        activeUploadControllerRef.current.abort();
      }
      if (directWriteEnabled) {
        await handleClearDirectWrite();
      }
      setDirectWriteStatus("Uploads cancelled. Choose a new folder to continue.");
      setCancelUploadsConfirmOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to cancel uploads.";
      setCancelUploadsError(message);
    } finally {
      setCancelUploadsLoading(false);
    }
  }, [
    batchId,
    clearPersistedBatch,
    directWriteEnabled,
    fetchUserProfile,
    handleClearDirectWrite,
  ]);
  useEffect(() => {
    if (!batchStorageKey || batchId) {
      return;
    }
    const storedBatchId = readStoredBatchId();
    if (storedBatchId) {
      setBatchId(storedBatchId);
    }
  }, [batchStorageKey, batchId, readStoredBatchId]);

  // Ripristina il batch_id dal backend quando il client non ha alcun batch
  // salvato in localStorage: copre i casi di connessione interrotta durante
  // l'init del signed upload, risposta persa, refresh inopportuno, o sessione
  // su un altro dispositivo/browser che ha comunque lasciato un pending_upload_batch.

  useEffect(() => {
    if (!batchStorageKey || batchId) {
      return;
    }
    const storedBatchId = readStoredBatchId();
    if (storedBatchId) {
      return; // Il flusso di restore standard (localStorage) gestisce già questo caso.
    }
    let cancelled = false;
    const checkPendingJob = async () => {
      try {
        const pendingState = await authService.getPendingJobState();
        if (cancelled) {
          return;
        }
        const pendingBatchId = pendingState?.batch_id;
        if (pendingBatchId) {
          console.log('📦 Pending job state found, restoring batch:', pendingBatchId);
          setBatchId(pendingBatchId);
        }
      } catch (error) {
        console.warn('⚠️ Unable to check pending job state:', error);
      }
    };
    checkPendingJob();
    return () => {
      cancelled = true;
    };
  }, [batchStorageKey, readStoredBatchId, setBatchId]);
  useEffect(() => {
    if (!batchStorageKey || !batchId) {
      return;
    }
    writeStoredBatchId(batchId);
  }, [batchId, batchStorageKey, writeStoredBatchId]);
  const isUserAuthenticated = isAuthenticated || Boolean(authService.getCurrentUser());
  const showMoreLikeThis = enableSemanticSearch && hasActiveSubscription;



  const shouldWarnForDownloadQuota = () =>
    filesUploaded &&
    typeof uploadedTotalBytes === 'number' &&
    typeof downloadAllowanceBytes === 'number' &&
    downloadAllowanceBytes >= 0 &&
    uploadedTotalBytes > downloadAllowanceBytes;

  useEffect(() => {
    if (isCompactLayout) {
      setIsAccountCollapsed(false);
    }
  }, [isCompactLayout]);

  const readErrorDetail = useCallback(async (response: Response): Promise<string | null> => {
    try {
      const data = await response.json();
      if (data && typeof data.detail === 'string') {
        return data.detail;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const finalizeDownload = useCallback(async (batchId: string, jobId?: string) => {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        return;
      }
      const idToken = await authService.requireAuthToken();
      await fetch(`${HTTP_API_URL}/batch/complete-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ batch_id: batchId, job_id: jobId }),
      });
    } catch (error) {
      console.error('Unable to finalize download cleanup:', error);
    }
  }, []);

  const normalizeHistoricalMarker = useCallback((entry: MapMarkerResponse): MarkerData | null => {
    // Include ALL markers (even without coordinates) if they have special flags
    // This is needed for unmarked counter to work correctly
    const hasCoordinates = typeof entry.lat === 'number' && typeof entry.lng === 'number';
    const isHidden = entry.marker_hidden === true;
    const isUnmarked = entry.unmarked === true;  // NEW: Check unmarked flag
    const belongsToCurrentBatch = Boolean(batchId && entry.batch_id && entry.batch_id === batchId);

    // Skip only if no coordinates AND not hidden AND not unmarked
    // This ensures we include:
    // 1. Markers with coordinates (normal geotagged photos)
    // 2. Hidden markers (deleted markers)
    // 3. Unmarked markers (photos that never had GPS coordinates from any session)
    if (!hasCoordinates && !isHidden && !isUnmarked) {
      return null;
    }

    return {
      id: `${entry.batch_id || 'batch'}::${entry.photo_id || entry.filename}`,
      photoId: entry.photo_id,
      batchId: entry.batch_id,
      batchName: entry.batch_name,
      lat: entry.lat ?? 0,  // Default to 0 if hidden/unmarked marker has no coords
      lng: entry.lng ?? 0,
      filename: entry.filename || '',
      originalFilename: entry.original_filename,
      photo_path: entry.photo_path,
      imageUrl: entry.image_url,
      thumbnail: entry.thumbnail ?? undefined,
      thumbnail_url: entry.thumbnail_url ?? entry.thumbnail_path ?? undefined,
      contentHash: entry.content_hash,
      processedAt: entry.processed_at,
      status: entry.status,
      outputRelativePath: entry.output_relative_path,
      finalLocation: (entry.final_location as MarkerLocationMeta) || undefined,
      finalDate: (entry.final_date as MarkerDateMeta) || undefined,
      markerHidden: isHidden || isUnmarked,  // Hide unmarked entries from the map
      unmarked: isUnmarked || isHidden,  // CRITICAL: Use unmarked flag from backend
      isHistorical: !belongsToCurrentBatch,
      isSession: belongsToCurrentBatch,
      draggable: !isHidden && !isUnmarked && (belongsToCurrentBatch || hasActiveSubscription),
    };
  }, [hasActiveSubscription, batchId]);

  const normalizeDuplicateGroup = useCallback(
    (group: DuplicateGroupResponse): DuplicateGroup | null => {
      const normalizedMarkers = (group.markers || [])
        .map(normalizeHistoricalMarker)
        .filter((marker): marker is MarkerData => Boolean(marker));

      if (!normalizedMarkers.length) {
        return null;
      }

      return {
        contentHash: group.content_hash,
        groupSize: group.group_size ?? normalizedMarkers.length,
        markers: normalizedMarkers,
      };
    },
    [normalizeHistoricalMarker]
  );



  const fetchHistoricalMarkers = useCallback(async () => {
    if (!isAuthenticated || !hasActiveSubscription) {
      setHistoricalMarkers([]);
      setHistoricalMarkersError(null);
      setHistoricalMarkersLoading(false);
      return;
    }

    setHistoricalMarkersLoading(true);
    setHistoricalMarkersError(null);
    try {
      const apiMarkers = await authService.fetchMapMarkers();
      const normalized = apiMarkers
        .map(normalizeHistoricalMarker)
        .filter((marker): marker is MarkerData => Boolean(marker));
      setHistoricalMarkers(normalized);
    } catch (error) {
      console.error('Error fetching historical markers:', error);
      setHistoricalMarkersError('Unable to load historical markers at the moment.');
    } finally {
      setHistoricalMarkersLoading(false);
    }
  }, [isAuthenticated, hasActiveSubscription, normalizeHistoricalMarker]);

  const computeDuplicateGroups = useCallback((markers: MarkerData[]): DuplicateGroup[] => {
    const grouped = markers.reduce<Record<string, MarkerData[]>>((acc, marker) => {
      const hash = marker.contentHash;
      if (!hash) {
        return acc;
      }
      if (!acc[hash]) {
        acc[hash] = [];
      }
      acc[hash].push(marker);
      return acc;
    }, {});
    return Object.entries(grouped)
      .filter(([, entries]) => entries.length >= 2)
      .map(([hash, entries]) => ({
        contentHash: hash,
        groupSize: entries.length,
        markers: entries,
      }));
  }, []);

  const fetchDuplicateGroups = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    if (!hasActiveSubscription) {
      const sessionGroups = computeDuplicateGroups(sessionMarkers);
      setDuplicateGroups(sessionGroups);
      setDuplicateGroupsError(null);
      setDuplicateGroupsLoading(false);
      setDuplicateGroupsFetched(true);
      return;
    }

    setDuplicateGroupsLoading(true);
    setDuplicateGroupsError(null);
    try {
      const apiGroups = await authService.fetchDuplicateGroups();
      const normalizedGroups = apiGroups
        .map(normalizeDuplicateGroup)
        .filter((group): group is DuplicateGroup => Boolean(group));
      setDuplicateGroups(normalizedGroups);
      setDuplicateGroupsFetched(true);
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      setDuplicateGroupsError('Unable to load duplicate groups right now.');
    } finally {
      setDuplicateGroupsLoading(false);
    }
  }, [isAuthenticated, hasActiveSubscription, normalizeDuplicateGroup, computeDuplicateGroups, sessionMarkers]);

  const buildSemanticFilters = useCallback((): PhotoSearchFilters => {
    const filters: PhotoSearchFilters = {
      include_unknown_dates: includeUnknownDates,
    };
    if (dateFrom) {
      filters.date_from = dateFrom;
    }
    if (dateTo) {
      filters.date_to = dateTo;
    }
    if (semanticFilters.country) {
      filters.country_code = semanticFilters.country;
    }
    if (semanticFilters.locality) {
      filters.locality = semanticFilters.locality;
    }
    if (isSessionFilterActive && batchId) {
      filters.batch_id = batchId;
    }
    return filters;
  }, [includeUnknownDates, dateFrom, dateTo, semanticFilters.country, semanticFilters.locality, isSessionFilterActive, batchId]);

  const combinedMarkers = useMemo(() => {
    const filteredHistorical =
      batchId === null
        ? historicalMarkers
        : historicalMarkers.filter((marker) => marker.batchId !== batchId);

    const visibleHistorical = filteredHistorical.filter(m => !m.markerHidden && !m.unmarked);
    const visibleSession = sessionMarkers.filter(m => !m.markerHidden && !m.unmarked);

    return [...visibleHistorical, ...visibleSession];
  }, [historicalMarkers, sessionMarkers, batchId]);

  const shouldFilterSessionOnly = hasActiveSubscription && isSessionFilterActive;

  const parsedDateFrom = useMemo(() => {
    if (!dateFrom) return null;
    const iso = `${dateFrom}T00:00:00Z`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [dateFrom]);

  const parsedDateTo = useMemo(() => {
    if (!dateTo) return null;
    const iso = `${dateTo}T23:59:59.999Z`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [dateTo]);

  const hasDateFilter = useMemo(
    () => Boolean(dateFrom || dateTo) || !includeUnknownDates,
    [dateFrom, dateTo, includeUnknownDates]
  );

  const getMarkerDate = useCallback((marker: MarkerData): Date | null => {
    const fd = marker.finalDate as MarkerDateMeta | undefined;
    if (!fd) return null;
    const y = Number(fd.year);
    const m = Number(fd.month);
    const d = Number(fd.day);
    if (!y || !m || !d) return null;
    const date = new Date(Date.UTC(y, m - 1, d));
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const isDateMatch = useCallback(
    (marker: MarkerData) => {
      const markerDate = getMarkerDate(marker);
      if (!markerDate) {
        return includeUnknownDates;
      }
      if (parsedDateFrom && markerDate < parsedDateFrom) {
        return false;
      }
      if (parsedDateTo && markerDate > parsedDateTo) {
        return false;
      }
      return true;
    },
    [getMarkerDate, includeUnknownDates, parsedDateFrom, parsedDateTo]
  );

  const dateFilterSummary = useMemo(() => {
    if (!hasDateFilter) {
      return "No date filter";
    }
    const parts: string[] = [];
    if (dateFrom) {
      parts.push(`from ${dateFrom}`);
    }
    if (dateTo) {
      parts.push(`to ${dateTo}`);
    }
    if (!dateFrom && !dateTo) {
      parts.push("all dates");
    }
    if (includeUnknownDates) {
      parts.push("including unknown");
    } else {
      parts.push("excluding unknown");
    }
    return parts.join(" · ");
  }, [dateFrom, dateTo, includeUnknownDates, hasDateFilter]);

  const baseMarkers = useMemo(() => {
    const sessionFiltered = shouldFilterSessionOnly
      ? combinedMarkers.filter((marker) => marker.isSession)
      : combinedMarkers;
    return sessionFiltered.filter(isDateMatch);
  }, [combinedMarkers, shouldFilterSessionOnly, isDateMatch]);



  const handleSemanticSearch = useCallback(
    async (pageTokenOverride: string | null = null, append = false, offsetOverride = 0) => {
      if (!enableSemanticSearch) return;
      const query = semanticQuery.trim();
      const filters = buildSemanticFilters();
      const hasGeoFilters = Boolean(filters.country_code || filters.locality);

      // Geo-only filter (no query): filter markers locally without calling the API
      if (!query && hasGeoFilters) {
        setSemanticLoading(true);
        if (!append) {
          setSemanticPageToken(null);
          setSemanticModeType('geo');
          setMoreLikeAnchor(null);
          setSemanticOffset(0);
        }
        const normalizedTarget = baseMarkers.filter((marker) => {
          if (!isDateMatch(marker)) return false;
          const loc = (marker.finalLocation as any) || {};
          const country =
            (loc.country_code || loc.countryCode || (marker as any).country_code || '')
              .toString()
              .toUpperCase();
          const locality =
            (loc.locality || loc.city || (marker as any).locality || '')
              .toString()
              .trim()
              .toLowerCase();
          if (filters.country_code && country !== filters.country_code.toUpperCase()) {
            return false;
          }
          if (filters.locality && locality !== (filters.locality || '').toLowerCase().trim()) {
            return false;
          }
          return true;
        });
        setSemanticResults(normalizedTarget);
        setSemanticPageToken(null);
        setSemanticOffset(0);
        setSemanticHasMore(false);
        setSemanticModeActive(true);
        setSemanticModeType('geo');
        setMoreLikeAnchor(null);
        setIsDuplicateMode(false);
        setSemanticError(normalizedTarget.length ? null : "No markers match the selected location filters.");
        setSemanticLoading(false);
        return;
      }

      if (!query) {
        setSemanticError("Add a search phrase to start or choose a location filter.");
        return;
      }

      setSemanticLoading(true);
      setSemanticError(null);
      if (!append) {
        setSemanticPageToken(null);
        setSemanticModeType('text');
        setMoreLikeAnchor(null);
        setSemanticOffset(0);
      }
      try {
        const response = await authService.searchPhotos({
          query_text: query,
          filters: buildSemanticFilters(),
          limit: 50,
          offset: pageTokenOverride ? undefined : offsetOverride,
          page_token: pageTokenOverride,
        });
        const normalized = (response.results || [])
          .map(normalizeHistoricalMarker)
          .filter((marker): marker is MarkerData => Boolean(marker));
        setSemanticResults((prev) => (append ? [...prev, ...normalized] : normalized));
        setSemanticPageToken(response.next_page_token ?? null);
        setSemanticOffset(response.next_offset ?? 0);
        setSemanticHasMore(Boolean(response.next_page_token ?? response.next_offset));
        setSemanticModeActive(true);
        setSemanticModeType('text');
        setMoreLikeAnchor(null);
        setIsDuplicateMode(false);
      } catch (error) {
        console.error('Error during semantic search:', error);
        setSemanticError(error instanceof Error ? error.message : 'Semantic search failed.');
        if (!append) {
          setSemanticResults([]);
          setSemanticModeActive(false);
          setSemanticModeType(null);
          setSemanticPageToken(null);
        }
      } finally {
        setSemanticLoading(false);
      }
    },
    [enableSemanticSearch, semanticQuery, buildSemanticFilters, normalizeHistoricalMarker, baseMarkers, isDateMatch]
  );

  const handleMoreLikeThis = useCallback(
    async (marker: MarkerData, pageTokenOverride: string | null = null, append = false) => {
      console.log('[App] handleMoreLikeThis', marker.id, 'Semantic:', enableSemanticSearch, 'Sub:', hasActiveSubscription);
      if (!enableSemanticSearch || !hasActiveSubscription) {
        console.warn('[App] Semantic Search disabled or no subscription');
        return;
      }
      const parsedId = marker.id?.includes('::') ? marker.id.split('::') : [];
      const targetBatchId = marker.batchId || parsedId[0] || batchId; // Added batchId global fallback
      const targetPhotoId = marker.photoId || parsedId[1];
      if (!targetBatchId || !targetPhotoId) {
        console.error('Missing photo reference for similar search.', marker);
        setSemanticError('Missing photo reference for similar search.');
        return;
      }
      setSemanticLoading(true);
      setSemanticError(null);
      if (!append) {
        setSemanticPageToken(null);
        setSemanticOffset(0);
        setMoreLikeThisCandidates([]);
        setVisibleMoreLikeCount(0);
      }
      try {
        const SIMILAR_IMAGE_LIMIT = 50; // Fetch max candidates at once
        const response = await authService.searchPhotosByImage({
          batch_id: targetBatchId,
          photo_id: targetPhotoId,
          limit: SIMILAR_IMAGE_LIMIT,
          page_token: pageTokenOverride,
        });
        const normalized = (response.results || [])
          .map(normalizeHistoricalMarker)
          .filter((m): m is MarkerData => Boolean(m));

        // For "More Like This": 
        // 1. Store all candidates
        // 2. Initially show just 1 (or 0 if none found) + source
        // 3. User clicks "Even more like this" to increment visible count

        setMoreLikeThisCandidates(normalized);
        const initialVisible = normalized.length > 0 ? 1 : 0;
        setVisibleMoreLikeCount(initialVisible);

        // Semantic results for map display: Source + Visible Candidates
        // Note: marker is the source (Anchor). 
        // Ensure Source is always in the list for highlighting.
        setSemanticResults([marker, ...normalized.slice(0, initialVisible)]);

        setSemanticPageToken(response.next_page_token ?? null);
        setSemanticOffset(response.next_offset ?? 0);
        // We don't really support pagination on top of the 50 limit for this specific UX flow yet
        setSemanticHasMore(false);

        setSemanticModeActive(true);
        setSemanticModeType('image');
        setMoreLikeAnchor(marker);
        setIsDuplicateMode(false);
      } catch (error) {
        console.error('Error during similar-image search:', error);
        setSemanticError(error instanceof Error ? error.message : 'Similar image search failed.');
        if (!append) {
          setSemanticModeActive(false);
          setSemanticModeType(null);
          setSemanticPageToken(null);
          setMoreLikeAnchor(null);
          setMoreLikeThisCandidates([]);
          setVisibleMoreLikeCount(0);
        }
      } finally {
        setSemanticLoading(false);
      }
    },
    [enableSemanticSearch, normalizeHistoricalMarker]
  );

  const handleShowMoreSimilar = useCallback(() => {
    if (!moreLikeAnchor || moreLikeThisCandidates.length === 0) return;

    // Increment visible count
    const nextCount = visibleMoreLikeCount + 1;
    if (nextCount > moreLikeThisCandidates.length) return;

    setVisibleMoreLikeCount(nextCount);
    // Update displayed results: Source + New slice
    setSemanticResults([moreLikeAnchor, ...moreLikeThisCandidates.slice(0, nextCount)]);

  }, [moreLikeAnchor, moreLikeThisCandidates, visibleMoreLikeCount]);

  const handleSemanticLoadMore = useCallback(() => {
    if (semanticModeType === 'image') return; // Handled by handleShowMoreSimilar
    if (!semanticHasMore || semanticLoading) return;
    handleSemanticSearch(semanticPageToken, true, semanticOffset);
  }, [semanticHasMore, semanticLoading, semanticModeType, semanticPageToken, semanticOffset, handleSemanticSearch]);

  const handleClearSemanticSearch = useCallback(() => {
    setSemanticModeActive(false);
    setSemanticResults([]);
    setSemanticOffset(0);
    setSemanticHasMore(false);
    setSemanticError(null);
    setSemanticPageToken(null);
    setSemanticModeType(null);
    setMoreLikeAnchor(null);
  }, []);

  useEffect(() => {
    if (!enableSemanticSearch) {
      handleClearSemanticSearch();
      return;
    }
    if (!isAuthenticated) {
      handleClearSemanticSearch();
    }
  }, [enableSemanticSearch, isAuthenticated, handleClearSemanticSearch]);

  // Nuovo listener per lo stato del batch in tempo reale da Firestore
  const { batchData, error: batchListenerError } = useBatchListener(batchId);
  const batchStatus = (batchData as BatchStatus | null) ?? null;
  const aiFallbackState = batchStatus?.ai_fallback_state ?? null;
  const isBatchComplete = Boolean(
    batchStatus?.progress === 100 ||
    (batchStatus?.processed && batchStatus?.total && batchStatus.processed >= batchStatus.total)
  );
  const rawUserPrompt: BatchPrompt | null = batchStatus?.user_prompt ?? null;
  const userPrompt: BatchPrompt | null = isBatchComplete ? null : rawUserPrompt;
  const [modalSelected, setModalSelected] = React.useState("");
  const [modalImageUrl, setModalImageUrl] = React.useState<string | null>(null);
  const [forceClosePromptModal, setForceClosePromptModal] = React.useState(false);
  const [promptError, setPromptError] = useState<string>("");
  const buildMarkerKey = useCallback(
    (marker: MarkerData) => `${marker.batchId || 'session'}::${marker.photoId || marker.id}`,
    []
  );
  const fallbackDownloadBatchId = useMemo(() => {
    if (batchId) {
      return null;
    }
    return historicalMarkers.length ? historicalMarkers[0]?.batchId ?? null : null;
  }, [batchId, historicalMarkers]);
  const computedDownloadBatchId = batchId ?? fallbackDownloadBatchId ?? null;
  const downloadBatchId = downloadBatchOverride ?? computedDownloadBatchId;
  const downloadBatchListener = useBatchListener(
    downloadBatchId && downloadBatchId !== batchId ? downloadBatchId : null
  );
  const downloadBatchStatus: BatchStatus | null =
    downloadBatchId && downloadBatchId === batchId
      ? batchStatus
      : ((downloadBatchListener.batchData as BatchStatus | null) ?? null);
  const isDirectWriteReady = isBatchComplete;
  const isDirectWriteModeActive = directWriteEnabled && directWriteSupport.supported;
  const uploadBlockInfo = useMemo(() => {
    if (uploadStatus === 'uploading') {
      return {
        title: "Upload in progress",
        message: "Wait for the current upload to finish before starting a new upload.",
      };
    }
    const hasBatchContext = Boolean(batchId || batchStatus);
    if (!hasBatchContext) return null;

    const outputsHandled = Boolean(
      batchStatus?.cleanup_state?.processed_deleted ||
      batchStatus?.downloaded_at ||
      batchStatus?.direct_write_completed_at
    );
    const total = batchStatus?.total ?? uploadedFileCount ?? 0;
    const processed = batchStatus?.processed ?? 0;

    if (total > 0 && processed < total) {
      return {
        title: "Upload in progress",
        message:
          "Uploads are already pending or analysis is in progress. Wait for the current batch to finish before starting a new upload.",
      };
    }
    if (filesUploaded && !outputsHandled) {
      return {
        title: "Upload in progress",
        message:
          "Uploads are already pending or analysis is in progress. Wait for the current batch to finish before starting a new upload.",
      };
    }
    if (total > 0 && processed >= total && !outputsHandled) {
      if (isDirectWriteModeActive) {
        const message = directWriteHadFailure
          ? "Direct Write failed. Download the processed files before starting a new upload."
          : "Write processed files in-place or download them before starting a new upload.";
        return { title: "Processed files pending", message };
      }
      return {
        title: "Download required",
        message: "Download the processed files before starting a new upload.",
      };
    }
    return null;
  }, [
    uploadStatus,
    batchId,
    batchStatus,
    filesUploaded,
    uploadedFileCount,
    isDirectWriteModeActive,
    directWriteHadFailure,
  ]);
  const directWriteUploadsLocked = Boolean(uploadBlockInfo);
  const checkUploadsAllowed = useCallback(() => {
    if (!uploadBlockInfo) {
      return true;
    }
    setUploadValidationTitle(uploadBlockInfo.title);
    setUploadValidationError(uploadBlockInfo.message);
    return false;
  }, [uploadBlockInfo]);
  const isUploadSectionReset = Boolean(batchId && uploadSectionResetBatch === batchId);
  const shouldBlockDirectWriteToggle = useMemo(
    () => uploadMode === 'download' && !isUploadSectionReset,
    [uploadMode, isUploadSectionReset]
  );
  const handleDirectWriteToggleBlocked = useCallback(() => {
    setUploadValidationTitle("Direct Write");
    setUploadValidationError(
      "There are files uploaded in Download mode. Clear current uploads to use Direct Write"
    );
  }, []);
  const isUploadCancelable = useMemo(() => {
    // Consente di cancellare un batch di cui conosciamo il batch_id anche se
    // l'upload non è mai stato marcato come "filesUploaded" (es. interruzione
    // della connessione durante il caricamento / init del signed upload).
    if (!filesUploaded && !batchId) return false;
    const processed = batchStatus?.processed ?? 0;
    return processed === 0;
  }, [filesUploaded, batchId, batchStatus?.processed]);
  const isDownloadBatchCleaned = Boolean(downloadBatchStatus?.cleanup_state?.processed_deleted);
  const directWriteFailures = useMemo(
    () => directWriteWriteReport.filter((r) => r.status === "failed" && r.error),
    [directWriteWriteReport]
  );
  const directWriteSuccesses = useMemo(
    () =>
      directWriteWriteReport.filter(
        (r) => r.status === "written" || (r.status === "failed" && !r.error)
      ),
    [directWriteWriteReport]
  );
  useEffect(() => {
    if (!isDownloadBatchCleaned) {
      return;
    }
    setPendingDownloadJob(null);
    setIsDownloading(false);
    setDownloadBatchOverride(null);
    setDownloadAllIncludingHidden(false);
  }, [isDownloadBatchCleaned]);
  useEffect(() => {
    setDirectWriteHadFailure(false);
  }, [batchId]);
  useEffect(() => {
    if (resolvedUserId || !batchId) {
      return;
    }
    setBatchId(null);
  }, [resolvedUserId, batchId]);
  useEffect(() => {
    if (!batchId) {
      return;
    }
    if (batchStatus?.cleanup_state?.processed_deleted) {
      clearPersistedBatch();
      setBatchId(null);
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
    }
  }, [batchId, batchStatus?.cleanup_state?.processed_deleted, clearPersistedBatch]);
  useEffect(() => {
    if (!batchListenerError) {
      return;
    }
    if (batchId) {
      setBatchId(null);
    }
    clearPersistedBatch();
  }, [batchListenerError, batchId, clearPersistedBatch]);
  useEffect(() => {
    if (batchId || !uploadSectionResetBatch) {
      return;
    }
    setUploadSectionResetBatch(null);
  }, [batchId, uploadSectionResetBatch]);

  useEffect(() => {
    if (!batchId) {
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
      setUploadMode(null);
      return;
    }

    if (!batchStatus) {
      return;
    }

    if (isUploadSectionReset || batchStatus.cleanup_state?.uploads_deleted) {
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
      return;
    }

    const uploads = (batchStatus.uploaded_files ?? []) as Array<{ name?: string; size?: number }>;
    const inferredCount = uploads.length || (typeof batchStatus.total === "number" ? batchStatus.total : 0);

    if (inferredCount > 0) {
      setFilesUploaded(true);
      setUploadedFileCount(inferredCount);

      const bytesFromUploads = uploads.reduce((acc, file) => {
        const sizeVal = typeof file?.size === "number" ? file.size : 0;
        return acc + sizeVal;
      }, 0);

      const totalBytes =
        bytesFromUploads > 0
          ? bytesFromUploads
          : typeof batchStatus.uploads_total_bytes === "number"
            ? batchStatus.uploads_total_bytes
            : null;

      if (typeof totalBytes === "number" && totalBytes >= 0) {
        setUploadedTotalBytes(totalBytes);
        setUploadedTotalSize(Number((totalBytes / (1024 * 1024)).toFixed(2)));
      }
    }
  }, [batchId, batchStatus, isUploadSectionReset]);
  useEffect(() => {
    if (!batchId || !batchStatus) {
      return;
    }
    const outputsHandled = Boolean(
      batchStatus.downloaded_at ||
      batchStatus.direct_write_completed_at ||
      batchStatus.cleanup_state?.processed_deleted
    );
    if (!outputsHandled) {
      return;
    }
    if (uploadSectionResetBatch === batchId) {
      return;
    }
    void resetUploadSection({ preserveDirectWriteReport: true });
  }, [
    batchId,
    batchStatus,
    uploadSectionResetBatch,
    resetUploadSection,
  ]);
  const lastPromptIdRef = React.useRef<string>("");
  const shardDownloadRef = React.useRef(false);

  useEffect(() => {
    if (
      activeDuplicateHash &&
      !duplicateGroups.some((group) => group.contentHash === activeDuplicateHash)
    ) {
      setActiveDuplicateHash(null);
    }
  }, [activeDuplicateHash, duplicateGroups]);


  // Auto-scroll to map on mobile when analysis completes
  useEffect(() => {
    if (isMobileLayout && batchStatus?.progress === 100) {
      // Small timeout to ensure DOM is ready and layout is stable
      const timer = setTimeout(() => {
        mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isMobileLayout, batchStatus?.progress]);

  // NOTA: La logica WebSocket è stata rimossa e sostituita da useBatchListener.
  // Il componente ora reagirà direttamente alle modifiche di `batchStatus`.
  // La gestione dei `new_marker` dovrà essere adattata se era una funzionalità richiesta.
  // Per ora, ci concentriamo sulla gestione dei prompt e dello stato del batch.

  useEffect(() => {
    if (batchStatus?.cleanup_state?.processed_deleted) {
      setSessionMarkers([]);
      return;
    }

    if (!batchStatus?.files_status) {
      setSessionMarkers([]);
      return;
    }

    const orderMap = new Map<string, number>();
    (batchStatus.uploaded_files || []).forEach((file, index) => {
      if (file?.name) {
        orderMap.set(file.name.replace(/[\\/]/g, '_'), index);
      }
    });

    const filesStatus = batchStatus.files_status ?? {};
    const markerEntries = Object.entries(filesStatus)
      .map(([safeKey, status]) => {
        const processingState = status?.processing_state;
        if (processingState?.marker_hidden) {
          return null;
        }
        const finalLocation =
          processingState?.final_location ||
          processingState?.marker?.final_location ||
          processingState?.marker?.finalLocation;
        const markerMeta = processingState?.marker;
        const finalDate =
          markerMeta?.finalDate ||
          markerMeta?.final_date ||
          processingState?.final_date;

        const lat =
          typeof markerMeta?.lat === 'number'
            ? markerMeta.lat
            : typeof finalLocation?.latitude === 'number'
              ? finalLocation.latitude
              : undefined;
        const lng =
          typeof markerMeta?.lng === 'number'
            ? markerMeta.lng
            : typeof finalLocation?.longitude === 'number'
              ? finalLocation.longitude
              : undefined;
        if (typeof lat !== 'number' || typeof lng !== 'number') {
          return null;
        }

        const originalFilename =
          status?.original_path ||
          markerMeta?.original_filename ||
          safeKey.replace(/_/g, '/');
        const fallbackFilename = originalFilename
          ? originalFilename.split('/').pop() ?? originalFilename
          : safeKey;
        const filename = markerMeta?.filename ?? fallbackFilename;
        const photoPath =
          markerMeta?.photo_path ||
          processingState?.output_blob ||
          '';
        const imageUrl =
          markerMeta?.imageUrl ||
          (photoPath ? `/gcs-images/${photoPath}` : undefined);

        const markerData: MarkerData = {
          id: `${batchId || 'session'}::${safeKey}`,
          photoId: safeKey,
          batchId: batchId ?? undefined,
          batchName: batchStatus?.original_folder_name,
          lat,
          lng,
          filename,
          originalFilename,
          photo_path: photoPath,
          imageUrl,
          thumbnail: markerMeta?.thumbnail || undefined,
          thumbnail_url: markerMeta?.thumbnail_url || undefined,
          contentHash:
            markerMeta?.content_hash ||
            processingState?.content_hash ||
            processingState?.original_content_hash,
          processedAt: markerMeta?.processed_at,
          status: processingState?.status,
          outputRelativePath: processingState?.output_relative_path,
          finalLocation,
          finalDate,
          isSession: true,
          isHistorical: false,
          draggable: true,
        };

        const order =
          orderMap.get(safeKey) ??
          (originalFilename ? orderMap.get(originalFilename.replace(/[\\/]/g, '_')) : undefined) ??
          Number.MAX_SAFE_INTEGER;

        return { marker: markerData, order };
      })
      .filter((entry): entry is { marker: MarkerData; order: number } => Boolean(entry))
      .sort((a, b) => a.order - b.order)
      .map((entry) => entry.marker);

    // if (markerEntries.length) {
    //   console.log('[MAP][sessionMarkers] markers computed', markerEntries);
    // } else {
    //   console.log('[MAP][sessionMarkers] nessun marker disponibile', batchStatus?.files_status);
    // }

    setSessionMarkers(markerEntries);
  }, [batchStatus?.files_status, batchStatus?.uploaded_files, batchId, batchStatus?.original_folder_name, batchStatus?.cleanup_state?.processed_deleted]);

  const duplicateGroupsForDisplay = useMemo(() => {
    return duplicateGroups
      .map((group) => {
        const filtered = group.markers.filter((marker) => {
          if (marker.unmarked || marker.markerHidden) {
            return false;
          }
          if (shouldFilterSessionOnly && !marker.isSession) {
            return false;
          }
          return isDateMatch(marker);
        });
        if (filtered.length < 2) {
          return null;
        }
        return {
          ...group,
          groupSize: filtered.length,
          markers: filtered,
        };
      })
      .filter((group): group is DuplicateGroup => Boolean(group));
  }, [duplicateGroups, shouldFilterSessionOnly, isDateMatch]);
  const duplicateMarkersForDisplay = useMemo(
    () => duplicateGroupsForDisplay.flatMap((group) => group.markers),
    [duplicateGroupsForDisplay]
  );

  useEffect(() => {
    if (!hasActiveSubscription) {
      const sessionGroups = computeDuplicateGroups(sessionMarkers);
      setDuplicateGroups(sessionGroups);
      setDuplicateGroupsFetched(true);
      if (
        activeDuplicateHash &&
        !sessionGroups.some((group) => group.contentHash === activeDuplicateHash)
      ) {
        setActiveDuplicateHash(null);
      }
      return;
    }
    // Quando si passa da non-abbonato ad abbonato, forza un refetch dei duplicati globali
    setDuplicateGroupsFetched(false);
    setDuplicateGroups([]);
    setDuplicateGroupsError(null);
  }, [hasActiveSubscription, sessionMarkers, computeDuplicateGroups, activeDuplicateHash]);

  useEffect(() => {
    if (
      activeDuplicateHash &&
      !duplicateGroupsForDisplay.some((group) => group.contentHash === activeDuplicateHash)
    ) {
      setActiveDuplicateHash(null);
    }
  }, [activeDuplicateHash, duplicateGroupsForDisplay]);

  const activeDuplicateGroup = useMemo(
    () =>
      duplicateGroupsForDisplay.find((group) => group.contentHash === activeDuplicateHash) || null,
    [duplicateGroupsForDisplay, activeDuplicateHash]
  );

  const visibleMarkers = useMemo(() => {
    if (downloadUnmarkedOnly) {
      return [];
    }
    if (semanticModeActive) {
      return semanticResults.filter(isDateMatch);
    }
    if (isDuplicateMode) {
      return activeDuplicateGroup?.markers || duplicateMarkersForDisplay;
    }
    return baseMarkers;
  }, [downloadUnmarkedOnly, semanticModeActive, semanticResults, isDateMatch, isDuplicateMode, activeDuplicateGroup, duplicateMarkersForDisplay, baseMarkers]);

  const semanticOptions = useMemo(() => {
    const countries = new Set<string>();
    const localities = new Set<string>();
    const optionSource = visibleMarkers.length ? visibleMarkers : baseMarkers;
    optionSource.forEach((marker) => {
      const loc = marker.finalLocation as MarkerLocationMeta | undefined;
      if (!loc) return;
      const country = (loc as any)?.country_code || (loc as any)?.countryCode;
      if (country) {
        countries.add(String(country).toUpperCase());
      }
      const locality = (loc as any)?.locality || loc.city;
      if (locality) {
        localities.add(locality);
      }
      if (!locality && typeof loc.address === 'string') {
        const coarse = loc.address.split(',')[0]?.trim();
        if (coarse) {
          localities.add(coarse);
        }
      }
    });
    return {
      countries: Array.from(countries).sort(),
      localities: Array.from(localities).sort(),
    };
  }, [visibleMarkers, baseMarkers]);

  useEffect(() => {
    if (!pendingDuplicateModeExitCheck) {
      return;
    }
    if (duplicateGroupsLoading || !duplicateGroupsFetched) {
      return;
    }
    if (!isDuplicateMode) {
      setPendingDuplicateModeExitCheck(false);
      return;
    }
    if (duplicateGroupsForDisplay.length === 0) {
      setIsDuplicateMode(false);
    }
    setPendingDuplicateModeExitCheck(false);
  }, [
    pendingDuplicateModeExitCheck,
    duplicateGroupsLoading,
    duplicateGroupsFetched,
    duplicateGroupsForDisplay.length,
    isDuplicateMode,
  ]);

  const unmarkedCount = useMemo(() => {
    let count = 0;

    // Count unmarked from current batch
    if (batchStatus?.files_status) {
      count += Object.values(batchStatus.files_status).reduce((acc, entry) => {
        if (!entry) {
          return acc;
        }
        const state = entry.processing_state;
        if (!state) {
          return acc;
        }
        const status = state.status || "";
        const markerHidden = state.marker_hidden === true;
        const markerPresent = Boolean(state.marker);
        const unmarkedFlag = state.unmarked === true;
        const deletedBlob = status === "deleted_blob";
        if (deletedBlob) {
          return acc;
        }
        const completedLike =
          status.startsWith("completed") ||
          status === "deleted_marker" ||
          markerHidden ||
          unmarkedFlag;
        if (!completedLike) {
          return acc;
        }
        // Check for final_location in both marker (optimized) and root (legacy)
        const hasLocation = Boolean(
          state.marker?.finalLocation ||
          state.marker?.final_location ||
          state.final_location
        );
        const lacksMarker = !markerPresent || markerHidden || status === "deleted_marker";
        if (unmarkedFlag || lacksMarker || !hasLocation) {
          return acc + 1;
        }
        return acc;
      }, 0);
    }

    // For subscribed users, include historical unmarked images
    if (hasActiveSubscription && historicalMarkers.length > 0) {
      const historicalUnmarked = historicalMarkers.filter(marker => {
        // Skip markers from current batch (already counted above)
        if (batchId && marker.batchId === batchId) {
          return false;
        }

        // Include markers that are hidden or deleted
        if (marker.markerHidden || marker.status === 'deleted_marker') {
          return true;
        }

        // CRITICAL FIX: Include markers with unmarked flag
        // These are images that never had GPS coordinates from any session
        if (marker.unmarked === true) {
          return true;
        }

        // Include markers without coordinates (truly unmarked)
        if (!marker.lat || !marker.lng) {
          return true;
        }

        return false;
      });

      count += historicalUnmarked.length;
    }

    return count;
  }, [batchStatus?.files_status, batchId, hasActiveSubscription, historicalMarkers]);

  const markedCount = useMemo(() => {
    const filteredHistorical =
      batchId === null
        ? historicalMarkers
        : historicalMarkers.filter((marker) => marker.batchId !== batchId);
    const historicalMarked = filteredHistorical.filter(m => !m.markerHidden && !m.unmarked);
    const sessionMarked = sessionMarkers.filter(m => !m.markerHidden && !m.unmarked);
    return historicalMarked.length + sessionMarked.length;
  }, [historicalMarkers, sessionMarkers, batchId]);

  // Calculate list of unmarked markers for the modal
  const unmarkedMarkers = useMemo(() => {
    const markers: MarkerData[] = [];

    // 1. From current batch
    if (batchStatus?.files_status) {
      Object.entries(batchStatus.files_status).forEach(([photoId, entry]) => {
        if (!entry) return;
        const state = entry.processing_state;
        if (!state) return;

        const status = state.status || "";
        const markerHidden = state.marker_hidden === true;
        const markerPresent = Boolean(state.marker);
        const unmarkedFlag = state.unmarked === true;
        const deletedBlob = status === "deleted_blob";

        if (deletedBlob) return;

        const completedLike =
          status.startsWith("completed") ||
          status === "deleted_marker" ||
          markerHidden ||
          unmarkedFlag;

        if (!completedLike) return;

        const hasLocation = Boolean(
          state.marker?.finalLocation ||
          state.marker?.final_location ||
          state.final_location
        );
        const lacksMarker = !markerPresent || markerHidden || status === "deleted_marker";

        if (unmarkedFlag || lacksMarker || !hasLocation) {
          const originalFilename = entry.original_path || photoId;
          const fallbackFilename = originalFilename.split('/').pop() || originalFilename;
          // Use output_relative_path if available (renamed file), otherwise fallback
          const filename = state.output_relative_path
            ? state.output_relative_path.split('/').pop()
            : fallbackFilename;

          // Construct full photo path if output_blob is missing (common for unprocessed files)
          // Also check state.marker.photo_path as it might be preserved even if output_blob is missing
          let photoPath = state.output_blob || state.marker?.photo_path;
          // Use batchStatus.user_id as primary source, fallback to currentUser.uid
          const userId = batchStatus?.user_id || currentUser?.uid;

          if (!photoPath && userId && batchId) {
            const relativePath = state.output_relative_path || entry.original_path || photoId;
            // Ensure no double slashes
            const cleanRelative = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
            photoPath = `users/${userId}/${batchId}/${cleanRelative}`;
          }

          const imageUrl = photoPath ? `${HTTP_API_URL}/gcs-images/${photoPath}` : undefined;

          // Prefer worker thumbnail URL; no fallback to processed _thumb.jpg
          const thumbnailUrl = state.thumbnail_url || undefined;

          // Create a MarkerData object for this unmarked file
          markers.push({
            id: photoId,
            lat: 0,
            lng: 0,
            filename: filename || photoId,
            originalFilename: originalFilename,
            thumbnail_url: thumbnailUrl, // Use constructed thumbnail URL
            photo_path: photoPath,
            imageUrl: imageUrl,
            batchId: batchId || '',
            photoId: photoId,
            markerHidden: true, // It is effectively hidden/unmarked
            unmarked: true,
            isSession: true,
            isHistorical: false,
            draggable: true,
          } as MarkerData);
        }
      });
    }

    // 2. From historical markers (for subscribers)
    if (hasActiveSubscription && historicalMarkers.length > 0) {
      const historicalUnmarked = historicalMarkers.filter(marker => {
        if (batchId && marker.batchId === batchId) return false;
        // Include unmarked flag check for historical images
        return marker.unmarked === true;
      });
      markers.push(
        ...historicalUnmarked.map(m => ({
          ...m,
          draggable: true,
        }))
      );
    }

    return markers;
  }, [batchStatus?.files_status, batchId, hasActiveSubscription, historicalMarkers, currentUser]);


  const downloadMarkersPayload = useMemo<DownloadMarkerTargetPayload[]>(() => {
    if (downloadUnmarkedOnly) {
      const targets = new Map<string, DownloadMarkerTargetPayload>();
      const addTarget = (batch_id?: string | null, photo_id?: string | null) => {
        if (!batch_id || !photo_id) return;
        const key = `${batch_id}::${photo_id}`;
        if (!targets.has(key)) {
          targets.set(key, { batch_id, photo_id });
        }
      };

      if (batchStatus?.files_status && batchId) {
        Object.entries(batchStatus.files_status).forEach(([photoId, entry]) => {
          if (!entry) return;
          const state = entry.processing_state;
          if (!state) return;
          const status = state.status || "";
          const markerHidden = state.marker_hidden === true;
          const markerPresent = Boolean(state.marker);
          const unmarkedFlag = state.unmarked === true;
          const deletedBlob = status === "deleted_blob";
          const markerRemoved =
            status === "deleted_marker" ||
            status === "marker_removed" ||
            status === "marker_only_deleted" ||
            status === "marker_removed_only";
          if (deletedBlob) return;

          const hasLocation = Boolean(
            state.marker?.finalLocation ||
            state.marker?.final_location ||
            state.final_location
          );
          const lacksMarker = !markerPresent || markerHidden || markerRemoved;

          if (unmarkedFlag || lacksMarker || !hasLocation) {
            addTarget(batchId, photoId);
          }
        });
      }

      // Include hidden/unmarked markers from session + historical (even if not in files_status)
      [...sessionMarkers, ...historicalMarkers].forEach((marker) => {
        if (!marker) return;
        if (!marker.unmarked && !marker.markerHidden) return;
        const targetBatchId = marker.batchId || batchId;
        const photoId = marker.photoId || marker.id;
        addTarget(targetBatchId, photoId ?? null);
      });

      return Array.from(targets.values());
    }

    return visibleMarkers
      .map((marker) => {
        const targetBatchId = marker.batchId || null;
        const photoId = marker.photoId;
        if (!targetBatchId || !photoId) {
          return null;
        }
        return {
          batch_id: targetBatchId,
          photo_id: photoId,
        };
      })
      .filter((entry): entry is DownloadMarkerTargetPayload => Boolean(entry));
  }, [visibleMarkers, downloadUnmarkedOnly, batchStatus?.files_status, batchId, sessionMarkers, historicalMarkers]);

  const hasActiveFilters = shouldFilterSessionOnly || isDuplicateMode;
  const shouldShowDownloadAllToggle =
    !hasActiveFilters && !(isDirectWriteModeActive && !hasActiveSubscription);
  const shouldDownloadAll = shouldShowDownloadAllToggle && downloadAllIncludingHidden;
  const canDownload = !isDownloadBatchCleaned && Boolean(downloadBatchId) && (shouldDownloadAll || downloadMarkersPayload.length > 0);
  const hasDirectWriteFilters = Boolean(
    shouldFilterSessionOnly ||
    hasDateFilter ||
    semanticModeActive ||
    isDuplicateMode ||
    downloadUnmarkedOnly
  );
  const directWriteFilterPhotoIds = useMemo(() => {
    if (!batchId || !hasDirectWriteFilters) {
      return null;
    }
    const ids = new Set<string>();
    visibleMarkers.forEach((marker) => {
      if (marker.batchId && marker.batchId !== batchId) return;
      if (!marker.batchId && !marker.isSession) return;
      const photoId = marker.photoId || marker.id;
      if (photoId) {
        ids.add(photoId);
      }
    });
    return ids;
  }, [batchId, hasDirectWriteFilters, visibleMarkers]);

  const buildDirectWriteTargets = useCallback(
    (onlyFailed?: boolean): DirectWriteTarget[] => {
      const filesStatus = batchStatus?.files_status;
      if (!filesStatus) return [];
      if (directWriteFilterPhotoIds && directWriteFilterPhotoIds.size === 0) {
        return [];
      }
      const outputFolder = (batchStatus?.output_folder || "").replace(/\/+$/, "");
      const failedSet =
        onlyFailed
          ? new Set(
            directWriteWriteReport
              .filter((r) => r.status === "failed")
              .map((r) => normalizeDirectWritePath(r.targetRelativePath) || r.targetRelativePath)
          )
          : null;
      const targetsMap = new Map<string, DirectWriteTarget>();

      Object.entries(filesStatus).forEach(([photoId, entry]) => {
        if (directWriteFilterPhotoIds && !directWriteFilterPhotoIds.has(photoId)) {
          return;
        }
        if (!entry?.processing_state) return;
        const state = entry.processing_state;
        const status = state.status || "";
        const markerHidden = state.marker_hidden === true;
        const markerPresent = Boolean(state.marker);
        const unmarkedFlag = state.unmarked === true;
        const completedLike =
          status.startsWith("completed") ||
          status === "deleted_marker" ||
          markerHidden ||
          unmarkedFlag;
        if (!completedLike) return;
        const hasLocation = Boolean(
          state.marker?.finalLocation ||
          state.marker?.final_location ||
          state.final_location
        );
        const lacksMarker = !markerPresent || markerHidden || status === "deleted_marker";
        if (unmarkedFlag || lacksMarker || !hasLocation) {
          return;
        }

        const outputRel = state.output_relative_path?.replace(/^\/+/, "");
        const derivedOutputBlob =
          outputFolder && outputRel
            ? `${outputFolder}/${outputRel}`
            : outputRel || null;
        const outputBlob =
          derivedOutputBlob ||
          state.output_blob ||
          state.marker?.photo_path;
        if (!outputBlob) return;
        if (!status.startsWith("completed") && !state.output_relative_path) {
          return;
        }
        const isHttp = typeof outputBlob === "string" && /^https?:\/\//i.test(outputBlob);
        const cleanBlob = typeof outputBlob === "string" ? outputBlob.replace(/^\/+/, "") : "";
        const downloadUrl = isHttp ? (outputBlob as string) : `${HTTP_API_URL}/gcs-images/${encodeURI(cleanBlob)}`;
        const blobPath = !isHttp && cleanBlob ? cleanBlob : undefined;

        const targetRelRaw =
          state.output_relative_path ||
          state.marker?.filename ||
          entry.original_path ||
          state.marker?.original_filename ||
          photoId;
        const targetRel = normalizeDirectWritePath(targetRelRaw) || targetRelRaw;
        if (!targetRel) return;

        const key = targetRel.toLowerCase();
        if (failedSet && !failedSet.has(targetRel) && !failedSet.has(targetRelRaw || "")) {
          return;
        }

        const originalRel = normalizeDirectWritePath(entry.original_path || null);
        if (!targetsMap.has(key)) {
          targetsMap.set(key, {
            id: photoId,
            downloadUrl,
            blobPath,
            targetRelativePath: targetRel,
            originalRelativePath: originalRel ?? undefined,
          });
        }
      });

      return Array.from(targetsMap.values());
    },
    [
      HTTP_API_URL,
      batchStatus?.files_status,
      batchStatus?.output_folder,
      directWriteFilterPhotoIds,
      directWriteWriteReport,
      normalizeDirectWritePath,
    ]
  );

  const resolveDirectWriteHandles = useCallback(
    async (handle: FileSystemDirectoryHandle, targets: DirectWriteTarget[]) => {
      const wanted = new Set<string>();
      targets.forEach((target) => {
        const rel = normalizeDirectWritePath(target.originalRelativePath || target.targetRelativePath);
        if (rel) {
          wanted.add(rel.toLowerCase());
        }
      });
      if (wanted.size === 0) {
        return new Map<string, FileSystemFileHandle>();
      }
      const map = new Map<string, FileSystemFileHandle>();
      for await (const entry of streamDirectWriteEntries(handle, {})) {
        const rel = normalizeDirectWritePath(entry.relativePath) || entry.relativePath;
        if (!rel) continue;
        const key = rel.toLowerCase();
        if (wanted.has(key)) {
          map.set(key, entry.handle);
          if (map.size >= wanted.size) {
            break;
          }
        }
      }
      return map;
    },
    [normalizeDirectWritePath]
  );

  const resolveImageUrl = React.useCallback((imageUrl?: string | null) => {
    if (!imageUrl) {
      return null;
    }
    if (/^(https?:|data:)/i.test(imageUrl)) {
      return imageUrl;
    }
    const normalized = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${HTTP_API_URL}${normalized}`;
  }, []);

  const handleGeminiPreview = React.useCallback((src: string | null) => {
    if (!src) {
      setModalImageUrl(null);
      return;
    }
    setModalImageUrl(prev => (prev === src ? null : src));
  }, []);

  const handleOpenModal = useCallback(
    async (marker?: MarkerData) => {
      if (!marker) {
        setModalImageUrl(null);
        return;
      }

      // 1. Prefer Proxy URL constructed from photo_path (Most robust, works for all processed files)
      if (marker.photo_path) {
        const cleanPath = marker.photo_path.startsWith('/') ? marker.photo_path.slice(1) : marker.photo_path;

        // Defensive check: photo_path should never contain a URL
        if (cleanPath.startsWith('http')) {
          console.error('[IMAGE MODAL BUG] photo_path contains URL instead of path!', {
            photo_path: marker.photo_path,
            marker_id: marker.id,
            batch_id: marker.batchId,
          });
          // Fallback to imageUrl if photo_path is corrupted
          const resolved = resolveImageUrl(marker.imageUrl);
          if (resolved) {
            setModalImageUrl(resolved);
            return;
          }
        }

        const proxyUrl = `${HTTP_API_URL}/gcs-images/${cleanPath}`;
        console.log('[IMAGE MODAL] Opening with photo_path:', cleanPath.substring(0, 60) + '...');
        setModalImageUrl(proxyUrl);
        return;
      }

      // 2. Fallback to existing imageUrl
      const resolved = resolveImageUrl(marker.imageUrl);
      if (resolved) {
        setModalImageUrl(resolved);
      } else {
        setModalImageUrl(null);
      }
    },
    [resolveImageUrl]
  );

  const handleCloseModal = () => {
    setModalImageUrl(null);
  };

  useEffect(() => {
    fetchHistoricalMarkers();
  }, [fetchHistoricalMarkers]);

  useEffect(() => {
    if (!hasActiveSubscription) {
      return;
    }
    if (batchStatus?.progress === 100) {
      fetchHistoricalMarkers();
    }
  }, [batchStatus?.progress, hasActiveSubscription, fetchHistoricalMarkers]);

  useEffect(() => {
    if (!hasActiveSubscription || !isDuplicateMode) {
      return;
    }
    if (batchStatus?.progress === 100) {
      fetchDuplicateGroups();
    }
  }, [batchStatus?.progress, hasActiveSubscription, isDuplicateMode, fetchDuplicateGroups]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!autoZoomAfterAnalysis) return;
    if (batchStatus?.progress !== 100) return;
    if (!batchId) return;
    if (!sessionMarkers.length) return;
    if (lastAutoFitBatchRef.current === batchId) return;

    setMapAutoFitMarkers(sessionMarkers);
    setMapAutoFitToken(prev => prev + 1);
    lastAutoFitBatchRef.current = batchId;
    try {
      window.sessionStorage.setItem(MAP_AUTOFIT_BATCH_KEY, batchId);
    } catch {
      // ignore storage errors
    }
  }, [autoZoomAfterAnalysis, batchStatus?.progress, batchId, sessionMarkers]);

  // Se l'utente è abbonato e la UI è in duplicate mode, rilancia il fetch quando il profilo termina di caricarsi
  useEffect(() => {
    if (!isDuplicateMode) return;
    if (!isAuthenticated) return;
    if (!hasActiveSubscription) return;
    fetchDuplicateGroups();
  }, [
    isDuplicateMode,
    isAuthenticated,
    hasActiveSubscription,
    fetchDuplicateGroups,
  ]);

  // Se l'utente NON è abbonato (o lo diventa dopo il caricamento), calcola i duplicati locali dopo che l'auth è pronta
  useEffect(() => {
    if (!isDuplicateMode) return;
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (hasActiveSubscription) return;
    if (duplicateGroupsFetched) return;
    const sessionGroups = computeDuplicateGroups(sessionMarkers);
    setDuplicateGroups(sessionGroups);
    setDuplicateGroupsFetched(true);
    setDuplicateGroupsLoading(false);
  }, [
    isDuplicateMode,
    authLoading,
    isAuthenticated,
    hasActiveSubscription,
    duplicateGroupsFetched,
    computeDuplicateGroups,
    sessionMarkers,
  ]);

  useEffect(() => {
    if (!hasActiveSubscription && isSessionFilterActive) {
      setIsSessionFilterActive(false);
    }
  }, [hasActiveSubscription, isSessionFilterActive]);

  useEffect(() => {
    if (!hasActiveSubscription) {
      setShowDeleteOptions(false);
      setDeleteConfirmAction(null);
      setDeleteErrorMessage(null);
    }
  }, [hasActiveSubscription]);

  useEffect(() => {
    setMarkerChangeCounts({});
    setPendingCreditCharges({});
    setHistoricalUnlocked({});
    setDraggedMarkerInfo(null);
  }, [batchId]);

  const handleToggleDuplicateMode = useCallback(() => {
    setIsDuplicateMode((prev) => {
      const next = !prev;
      if (next) {
        if (hasActiveSubscription) {
          setDuplicateGroupsFetched(false);
          setDuplicateGroups([]);
          fetchDuplicateGroups();
        } else {
          if (!(authLoading && profile === null)) {
            const sessionGroups = computeDuplicateGroups(sessionMarkers);
            setDuplicateGroups(sessionGroups);
            setDuplicateGroupsFetched(true);
          }
        }
      } else {
        setActiveDuplicateHash(null);
      }
      return next;
    });
  }, [
    hasActiveSubscription,
    fetchDuplicateGroups,
    computeDuplicateGroups,
    sessionMarkers,
    authLoading,
    profile,
  ]);

  const handleToggleSessionFilter = useCallback(() => {
    if (!hasActiveSubscription) {
      return;
    }
    setIsSessionFilterActive((prev) => !prev);
  }, [hasActiveSubscription]);

  const handleOpenDeleteConfirm = useCallback((action: "all" | "session") => {
    setDeleteErrorMessage(null);
    setDeleteConfirmAction(action);
    setShowDeleteOptions(false);
  }, []);

  const refreshProfileAfterStorageMutation = useCallback(async () => {
    await fetchUserProfile(true).catch((error) => {
      console.warn("Unable to refresh profile after storage mutation:", error);
    });
  }, [fetchUserProfile]);

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeleteConfirmAction(null);
    setDeleteErrorMessage(null);
    setDeleteLoading(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmAction) return;
    setDeleteErrorMessage(null);
    setDeleteLoading(true);
    try {
      if (deleteConfirmAction === "all") {
        await authService.deleteAllStorage();
        setSessionMarkers([]);
        setHistoricalMarkers([]);
        setDuplicateGroups([]);
        setDuplicateGroupsFetched(false);
        setDuplicateGroupsError(null);
      } else {
        if (!batchId) {
          throw new Error("No active session to delete.");
        }
        await authService.deleteBatchStorage(batchId);
        setSessionMarkers([]);
        setDuplicateGroups([]);
        setDuplicateGroupsFetched(false);
        setDuplicateGroupsError(null);
      }
      setActiveDuplicateHash(null);
      setIsDuplicateMode(false);
      await refreshProfileAfterStorageMutation();
      await fetchHistoricalMarkers();
    } catch (error) {
      console.error("Error deleting images:", error);
      setDeleteErrorMessage(error instanceof Error ? error.message : "Unable to delete images.");
      return;
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmAction(null);
    }
  }, [
    deleteConfirmAction,
    batchId,
    fetchHistoricalMarkers,
    refreshProfileAfterStorageMutation,
    setSessionMarkers,
    setHistoricalMarkers,
    setDuplicateGroups,
    setDuplicateGroupsFetched,
    setDuplicateGroupsError,
    setActiveDuplicateHash,
    setIsDuplicateMode,
  ]);

  const handleClearDateFilter = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    setIncludeUnknownDates(true);
  }, []);

  const handleSelectDuplicateGroup = (hash: string | null) => {
    setActiveDuplicateHash(hash);
  };

  const handleMarkerDragEnd = (marker: MarkerData, newPosition: { lat: number; lng: number }) => {
    if (!marker || marker.draggable === false) {
      return;
    }

    const markerKey = buildMarkerKey(marker);
    const currentCount = markerChangeCounts[markerKey] || 0;
    if (currentCount >= 4) {
      setShowLimitReached({ marker, markerKey, newPosition });
      return;
    }

    setDraggedMarkerInfo({
      marker,
      source: 'map',
      markerKey,
      originalPosition: { lat: marker.lat, lng: marker.lng },
      newPosition,
    });
  };

  const handleRequestMoveMarker = useCallback(
    (marker: MarkerData) => {
      console.log('[App] Request Move for:', marker.id, 'isHistorical:', marker.isHistorical);
      if (!marker) {
        return Promise.resolve(false);
      }
      const markerKey = buildMarkerKey(marker);
      if (!marker.isHistorical) {
        return Promise.resolve(true);
      }
      if (!hasActiveSubscription) {
        prefetchPurchaseModal();
        setShowPurchaseModal(true);
        return Promise.resolve(false);
      }
      if (historicalUnlocked[markerKey]) {
        return Promise.resolve(true);
      }
      if (availableCredits < 1) {
        setHistoricalMoveCreditsWarning(true);
        return Promise.resolve(false);
      }
      return new Promise<boolean>((resolve) => {
        setHistoricalMovePrompt({ marker, markerKey, resolver: resolve });
      });
    },
    [hasActiveSubscription, availableCredits, buildMarkerKey, historicalUnlocked]
  );

  const handleCreateMarkerFromUnmarked = useCallback(async (marker: MarkerData) => {
    try {
      // Close the unmarked modal/dropdown so subsequent prompts stay on top
      setShowUnmarkedImagesModal(false);
      setShowUnmarkedDropdown(false);
      const allowed = await handleRequestMoveMarker(marker);
      if (!allowed) {
        return;
      }
      const prepared: MarkerData = {
        ...marker,
        isSession: marker.isSession ?? !marker.isHistorical,
        draggable: true,
        markerHidden: false,
        unmarked: false,
        lat: typeof marker.lat === 'number' && !Number.isNaN(marker.lat) ? marker.lat : 0,
        lng: typeof marker.lng === 'number' && !Number.isNaN(marker.lng) ? marker.lng : 0,
      };
      setPendingMoveTarget(prepared);
    } catch (error) {
      console.error('Unable to start create-marker flow:', error);
    }
  }, [handleRequestMoveMarker]);

  const handleHistoricalMovePromptConfirm = () => {
    if (!historicalMovePrompt) {
      return;
    }
    const { markerKey, resolver } = historicalMovePrompt;
    setPendingCreditCharges(prev => ({ ...prev, [markerKey]: true }));
    setHistoricalUnlocked(prev => ({ ...prev, [markerKey]: true }));
    setMarkerChangeCounts(prev => ({ ...prev, [markerKey]: 0 }));
    resolver(true);
    setHistoricalMovePrompt(null);
  };

  const handleHistoricalMovePromptCancel = () => {
    if (!historicalMovePrompt) {
      return;
    }
    historicalMovePrompt.resolver(false);
    setHistoricalMovePrompt(null);
  };

  const handleCloseLimitReached = () => {
    setShowLimitReached(null);
  };

  const handleUseCreditAfterLimit = () => {
    if (!showLimitReached) {
      return;
    }
    if (availableCredits < 1) {
      setHistoricalMoveCreditsWarning(true);
      return;
    }
    const { markerKey, marker } = showLimitReached;
    setPendingCreditCharges(prev => ({ ...prev, [markerKey]: true }));
    setMarkerChangeCounts(prev => ({ ...prev, [markerKey]: 0 }));
    if (marker.isHistorical) {
      setHistoricalUnlocked(prev => ({ ...prev, [markerKey]: true }));
    }
    setShowLimitReached(null);
  };

  const handleRequestDeleteMarker = (marker: MarkerData) => {
    console.log('[App] Request delete for marker:', marker.id, marker.batchId, marker.photoId, 'Global Batch:', batchId);
    // FALLBACK: Use global batchId if marker is missing it (common for current session markers)
    const targetBatchId = marker.batchId || batchId;

    if (!targetBatchId || !(marker.photoId || marker.id)) {
      console.error('Unable to delete: missing identifiers.', marker, 'Target Batch:', targetBatchId);
      return;
    }
    setDeleteError(null);
    // IMPORTANT: Pass the marker with guaranteed batchId to the dialog logic if needed
    // But since we use executeDeleteAction which re-calculates, we just need to ensure validation passes here.
    // Actually, update the marker object in state? NO, deleteDialog takes the marker ref.
    // We should patch it or ensure executeDeleteAction uses fallback too.
    setDeleteDialog({ kind: "single", marker: { ...marker, batchId: targetBatchId } });
  };

  const handleRequestDeleteDuplicates = (marker: MarkerData) => {
    if (!marker.contentHash) {
      console.error('Unable to delete duplicates: hash not available.');
      return;
    }
    if (!hasActiveSubscription) {
      prefetchPurchaseModal();
      setShowPurchaseModal(true);
      return;
    }
    setDeleteError(null);
    setDeleteDialog({ kind: "group", marker });
  };

  const getChangeText = (currentCount: number) => {
    const remainingAfterThis = 4 - currentCount - 1;
    if (remainingAfterThis <= 0) return "You won't be able to change position again (else spend 1 credit to allow 4 more moves)";
    if (remainingAfterThis === 1) return "You can change the position 1 more time";
    return `You can change the position ${remainingAfterThis} more times`;
  };

  const handleConfirmDrag = async () => {
    if (!draggedMarkerInfo) return;

    if (!isAuthenticated) {
      console.error('User not authenticated');
      return;
    }

    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      console.error('Current user null');
      return;
    }

    const idToken = await authService.requireAuthToken();

    const { marker, markerKey, newPosition, originalPosition } = draggedMarkerInfo;

    // Type guards for optional properties
    if (!markerKey || !newPosition || !originalPosition) {
      console.error('Missing drag info properties');
      setDraggedMarkerInfo(null);
      return;
    }

    const targetBatchId = marker.batchId || batchId;
    const targetPhotoId = marker.photoId || marker.id;
    if (!targetBatchId || !targetPhotoId) {
      console.error('Identificativi mancanti per aggiornare il marker.');
      setDraggedMarkerInfo(null);
      return;
    }

    const shouldChargeCredits = pendingCreditCharges[markerKey] === true;

    // 2. Invia la richiesta al backend per salvare le modifiche
    try {
      const response = await fetch(`${HTTP_API_URL}/api/update-photo-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          batch_id: targetBatchId,
          photo_id: targetPhotoId,
          latitude: newPosition.lat,
          longitude: newPosition.lng,
          charge_credits: shouldChargeCredits,
        }),
      });

      if (!response.ok) {
        if (response.status === 402 && shouldChargeCredits) {
          setHistoricalMoveCreditsWarning(true);
          return;
        }
        throw new Error('Network or server error.');
      }

      const result = await response.json();

      // Defensive logging to track the response
      console.log('[MARKER UPDATE] Response from backend:', {
        new_image_url: result.new_image_url?.substring(0, 50) + '...',
        new_thumbnail_url: result.new_thumbnail_url,
        new_filename: result.new_filename,
        renamed: result.renamed,
        new_photo_path: result.new_photo_path || 'not provided (file not renamed)',
      });

      const newFinalLocation: MarkerLocationMeta | undefined =
        result?.final_location && typeof result.final_location === 'object'
          ? result.final_location as MarkerLocationMeta
          : undefined;
      const existingLocation: MarkerLocationMeta =
        (marker.finalLocation as MarkerLocationMeta | undefined) ||
        (marker as any).final_location ||
        {};
      const updatedFinalLocation: MarkerLocationMeta = {
        ...existingLocation,
        ...(newFinalLocation || {}),
        latitude: newPosition.lat,
        longitude: newPosition.lng,
        source: 'map_adjustment',
      };
      if (result?.new_address && !updatedFinalLocation.address) {
        updatedFinalLocation.address = result.new_address;
      }

      // Incrementa il contatore delle modifiche
      const updatedMarkerForKey: MarkerData = {
        ...marker,
        id: result.new_marker_id || marker.id,
        photoId: result.new_marker_id || marker.photoId || marker.id,
        batchId: targetBatchId,
        filename: result.new_filename || marker.filename,

        // CRITICAL: Use new_photo_path if file was renamed, otherwise preserve original
        // When renamed=true, backend sends new_photo_path (new GCS blob location)
        // When renamed=false, backend doesn't send it and we keep the original path
        photo_path: result.renamed && result.new_photo_path
          ? result.new_photo_path  // Use new path when file was renamed
          : marker.photo_path,     // Keep original path when no rename

        imageUrl: result.new_image_url || marker.imageUrl,  // Update signed URL
        thumbnail: marker.thumbnail,
        thumbnail_url: result.new_thumbnail_url || marker.thumbnail_url,
        lat: newPosition.lat,
        lng: newPosition.lng,
        markerHidden: false,
        unmarked: false,
        draggable: true,
        finalLocation: updatedFinalLocation,
      };
      const newMarkerKey = buildMarkerKey(updatedMarkerForKey);
      setMarkerChangeCounts(prev => {
        const next = { ...prev };
        const currentCount = (next[markerKey] || 0) + 1;
        if (newMarkerKey !== markerKey) {
          delete next[markerKey];
        }
        next[newMarkerKey] = currentCount;
        return next;
      });
      if (shouldChargeCredits) {
        setPendingCreditCharges(prev => {
          if (newMarkerKey === markerKey) {
            const next = { ...prev };
            delete next[markerKey];
            return next;
          }
          const next = { ...prev };
          delete next[markerKey];
          return next;
        });
        if (marker.isHistorical) {
          setHistoricalUnlocked(prev => {
            if (newMarkerKey === markerKey) {
              return { ...prev, [markerKey]: true };
            }
            const next = { ...prev, [newMarkerKey]: true };
            delete next[markerKey];
            return next;
          });
        }
      } else if (newMarkerKey !== markerKey) {
        setPendingCreditCharges(prev => {
          if (!prev[markerKey]) {
            return prev;
          }
          const next = { ...prev };
          next[newMarkerKey] = next[markerKey];
          delete next[markerKey];
          return next;
        });
        setHistoricalUnlocked(prev => {
          if (!prev[markerKey]) {
            return prev;
          }
          const next = { ...prev };
          next[newMarkerKey] = next[markerKey];
          delete next[markerKey];
          return next;
        });
      }

      const applyMarkerUpdate = (entry: MarkerData): MarkerData => {
        if (entry.id !== marker.id) {
          return entry;
        }
        return {
          ...updatedMarkerForKey,
        };
      };

      if (marker.isSession) {
        setSessionMarkers(currentMarkers => currentMarkers.map(applyMarkerUpdate));
      } else {
        setHistoricalMarkers(currentMarkers => currentMarkers.map(applyMarkerUpdate));
      }
      setDuplicateGroups(groups =>
        groups.map(group => ({
          ...group,
          markers: group.markers.map(applyMarkerUpdate),
        }))
      );
      if (marker.isHistorical) {
        fetchHistoricalMarkers();
      }

      console.log(`Salvataggio di ${marker.id} alla nuova posizione`, newPosition);

    } catch (error) {
      console.error("Error updating photo:", error);
      const revertUpdate = (entry: MarkerData): MarkerData =>
        entry.id === marker.id
          ? { ...entry, lat: originalPosition.lat, lng: originalPosition.lng }
          : entry;
      if (marker.isSession) {
        setSessionMarkers(currentMarkers => currentMarkers.map(revertUpdate));
      } else {
        setHistoricalMarkers(currentMarkers => currentMarkers.map(revertUpdate));
      }
    } finally {
      setDraggedMarkerInfo(null); // Chiudi il popup
    }
  };

  const handleCloseDeleteDialog = () => {
    if (isDeletingMarker) {
      return;
    }
    setDeleteDialog(null);
    setDeleteError(null);
  };

  const executeDeleteAction = async (mode: DeleteMode) => {
    if (!deleteDialog) return;
    const marker = deleteDialog.marker;
    if (!marker.batchId) {
      setDeleteError("Unable to delete: missing batch identifier.");
      return;
    }
    const photoId = marker.photoId || marker.id;
    if (!photoId) {
      setDeleteError("Unable to delete: missing photo identifier.");
      return;
    }

    setIsDeletingMarker(true);
    setDeleteError(null);
    const targetKey = `${marker.batchId || ""}::${photoId}`;
    try {
      if (deleteDialog.kind === "single") {
        await authService.deleteMarker({
          batch_id: marker.batchId,
          photo_id: photoId,
          mode,
        });

        setSessionMarkers((prev) =>
          prev.filter((entry) => `${entry.batchId || ""}::${entry.photoId || entry.id}` !== targetKey)
        );
        setHistoricalMarkers((prev) =>
          prev.filter((entry) => `${entry.batchId || ""}::${entry.photoId || entry.id}` !== targetKey)
        );
        if (marker.contentHash) {
          setDuplicateGroups((prev) => {
            let shouldClearActive = false;
            const next = prev
              .map((group) => {
                if (group.contentHash !== marker.contentHash) {
                  return group;
                }
                const filtered = group.markers.filter(
                  (entry) => `${entry.batchId || ""}::${entry.photoId || entry.id}` !== targetKey
                );
                const newSize = Math.max(filtered.length, group.groupSize - 1);
                if (filtered.length < 2) {
                  if (group.contentHash === activeDuplicateHash) {
                    shouldClearActive = true;
                  }
                  return null;
                }
                return {
                  ...group,
                  groupSize: newSize,
                  markers: filtered,
                };
              })
              .filter((group): group is DuplicateGroup => Boolean(group));
            if (shouldClearActive) {
              setActiveDuplicateHash(null);
            }
            return next;
          });
        }
      } else {
        if (!marker.contentHash) {
          throw new Error("Unable to delete duplicates without a content hash.");
        }
        await authService.deleteDuplicateGroup({
          content_hash: marker.contentHash,
          keep: {
            batch_id: marker.batchId,
            photo_id: photoId,
          },
          mode,
        });

        const keepKey = targetKey;
        setSessionMarkers((prev) =>
          prev.filter(
            (entry) =>
              entry.contentHash !== marker.contentHash ||
              `${entry.batchId || ""}::${entry.photoId || entry.id}` === keepKey
          )
        );
        setHistoricalMarkers((prev) =>
          prev.filter(
            (entry) =>
              entry.contentHash !== marker.contentHash ||
              `${entry.batchId || ""}::${entry.photoId || entry.id}` === keepKey
          )
        );
        setDuplicateGroups((prev) => {
          const next = prev.filter((group) => group.contentHash !== marker.contentHash);
          if (activeDuplicateHash === marker.contentHash) {
            setActiveDuplicateHash(null);
          }
          return next;
        });
        setPendingDuplicateModeExitCheck(true);
      }

      if (mode === "delete_blob") {
        await refreshProfileAfterStorageMutation();
      }
      if (hasActiveSubscription) {
        await fetchHistoricalMarkers();
        await fetchDuplicateGroups();
      }
      setDeleteDialog(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to delete the selected items.";
      setDeleteError(message);
      setPendingDuplicateModeExitCheck(false);
    } finally {
      setIsDeletingMarker(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!downloadBatchId) {
      setErrorMsg('No batch available for download.');
      return;
    }
    setErrorMsg(null);
    setDownloadLimitModalMessage(null);

    if (downloadAllowanceBytes !== null && downloadAllowanceBytes <= 0) {
      setDownloadLimitModalMessage(DEFAULT_DOWNLOAD_LIMIT_MESSAGE);
      return;
    }

    if (!shouldDownloadAll && downloadMarkersPayload.length === 0) {
      setErrorMsg('No markers available to download.');
      return;
    }

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) throw new Error("User not authenticated.");
      const idToken = await authService.requireAuthToken();

      const startResponse = await fetch(`${HTTP_API_URL}/batch/zip-and-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          batch_id: downloadBatchId,
          include_all_processed: shouldDownloadAll,
          include_unmarked_only: downloadUnmarkedOnly && !shouldDownloadAll,
          ...(shouldDownloadAll ? {} : { markers: downloadMarkersPayload }),
        }),
      });

      if (!startResponse.ok) {
        const detail = await readErrorDetail(startResponse);
        if (startResponse.status === 403) {
          setDownloadLimitModalMessage(detail ?? DEFAULT_DOWNLOAD_LIMIT_MESSAGE);
          return;
        }
        throw new Error(detail ?? "Error starting compression.");
      }

      const startData = await startResponse.json().catch(() => null);
      const jobId = startData?.job_id as string | undefined;
      setPendingDownloadJob({ batchId: downloadBatchId, jobId, shardIndex: 0 });
      setDownloadBatchOverride(downloadBatchId);
      setIsDownloading(true);
    } catch (error) {
      const code = (error as any)?.code;
      if (code === 'email-not-verified') {
        setErrorMsg('Please verify your email before downloading. Check your inbox or use the magic link.');
      } else {
        console.error("Error in handleDownloadClick:", error);
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Error starting compression.";
        setErrorMsg(message);
      }
      setPendingDownloadJob(null);
      setIsDownloading(false);
    }
  };

  const handleDeleteUnmarked = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return;

    setIsDeletingUnmarked(true);
    try {
      const token = await authService.requireAuthToken();

      // For subscribed users, delete ALL historical unmarked images
      if (hasActiveSubscription) {
        // Collect all unmarked markers (current + historical)
        const unmarkedToDelete: Array<{ batch_id: string, photo_id: string }> = [];

        // Add unmarked from current batch
        if (batchId && batchStatus?.files_status) {
          Object.entries(batchStatus.files_status).forEach(([photoId, entry]) => {
            if (!entry) return;

            const state = entry.processing_state;
            if (!state) return;

            const status = state.status || "";
            const markerHidden = state.marker_hidden === true;
            const markerPresent = Boolean(state.marker);
            const unmarkedFlag = state.unmarked === true;
            const deletedBlob = status === "deleted_blob";

            if (deletedBlob) return;

            const completedLike =
              status.startsWith("completed") ||
              status === "deleted_marker" ||
              markerHidden ||
              unmarkedFlag;

            if (!completedLike) return;

            const hasLocation = Boolean(
              state.marker?.finalLocation ||
              state.marker?.final_location ||
              state.final_location
            );

            const lacksMarker = !markerPresent || markerHidden || status === "deleted_marker";

            if (unmarkedFlag || lacksMarker || !hasLocation) {
              unmarkedToDelete.push({
                batch_id: batchId,
                photo_id: photoId
              });
            }
          });
        }

        // Add unmarked from historical markers
        historicalMarkers.forEach(marker => {
          // Skip current batch (already added above)
          if (batchId && marker.batchId === batchId) return;

          // Include hidden, deleted, or truly unmarked
          if (marker.markerHidden || marker.status === 'deleted_marker' || !marker.lat || !marker.lng) {
            const photoId = marker.photoId || marker.id;
            if (marker.batchId && photoId) {
              unmarkedToDelete.push({
                batch_id: marker.batchId,
                photo_id: photoId
              });
            }
          }
        });

        // Delete all unmarked images
        if (unmarkedToDelete.length > 0) {
          const deletePromises = unmarkedToDelete.map(async ({ batch_id, photo_id }) => {
            try {
              await authService.deleteMarker({
                batch_id,
                photo_id,
                mode: 'delete_blob'
              });
            } catch (error) {
              console.error(`Failed to delete ${photo_id} from ${batch_id}:`, error);
            }
          });

          await Promise.all(deletePromises);

          // Refresh historical markers and duplicates
          await refreshProfileAfterStorageMutation();
          await fetchHistoricalMarkers();
          await fetchDuplicateGroups();
        }
      } else {
        // For non-subscribed users, delete only from current batch
        if (!batchId) return;

        const response = await fetch(`${HTTP_API_URL}/api/map/delete-unmarked?batch_id=${batchId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete unmarked images');
        }
      }

      if (!hasActiveSubscription) {
        await refreshProfileAfterStorageMutation();
      }
      setShowDeleteUnmarkedConfirm(false);
      setShowUnmarkedDropdown(false);
    } catch (error) {
      const code = (error as any)?.code;
      if (code === 'email-not-verified') {
        alert('Verify your email (check your inbox or use the magic link) before deleting unmarked images.');
      } else {
        console.error('Error deleting unmarked:', error);
        alert('Failed to delete unmarked images. Please try again.');
      }
    } finally {
      setIsDeletingUnmarked(false);
    }
  };

  const downloadShard = useCallback(
    async (shardIndex: number) => {
      const jobSnapshot = pendingDownloadJob;
      if (!jobSnapshot) {
        return;
      }
      if (shardDownloadRef.current) {
        return;
      }
      shardDownloadRef.current = true;
      try {
        const currentUser = authService.getCurrentUser();
        if (!currentUser) throw new Error("User not authenticated.");
        const idToken = await authService.requireAuthToken();

        const payload: Record<string, unknown> = {
          batch_id: jobSnapshot.batchId,
          job_id: jobSnapshot.jobId,
        };
        if (shardIndex > 0) {
          payload.shard_index = shardIndex;
        }

        const urlResponse = await fetch(`${HTTP_API_URL}/batch/get-download-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(payload),
        });

        if (!urlResponse.ok) {
          const detail = await readErrorDetail(urlResponse);
          if (urlResponse.status === 403) {
            setDownloadLimitModalMessage(detail ?? DEFAULT_DOWNLOAD_LIMIT_MESSAGE);
          }
          throw new Error(detail ?? "Unable to get the download URL.");
        }

        const { download_url, total_parts = 1, part_index = shardIndex, filename } = await urlResponse.json();

        if (typeof download_url === 'string') {
          const a = document.createElement('a');
          a.href = download_url;
          a.download = filename || `${jobSnapshot.batchId}${total_parts > 1 ? `_part${part_index + 1}` : ''}.zip`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }

        if (total_parts > part_index + 1) {
          setPendingDownloadJob((prev) =>
            prev ? { ...prev, shardIndex: part_index + 1 } : prev
          );
        } else {
          await finalizeDownload(jobSnapshot.batchId, jobSnapshot.jobId);
          setPendingDownloadJob(null);
          setIsDownloading(false);
          setDownloadBatchOverride(null);
          await resetUploadSection();
        }
      } catch (error) {
        const code = (error as any)?.code;
        if (code === 'email-not-verified') {
          setErrorMsg('Please verify your email before downloading. Check your inbox or use the magic link.');
        } else {
          console.error("Error downloading file:", error);
          setErrorMsg("Download fallito.");
        }
        setPendingDownloadJob(null);
        setIsDownloading(false);
        setDownloadBatchOverride(null);
      } finally {
        shardDownloadRef.current = false;
      }
    },
    [pendingDownloadJob, readErrorDetail, finalizeDownload, resetUploadSection]
  );

  // Trigger download when ZIP metadata is ready
  useEffect(() => {
    if (!isDownloading || !pendingDownloadJob) {
      return;
    }
    if (!downloadBatchStatus || pendingDownloadJob.batchId !== downloadBatchId) {
      return;
    }
    if (!downloadBatchStatus.zip_path) {
      return;
    }
    if (
      pendingDownloadJob.jobId &&
      downloadBatchStatus.zip_ready_job_id !== pendingDownloadJob.jobId
    ) {
      return;
    }
    const shardIndex = pendingDownloadJob.shardIndex ?? 0;
    downloadShard(shardIndex);
  }, [
    downloadBatchStatus?.zip_path,
    downloadBatchStatus?.zip_ready_job_id,
    downloadBatchStatus,
    downloadBatchId,
    isDownloading,
    pendingDownloadJob,
    downloadShard,
  ]);

  const handleCancelDrag = () => {
    if (!draggedMarkerInfo) return;
    // Non fare nulla allo stato `markers`, basta chiudere il popup.
    // Il marker originale verrà reinserito dal useEffect di Map.tsx nella sua vecchia posizione.
    setDraggedMarkerInfo(null);
  };

  const hasPromptModal = !!userPrompt && !modalImageUrl && !forceClosePromptModal;

  useEffect(() => {
    const currentPromptId = userPrompt
      ? userPrompt.id ??
      `${userPrompt.type ?? "unknown"}::${userPrompt.current_file ?? ""}::${userPrompt.hint_source ?? ""}::${userPrompt.message ?? ""}`
      : "";

    if (userPrompt && currentPromptId !== lastPromptIdRef.current) {
      setModalSelected(userPrompt.selected_option || "");
      setForceClosePromptModal(false);
      setPromptError("");
      lastPromptIdRef.current = currentPromptId;
    } else if (!userPrompt && lastPromptIdRef.current) {
      lastPromptIdRef.current = "";
    }
  }, [userPrompt, userPrompt?.id, userPrompt?.type, userPrompt?.current_file, userPrompt?.hint_source, userPrompt?.message]);


  useEffect(() => {
    setStartMsg(null);
    setErrorMsg(null);
  }, [formState]);

  async function handleUserPromptChoice(type: string, choice: PromptChoice) {
    setModalSelected("");

    if (!batchId) {
      setPromptError("Batch ID not found. Unable to submit the choice.");
      return;
    }

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        setPromptError("User not authenticated. Unable to submit the choice.");
        return;
      }
      const idToken = await authService.requireAuthToken();

      // Invece di una chiamata HTTP, inviamo la scelta tramite WebSocket
      // (o manteniamo HTTP se il backend lo gestisce così)
      const response = await fetch(`${HTTP_API_URL}/batch/choice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          batch_id: batchId,
          user_choice: {
            type: type,
            choice: choice
          }
        }),
      });

      if (response.ok) {
        // Non è più necessario gestire lo stato del prompt qui,
        // Firestore si aggiornerà e l'hook `useBatchListener` riceverà la modifica.
        setForceClosePromptModal(true);
        setPromptError("");
      } else {
        setForceClosePromptModal(false);
        setPromptError("Backend communication error. Please try again.");
      }
    } catch (error) {
      setForceClosePromptModal(false);
      setPromptError("Network error. Check your connection and try again.");
      console.error("Network error:", error);
    }
  }

  const uploadFilesWithProgress = useCallback(
    (
      formData: FormData,
      idToken: string,
      onProgress: (percentage: number) => void
    ): Promise<{ batch_id: string; file_count: number }> =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${HTTP_API_URL}/batch/upload-files`, true);
        xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.min(
              100,
              Math.round((event.loaded / event.total) * 100)
            );
            onProgress(percent);
          }
        };
        xhr.onload = () => {
          try {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(JSON.parse(xhr.responseText));
              return;
            }
            reject(
              new Error(
                `Error uploading files: ${xhr.status} - ${xhr.statusText}`
              )
            );
          } catch (err) {
            reject(err);
          }
        };
        xhr.onerror = () => {
          reject(new Error("Network error during file upload."));
        };
        xhr.send(formData);
      }),
    [HTTP_API_URL]
  );

  const uploadFilesSignedWithProgress = useCallback(
    async (
      items: UploadItem[],
      folderName: string,
      idToken: string
    ): Promise<{ batch_id: string; file_count: number }> => {
      const retryDelays = [1000, 2000, 4000];
      const connectionKey = buildStateKey("upload_settings", "Rate your connection");
      const connectionValue = ((formState as any)[connectionKey] as string) || "average";
      const chunkMap: Record<string, number> = {
        weak: 5 * 1024 * 1024,
        average: 8 * 1024 * 1024,
        good: 16 * 1024 * 1024,
      };
      let currentChunkSize = chunkMap[connectionValue] || chunkMap["average"];
      const minChunkSize = 2 * 1024 * 1024; // 2 MB floor for degradations
      console.info(
        "[UPLOAD][INIT] connection_rating=%s chunk_size=%s bytes",
        connectionValue,
        currentChunkSize
      );

      const waitIfPaused = async () => {
        while (uploadPausedRef.current) {
          await new Promise((res) => setTimeout(res, 150));
        }
      };

      const sessionKeyFor = (blobPath: string, size: number) =>
        `gcs_upload_${blobPath}_${size}`;

      const recoverOffset = async (sessionUrl: string, total: number) => {
        try {
          const res = await fetch(sessionUrl, {
            method: "PUT",
            headers: { "Content-Range": `bytes */${total}` },
          });
          if (res.status === 308) {
            const rangeHeader = res.headers.get("Range") || res.headers.get("range");
            if (rangeHeader) {
              const match = rangeHeader.match(/bytes=0-(\d+)/);
              if (match && match[1]) {
                const parsed = parseInt(match[1], 10);
                if (!Number.isNaN(parsed)) {
                  return parsed + 1;
                }
              }
            }
          } else if (res.ok) {
            return total; // già completato
          }
        } catch (err) {
          console.warn("Unable to recover offset for resumable session", err);
        }
        return 0;
      };

      const itemsWithPaths = items.map((item) => ({
        ...item,
        content_type: item.content_type || "application/octet-stream",
      }));

      const payload = {
        folder_name: folderName,
        files: itemsWithPaths.map((item) => ({
          name: item.name,
          size: item.size,
          content_type: item.content_type,
        })),
      };

      const totalBytes = itemsWithPaths.reduce((acc, item) => acc + (item.size || 0), 0) || 1;
      let uploadedBytes = 0;

      const resp = await fetch(`${HTTP_API_URL}/batch/upload-files-signed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      const formatBytes = (val?: number) =>
        typeof val === "number" ? formatBytesForDisplay(val) : undefined;

      if (!resp.ok) {
        let detail = `${resp.status} ${resp.statusText}`;
        try {
          const data = await resp.json();
          const rawDetail = (data as any)?.detail || (data as any)?.message || detail;
          if (typeof rawDetail === "object" && rawDetail !== null) {
            const obj: any = rawDetail;
            const msg = obj.message || detail;
            const current = obj.readable_current || formatBytes(obj.current_bytes);
            const incoming = obj.readable_incoming || formatBytes(obj.incoming_bytes);
            const excess = obj.readable_excess || formatBytes(obj.excess_bytes);
            detail = `${msg} Current: ${current}, Incoming: ${incoming}, Excess: ${excess}`;
          } else {
            detail = rawDetail as string;
          }
        } catch (err) {
          // noop, keep default detail
        }
        const error: any = new Error(`Signed upload init failed: ${detail}`);
        error.status = resp.status;
        error.detail = detail;
        throw error;
      }

      const data = await resp.json();
      if (data?.batch_id) {
        // Persiste subito il batch_id ricevuto all'init: se la connessione
        // si interrompe durante il caricamento, il client può comunque mostrare
        // il bottone "Clear uploads" e liberare il lock pending_upload_batch.
        setBatchId(data.batch_id);
      }
      const uploads: Array<{ name: string; path?: string; signed_url: string; resumable_url?: string; content_type: string }> = data.uploads || [];
      const uploadMap = new Map(uploads.map((u) => [u.name, u]));

      const startResumableSession = async (url: string, contentType: string): Promise<string> => {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": contentType || "application/octet-stream",
            "x-goog-resumable": "start",
          },
        });
        if (!res.ok) {
          throw new Error(`Resumable init failed: ${res.status} ${res.statusText}`);
        }
        const loc = res.headers.get("location") || res.headers.get("Location");
        if (!loc) {
          throw new Error("Resumable init failed: missing session URL");
        }
        return loc;
      };

      const uploadSimpleWithRetry = async (url: string, file: File, contentType: string) => {
        let attempt = 0;
        while (true) {
          try {
            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("PUT", url, true);
              const finalContentType = contentType || "application/octet-stream";
              xhr.setRequestHeader("Content-Type", finalContentType);
              xhr.setRequestHeader("Content-Length", file.size.toString());
              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const percent = Math.min(
                    100,
                    Math.round(((uploadedBytes + event.loaded) / totalBytes) * 100)
                  );
                  setUploadProgress(percent);
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  uploadedBytes += file.size || 0;
                  const percent = Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
                  setUploadProgress(percent);
                  resolve();
                } else {
                  reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
                }
              };
              xhr.onerror = () => {
                reject(new Error("Network error during upload"));
              };
              xhr.send(file);
            });
            return;
          } catch (err) {
            attempt += 1;
            if (attempt > retryDelays.length) {
              throw err;
            }
            await new Promise((res) => setTimeout(res, retryDelays[attempt - 1]));
          }
        }
      };

      const uploadResumableChunked = async (
        sessionUrl: string,
        file: File,
        contentType: string,
        startingOffset: number
      ) => {
        let offset = startingOffset;
        const total = file.size;

        if (offset >= total) {
          return;
        }

        const uploadChunk = async (start: number, end: number) => {
          await waitIfPaused();
          let attempt = 0;
          while (true) {
            try {
              const chunk = file.slice(start, end);
              const controller = new AbortController();
              activeUploadControllerRef.current = controller;
              const res = await fetch(sessionUrl, {
                method: "PUT",
                headers: {
                  "Content-Type": contentType,
                  "Content-Length": `${chunk.size}`,
                  "Content-Range": `bytes ${start}-${end - 1}/${total}`,
                },
                body: chunk,
                signal: controller.signal,
              });
              activeUploadControllerRef.current = null;

              if (res.status === 308) {
                const rangeHeader = res.headers.get("Range") || res.headers.get("range");
                if (rangeHeader && rangeHeader.startsWith("bytes=")) {
                  const parts = rangeHeader.replace("bytes=", "").split("-");
                  const parsedEnd = parseInt(parts[1], 10);
                  if (!isNaN(parsedEnd)) {
                    return parsedEnd + 1;
                  }
                }
                return end; // fallback: assume chunk accepted
              }

              if (res.ok) {
                return end;
              }

              throw new Error(`Chunk upload failed: ${res.status} ${res.statusText}`);
            } catch (err: any) {
              activeUploadControllerRef.current = null;
              if (err?.name === "AbortError" && uploadPausedRef.current) {
                await waitIfPaused();
                continue;
              }
              // Degradazione chunk dinamica
              const degraded = Math.max(minChunkSize, Math.floor(currentChunkSize / 2));
              if (degraded < currentChunkSize) {
                console.info(
                  "[UPLOAD][DEGRADE] Reducing chunk size from %s to %s after error",
                  currentChunkSize,
                  degraded
                );
                currentChunkSize = degraded;
              }
              attempt += 1;
              if (attempt > retryDelays.length) {
                throw err;
              }
              await waitIfPaused();
              await new Promise((res) => setTimeout(res, retryDelays[attempt - 1]));
            }
          }
        };

        while (offset < total) {
          const end = Math.min(offset + currentChunkSize, total);
          const nextOffset = await uploadChunk(offset, end);
          uploadedBytes += nextOffset - offset;
          const percent = Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
          setUploadProgress(percent);
          offset = nextOffset;
        }
      };

      const resolveFile = async (item: UploadItem) => {
        if (item.file) return item.file;
        if (item.getFile) {
          return await item.getFile();
        }
        throw new Error(`Missing file handle for ${item.name}`);
      };

      for (const item of itemsWithPaths) {
        const uploadInfo = uploadMap.get(item.name);
        if (!uploadInfo?.signed_url) {
          throw new Error(`Missing signed URL for ${item.name}`);
        }

        const contentType = uploadInfo.content_type || item.content_type || "application/octet-stream";
        const file = await resolveFile(item);

        if (uploadInfo.resumable_url) {
          const sessionKey = sessionKeyFor(uploadInfo.path || uploadInfo.name, item.size || 0);
          let sessionUrl = localStorage.getItem(sessionKey) || null;
          if (!sessionUrl) {
            sessionUrl = await startResumableSession(uploadInfo.resumable_url, contentType);
            localStorage.setItem(sessionKey, sessionUrl);
          }
          const startingOffset = await recoverOffset(sessionUrl, file.size || 0);
          uploadedBytes += startingOffset;
          setUploadProgress(
            Math.min(100, Math.round((uploadedBytes / totalBytes) * 100))
          );
          await uploadResumableChunked(sessionUrl, file, contentType, startingOffset);
          localStorage.removeItem(sessionKey);
        } else {
          await uploadSimpleWithRetry(uploadInfo.signed_url, file, contentType);
        }
      }

      return { batch_id: data.batch_id, file_count: data.file_count };
    },
    [HTTP_API_URL]
  );

  const buildUploadItemsFromFiles = useCallback((files: File[]): UploadItem[] => {
    return files.map((file) => {
      const fileWithFolder = file as File & { _folderPath?: string; webkitRelativePath?: string };
      let finalPath = fileWithFolder.webkitRelativePath;
      if (!finalPath) {
        if (fileWithFolder._folderPath) {
          finalPath = `${fileWithFolder._folderPath}/${file.name}`;
        } else {
          finalPath = file.name;
        }
      }
      return {
        name: finalPath,
        size: file.size,
        content_type: file.type || "application/octet-stream",
        file,
      };
    });
  }, []);

  const handleUploadItems = async (items: UploadItem[], folderName: string, mode: UploadMode) => {
    if (!isUserAuthenticated) {
      handleRequireAuthForUpload();
      return;
    }
    if (!checkUploadsAllowed()) {
      return;
    }
    setUploadSectionResetBatch(null);
    if (!checkCreditsBeforeUpload(items.length)) {
      return;
    }
    setUploadMode(mode);
    setUploadValidationError(null);
    setUploadValidationTitle(null);
    setUploadPaused(false);
    uploadPausedRef.current = false;
    setUploadedFileCount(items.length);
    const totalBytes = items.reduce((acc, item) => acc + item.size, 0);
    setUploadedTotalBytes(totalBytes);
    const totalSizeMB = totalBytes / (1024 * 1024);
    setUploadedTotalSize(Number(totalSizeMB.toFixed(2)));

    const projectedUsage = storageUsageBytes + totalBytes;
    if (projectedUsage > UPLOAD_STORAGE_CAP_BYTES) {
      const excess = projectedUsage - UPLOAD_STORAGE_CAP_BYTES;
      setUploadStatus('idle');
      setUploadProgress(0);
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadValidationTitle("Storage limit reached");
      setUploadValidationError(
        `Storage limits reached. Please delete some stored images before uploading new files. ` +
        `Current: ${formatBytesForDisplay(storageUsageBytes)}, Incoming: ${formatBytesForDisplay(totalBytes)}, ` +
        `Excess: ${formatBytesForDisplay(excess)}`
      );
      return;
    }

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error("User not authenticated.");
      }
      const idToken = await authService.requireAuthToken();

      setUploadStatus('uploading');
      setUploadProgress(0);
      setUploadPaused(false);
      uploadPausedRef.current = false;

      let result: { batch_id: string; file_count: number };

      try {
        result = await uploadFilesSignedWithProgress(items, folderName, idToken);
      } catch (signedError) {
        const status = (signedError as any)?.status as number | undefined;
        const detail = (signedError as any)?.detail || (signedError as Error).message;

        console.warn("Signed upload failed:", signedError);
        setUploadStatus('idle');
        setUploadProgress(0);
        setUploadPaused(false);
        uploadPausedRef.current = false;
        setFilesUploaded(false);
        setUploadedFileCount(0);
        setUploadedTotalBytes(null);
        const uploadError = buildUploadErrorInfo(status, detail);
        setUploadValidationTitle(uploadError.title);
        setUploadValidationError(
          uploadError.message
        );
        setUploadMode(null);
        if (status === 409 || status === 429) {
          setUploadClearToken((prev) => prev + 1);
        }
        return;
      }

      setUploadStatus('done');
      setUploadProgress(100);
      setUploadPaused(false);
      uploadPausedRef.current = false;
      setBatchId(result.batch_id); // Salva il batch_id ricevuto
      setFilesUploaded(true);
      setUploadMode(mode);

      try {
        const readConfirmDetail = async (response: Response): Promise<string> => {
          try {
            const data = await response.clone().json();
            if (typeof data?.detail === "string") {
              return data.detail;
            }
            if (typeof data?.message === "string") {
              return data.message;
            }
          } catch {
            // ignore json parse issues
          }
          try {
            const text = await response.text();
            if (text) {
              return text;
            }
          } catch {
            // ignore text parse issues
          }
          return `HTTP ${response.status} ${response.statusText}`;
        };

        const maxAttempts = 3;
        let confirmed = false;
        let lastConfirmError = "Unable to confirm storage usage update.";
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          const response = await fetch(`${HTTP_API_URL}/batch/confirm-upload-usage`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ batch_id: result.batch_id }),
          });

          if (response.ok) {
            confirmed = true;
            break;
          }

          lastConfirmError = await readConfirmDetail(response);
          const retryable = response.status === 409;
          if (retryable && attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, attempt * 1200));
            continue;
          }
          break;
        }

        if (!confirmed) {
          throw new Error(lastConfirmError);
        }
        await fetchUserProfile(true).catch(() => null);
      } catch (e) {
        const message =
          e instanceof Error && e.message
            ? e.message
            : "Upload completed but storage usage sync failed.";
        console.warn("Unable to confirm storage usage increment:", e);
        setErrorMsg(`Upload completed, but storage counter sync failed: ${message}`);
      }

      // Auto-scroll to "Start analysis" button on mobile after upload
      if (isMobileLayout) {
        setTimeout(() => {
          startAnalysisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }

    } catch (error) {
      const code = (error as any)?.code;
      if (code === 'email-not-verified') {
        setUploadValidationTitle("Email verification required");
        setUploadValidationError('Verify your email (or use the magic link) before uploading files.');
      } else {
        console.error("Error uploading files:", error);
        setErrorMsg("Error uploading files: " + (error as Error).message);
      }
      setUploadStatus('idle');
      setUploadProgress(0);
      setFilesUploaded(false);
      setUploadedFileCount(0);
      setUploadedTotalBytes(null);
      setUploadedTotalSize(0);
      setUploadMode(null);
    }
  };

  const handleFilesUploaded = async (files: File[], folderName: string) => {
    const items = buildUploadItemsFromFiles(files);
    await handleUploadItems(items, folderName, 'download');
  };

  const resolveDirectWriteSelection = useCallback(
    (selection: DirectWriteSelection, entries: DirectWriteEntry[]) => {
      if (selection.allSelected) {
        const excluded = new Set(selection.excludedIds);
        return entries.filter((entry) => !excluded.has(entry.id));
      }
      const selected = new Set(selection.selectedIds);
      return entries.filter((entry) => selected.has(entry.id));
    },
    []
  );

  const startDirectWriteScan = useCallback(
    async (handle: FileSystemDirectoryHandle) => {
      directWriteScanAbortRef.current?.abort();
      directWriteScanAbortRef.current = null;
      const abortController = new AbortController();
      directWriteScanAbortRef.current = abortController;
      setDirectWriteScanItems([]);
      setDirectWriteScanWarnings([]);
      setDirectWriteScanStatus('scanning');
      setDirectWriteScanCount(0);
      setDirectWriteIgnoredFiles([]);
      setDirectWriteIgnoredCount(0);

      const warningsSet = new Set<string>();
      const pushWarning = (warning: string) => {
        if (warningsSet.has(warning)) return;
        warningsSet.add(warning);
        setDirectWriteScanWarnings(Array.from(warningsSet));
        const lowered = warning.toLowerCase();
        if (lowered.includes("recents") || lowered.includes("virtual") || lowered.includes("real folder")) {
          void clearDirectoryHandle();
          directWriteHandleRef.current = null;
          setDirectWriteHandleName(null);
        }
      };
      const ignoredSamples: DirectWriteIgnoredEntry[] = [];
      let ignoredTotal = 0;
      const pushIgnored = (entry: DirectWriteIgnoredEntry) => {
        ignoredTotal += 1;
        setDirectWriteIgnoredCount(ignoredTotal);
        if (ignoredSamples.length < 80) {
          ignoredSamples.push(entry);
          setDirectWriteIgnoredFiles([...ignoredSamples]);
        }
      };

      const chunk: DirectWriteEntry[] = [];
      let count = 0;
      try {
        for await (const entry of streamDirectWriteEntries(handle, {
          signal: abortController.signal,
          onWarning: pushWarning,
          onIgnored: pushIgnored,
        })) {
          if (abortController.signal.aborted) return;
          chunk.push(entry);
          count += 1;
          if (chunk.length >= 40) {
            const nextChunk = [...chunk];
            setDirectWriteScanItems((prev) => [...prev, ...nextChunk]);
            setDirectWriteScanCount(count);
            chunk.length = 0;
          }
        }
        if (abortController.signal.aborted) return;
        if (chunk.length > 0) {
          const nextChunk = [...chunk];
          setDirectWriteScanItems((prev) => [...prev, ...nextChunk]);
        }
        setDirectWriteScanCount(count);
        setDirectWriteScanStatus('done');
        if (count === 0) {
          pushWarning("No supported image files found in the selected folder.");
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        pushWarning(`Unable to scan folder: ${String((err as any)?.message || err)}`);
        setDirectWriteScanStatus('error');
      }
    },
    []
  );

  const handleDirectWriteSelectionConfirm = useCallback(
    async (selection: DirectWriteSelection) => {
      const handle = directWriteHandleRef.current;
      if (!handle) {
        setDirectWriteStatus("Select a folder first.");
        return;
      }

      const selectedEntries = resolveDirectWriteSelection(selection, directWriteScanItems);
      if (selectedEntries.length === 0) {
        setDirectWriteStatus("No files selected. Pick at least one file.");
        return;
      }
      if (!checkCreditsBeforeUpload(selectedEntries.length)) {
        return;
      }

      const perm = await ensureWritePermission(handle);
      const denied =
        perm.state === 'denied' &&
        (perm.requestState === 'denied' || perm.queryState === 'denied');
      if (denied) {
        setDirectWriteStatus("Permission revoked. Please reselect the folder.");
        return;
      }

      directWriteScanAbortRef.current?.abort();
      directWriteScanAbortRef.current = null;
      setDirectWritePickerOpen(false);
      setDirectWriteScanItems([]);
      setDirectWriteScanWarnings([]);
      setDirectWriteScanStatus('idle');
      setDirectWriteScanCount(0);
      setDirectWriteIgnoredFiles([]);
      setDirectWriteIgnoredCount(0);
      setDirectWriteSelectedCount(selectedEntries.length);
      setDirectWritePreparing(true);
      setDirectWritePrepareProgress(0);
      setDirectWriteStatus(`Preparing ${selectedEntries.length} files...`);

      const items: UploadItem[] = [];
      let firstPrepareError: any = null;
      const total = selectedEntries.length;
      for (let index = 0; index < total; index += 1) {
        const entry = selectedEntries[index];
        try {
          let cachedFile: File | null = null;
          const readFileWithFallback = async () => {
            let file: File | undefined;
            let directError: any = null;
            try {
              file = await entry.handle.getFile();
            } catch (err) {
              directError = err;
            }
            if (!file && entry.parentHandle) {
              try {
                const refreshed = await entry.parentHandle.getFileHandle(entry.name);
                file = await refreshed.getFile();
              } catch (err) {
                if (!directError) {
                  directError = err;
                }
              }
            }
            if (!file) {
              throw directError;
            }
            return file;
          };
          cachedFile = await readFileWithFallback();
          items.push({
            name: entry.fullPath,
            size: cachedFile.size,
            content_type: cachedFile.type || "application/octet-stream",
            getFile: async () => {
              if (cachedFile) return cachedFile;
              cachedFile = await readFileWithFallback();
              return cachedFile;
            },
          });
        } catch (err) {
          console.warn("Unable to read direct write file", entry.relativePath, err);
          if (!firstPrepareError) {
            firstPrepareError = err;
          }
        }
        if (index % 25 === 0 || index === total - 1) {
          const percent = Math.min(100, Math.round(((index + 1) / total) * 100));
          setDirectWritePrepareProgress(percent);
        }
      }

      setDirectWritePreparing(false);
      setDirectWritePrepareProgress(0);

      if (items.length === 0) {
        const errMsg = firstPrepareError?.message || String(firstPrepareError || '');
        setDirectWriteStatus(
          errMsg
            ? `No supported files could be prepared. ${errMsg}`
            : "No supported files could be prepared. Try again."
        );
        return;
      }
      if (items.length !== selectedEntries.length) {
        setDirectWriteSelectedCount(items.length);
      }

      await handleUploadItems(items, handle.name || "", 'direct');
      setDirectWriteStatus("Files loaded from selected folder");
    },
    [checkCreditsBeforeUpload, directWriteScanItems, handleUploadItems, resolveDirectWriteSelection]
  );

  const handleCloseDirectWritePicker = useCallback(() => {
    if (directWriteScanStatus === 'scanning') {
      directWriteScanAbortRef.current?.abort();
    }
    directWriteScanAbortRef.current = null;
    setDirectWritePickerOpen(false);
    setDirectWriteStatus("Selection cancelled");
    setDirectWriteScanItems([]);
    setDirectWriteScanWarnings([]);
    setDirectWriteScanStatus('idle');
    setDirectWriteScanCount(0);
    setDirectWriteIgnoredFiles([]);
    setDirectWriteIgnoredCount(0);
    setDirectWriteSelectedCount(0);
  }, [directWriteScanStatus]);

  const handleLoadDirectWriteFiles = useCallback(async () => {
    setDirectWriteLoading(true);
    setDirectWriteStatus(null);
    try {
      if (!checkUploadsAllowed()) {
        if (uploadBlockInfo?.message) {
          setDirectWriteStatus(uploadBlockInfo.message);
        }
        return;
      }
      if (!directWriteSupport.supported) {
        setDirectWriteStatus(directWriteSupport.reason || "Direct Write not supported");
        return;
      }
      const picker = (window as any).showDirectoryPicker;
      if (typeof picker !== "function") {
        setDirectWriteStatus("Directory picker not available");
        return;
      }
      const handle: FileSystemDirectoryHandle = await picker({ mode: "readwrite" });
      if ((handle as any).kind && (handle as any).kind !== "directory") {
        setDirectWriteStatus("Please pick a real folder (not Recents or a single file).");
        setDirectWriteHandleName(null);
        await clearDirectoryHandle();
        return;
      }
      const perm = await ensureWritePermission(handle);
      logPermissionState("User-selected handle", perm);
      const explicitlyDenied = perm.state === "denied" && (perm.requestState === "denied" || perm.queryState === "denied");
      if (explicitlyDenied) {
        setDirectWriteStatus(
          "Permission denied or folder not writable. Pick a real folder (e.g., DCIM/Camera, not Recents)."
        );
        setDirectWriteHandleName(null);
        await clearDirectoryHandle();
        return;
      }
      if (perm.state !== "granted") {
        setDirectWriteStatus("Grant read/write permission to use Direct Write.");
        setDirectWriteHandleName(null);
        await clearDirectoryHandle();
        return;
      }
      directWriteHandleRef.current = handle;
      await saveDirectoryHandle(handle);
      setDirectWriteHandleName(handle.name || "Selected folder");
      setDirectWriteWriteProgress(0);
      setDirectWriteWriterMessage(null);
      setDirectWriteWriteReport([]);
      setDirectWriteHadFailure(false);
      setDirectWriteHighlight(false);
      setDirectWritePreparing(false);
      setDirectWritePrepareProgress(0);
      setDirectWriteSelectedCount(0);
      setDirectWritePickerOpen(true);
      setDirectWriteStatus("Scanning selected folder...");
      void startDirectWriteScan(handle);
    } catch (err: any) {
      if (err?.name === "AbortError") {
        setDirectWriteStatus("Selection cancelled");
      } else {
        console.warn("Unable to load files from direct write handle", err);
        setDirectWriteStatus("Unable to load files from the selected folder");
      }
    } finally {
      setDirectWriteLoading(false);
    }
  }, [checkUploadsAllowed, directWriteSupport, startDirectWriteScan, uploadBlockInfo?.message]);

  useEffect(() => {
    if (!directWritePickerOpen) return;
    if (directWriteScanStatus === 'scanning') {
      const suffix = directWriteScanCount > 0 ? ` (${directWriteScanCount} found)` : '';
      setDirectWriteStatus(`Scanning selected folder${suffix}...`);
      return;
    }
    if (directWriteScanStatus === 'done') {
      if (directWriteScanWarnings.length > 0) {
        setDirectWriteStatus(directWriteScanWarnings.join(' '));
      } else {
        setDirectWriteStatus(`Found ${directWriteScanCount} files. Select files to upload.`);
      }
      return;
    }
    if (directWriteScanStatus === 'error') {
      setDirectWriteStatus(directWriteScanWarnings.join(' ') || 'Unable to scan folder.');
    }
  }, [directWritePickerOpen, directWriteScanStatus, directWriteScanWarnings, directWriteScanCount]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const recheckPermission = async () => {
      const handle = directWriteHandleRef.current;
      if (!handle) return;
      try {
        const permission = await ensureWritePermission(handle);
        logPermissionState("Focus permission check", permission);
        if (permission.state === "denied") {
          console.warn("[DirectWrite][Telemetry] handle-stale-permission-denied");
          await clearDirectoryHandle();
          directWriteHandleRef.current = null;
          setDirectWriteHandleName(null);
          setDirectWriteStatus("Direct Write permission revoked. Pick the folder again.");
          setDirectWriteWriteReport([]);
          setDirectWriteHadFailure(false);
          setDirectWritePickerOpen(false);
          setDirectWriteScanItems([]);
          setDirectWriteScanWarnings([]);
          setDirectWriteScanStatus('idle');
          setDirectWriteScanCount(0);
          setDirectWriteIgnoredFiles([]);
          setDirectWriteIgnoredCount(0);
          setDirectWriteSelectedCount(0);
        }
      } catch (err) {
        console.warn("Unable to revalidate permission on focus", err);
      }
    };
    window.addEventListener("focus", recheckPermission);
    return () => window.removeEventListener("focus", recheckPermission);
  }, []);

  // Force ZIP fallback if Direct Write isn't viable (no support or no handle) when user tries to write.
  // Avoid memoizing on a ref-only dependency so the flag updates after folder selection.
  const isDirectWriteFallback =
    !directWriteSupport.supported || (directWriteEnabled && !directWriteHandleRef.current);

  useEffect(() => {
    if (!isDirectWriteReady) return;
    if (!directWriteHandleRef.current) return;
    setDirectWriteHighlight(true);
    const timeout = window.setTimeout(() => setDirectWriteHighlight(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [isDirectWriteReady]);

  useEffect(() => {
    if (!batchId || !isBatchComplete) return;
    if (lastAnalysisCompleteBatchRef.current === batchId) return;
    setAnalysisCompleteModalOpen(true);
    lastAnalysisCompleteBatchRef.current = batchId;
  }, [batchId, isBatchComplete]);

  const handleTriggerFolderPicker = () => {
    const folderPickerInput = document.querySelector('input[type="file"][webkitdirectory]') as HTMLInputElement;
    if (folderPickerInput) {
      folderPickerInput.click();
    }
  };

  const handleDirectWriteApplyProcessed = useCallback(
    async (options?: { retryFailed?: boolean }) => {
      setDirectWriteWriterMessage(null);
      setDirectWriteWriteProgress(0);

      if (!directWriteEnabled) {
        setDirectWriteWriterMessage("Enable Direct Write to save files in-place.");
        return;
      }
      if (!directWriteSupport.supported) {
        setDirectWriteWriterMessage(directWriteSupport.reason || "Direct Write not supported on this device.");
        return;
      }
      if (!batchStatus || batchStatus.progress !== 100) {
        setDirectWriteWriterMessage("Wait for the analysis to finish before writing files.");
        return;
      }

      const targets = options?.retryFailed ? buildDirectWriteTargets(true) : buildDirectWriteTargets(false);
      if (!targets.length) {
        setDirectWriteWriterMessage(options?.retryFailed ? "Nothing to retry." : "No processed files ready to write.");
        setDirectWriteWriteReport([]);
        return;
      }

      let authToken = "";
      try {
        authToken = await authService.requireAuthToken();
      } catch (err) {
        console.warn("Unable to fetch auth token for Direct Write signed URLs", err);
        setDirectWriteWriterMessage("Authentication required to download processed files.");
        return;
      }

      // ──────────────────────────────────────────────────────────────────────
      // ANDROID-SPECIFIC FLOW: Use showOpenFilePicker to get writable handles
      // ──────────────────────────────────────────────────────────────────────
      if (directWriteSupport.needsFilePickerForWrite) {
        console.info("[DirectWrite] Android mode: using showOpenFilePicker for write phase");

        // Show instruction message
        setDirectWriteWriterMessage(getAndroidReselectionMessage(targets.length));

        // Prompt user to select files
        let handleMap: Map<string, FileSystemFileHandle>;
        try {
          handleMap = await promptForWritableHandles();
        } catch (err: any) {
          const errMsg = err?.message || String(err);
          if (errMsg.includes("cancelled")) {
            setDirectWriteWriterMessage("File selection cancelled. Select files to write.");
            return;
          }
          setDirectWriteWriterMessage(`Unable to select files: ${errMsg}`);
          return;
        }

        if (handleMap.size === 0) {
          setDirectWriteWriterMessage("No files selected. Please select the files you want to update.");
          return;
        }

        // Prepare Android write targets
        const androidTargets: AndroidWriteTarget[] = targets.map((t) => ({
          id: t.id,
          targetRelativePath: t.targetRelativePath,
          originalRelativePath: t.originalRelativePath ?? undefined,
          downloadUrl: t.downloadUrl,
          blobPath: t.blobPath,
          size: t.size,
        }));

        const signedUrlCache = new Map<string, string>();

        setDirectWriteHadFailure(false);
        setDirectWriteWriting(true);
        setDirectWriteWriteReport([]);

        try {
          const androidResults = await writeToAndroidFileSystem(androidTargets, handleMap, {
            filesStatus: batchStatus?.files_status,
            onProgress: (processed, total) => {
              const percent = Math.min(100, Math.round((processed / total) * 100));
              setDirectWriteWriteProgress(percent);
            },
            resolveDownloadUrl: async (target) => {
              if (!target.blobPath) {
                return target.downloadUrl;
              }
              const normalized = target.blobPath.replace(/^\/+/, "");
              const cached = signedUrlCache.get(normalized);
              if (cached) return cached;

              const fetchSignedUrl = async (token: string) => {
                const resp = await fetch(`${HTTP_API_URL}/direct-write/signed-url`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ blob_path: normalized }),
                });
                if (!resp.ok) {
                  const detail = await resp.text();
                  throw new Error(`Signed URL request failed: ${resp.status} ${detail}`);
                }
                const payload = await resp.json();
                if (!payload?.url) {
                  throw new Error("Signed URL response missing url");
                }
                return payload.url as string;
              };

              try {
                const signedUrl = await fetchSignedUrl(authToken);
                signedUrlCache.set(normalized, signedUrl);
                return signedUrl;
              } catch (err: any) {
                const message = (err?.message || "").toLowerCase();
                if (message.includes("401") || message.includes("403")) {
                  authToken = await authService.requireAuthToken();
                  const signedUrl = await fetchSignedUrl(authToken);
                  signedUrlCache.set(normalized, signedUrl);
                  return signedUrl;
                }
                throw err;
              }
            },
          });

          // Convert AndroidWriteResult to DirectWriteResult for UI compatibility
          const results: DirectWriteResult[] = androidResults.map((r) => ({
            id: r.id,
            targetRelativePath: r.targetRelativePath,
            originalRelativePath: r.originalRelativePath ?? null,
            status: r.status,
            method: r.method as DirectWriteResult["method"],
            bytesWritten: r.bytesWritten,
            error: r.error,
          }));

          setDirectWriteWriteReport(results);
          const failed = results.filter((r) => r.status === "failed" && r.error).length;
          const skipped = results.filter((r) => r.status === "skipped").length;
          const written = results.length - failed - skipped;
          setDirectWriteHadFailure(failed > 0 || skipped > 0);

          if (failed > 0 || skipped > 0) {
            let msg = `Direct Write completed: ${written} succeeded`;
            if (failed > 0) msg += `, ${failed} failed`;
            if (skipped > 0) msg += `, ${skipped} not selected`;
            msg += ".";
            if (skipped > 0) {
              msg += " To update skipped files, select them in the file picker.";
            }
            setDirectWriteWriterMessage(msg);
          } else {
            setDirectWriteWriterMessage(`Direct Write completed: ${written} files updated in-place.`);
            setDirectWriteHadFailure(false);
            if (batchId) {
              try {
                await fetch(`${HTTP_API_URL}/batch/complete-direct-write`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                  body: JSON.stringify({ batch_id: batchId }),
                });
              } catch (err) {
                console.warn("Unable to finalize Direct Write batch state:", err);
              }
              if (!hasActiveSubscription) {
                await finalizeDownload(batchId);
              }
              setBatchId(null);
              clearPersistedBatch();
            }
            await resetUploadSection({ preserveDirectWriteReport: true });
          }
          setDirectWriteReportOpen(true);
          setDirectWriteHighlight(false);
        } catch (err: any) {
          console.error("Direct Write (Android) failed", err);
          setDirectWriteWriterMessage("Direct Write failed.");
          setDirectWriteHadFailure(true);
          setDirectWriteReportOpen(true);
        } finally {
          setDirectWriteWriting(false);
        }
        return;
      }

      // ──────────────────────────────────────────────────────────────────────
      // DESKTOP FLOW: Use directory handle (original implementation)
      // ──────────────────────────────────────────────────────────────────────
      const handle = directWriteHandleRef.current;
      if (!handle) {
        setDirectWriteWriterMessage("Select the folder again to write files in-place.");
        setDirectWriteStatus("Pick a folder to load files and write in-place.");
        return;
      }

      let permission: PermissionCheckResult | null = null;
      try {
        permission = await requestWritePermission(handle);
        logPermissionState("Write attempt (request)", permission);
      } catch (err) {
        console.warn("Unable to request permission before writing", err);
      }
      if (permission?.state === "denied") {
        await clearDirectoryHandle();
        directWriteHandleRef.current = null;
        setDirectWriteHandleName(null);
        setDirectWriteStatus("Permission required to use Direct Write");
        setDirectWriteWriterMessage("Direct Write permission was revoked. Select the folder again.");
        return;
      }
      if (permission && permission.state !== "granted") {
        setDirectWriteWriterMessage("Grant write permission to continue. Select the folder again.");
        return;
      }

      setDirectWriteHadFailure(false);
      setDirectWriteWriting(true);
      setDirectWriteWriteReport([]);
      const preferMove = directWriteSupport.platform !== "android";
      const chunkSize = directWriteSupport.platform === "android" ? 128 * 1024 : undefined;
      const signedUrlCache = new Map<string, string>();

      try {
        const handleLookup = await resolveDirectWriteHandles(handle, targets);
        const targetsWithHandles = targets.map((target) => {
          const rel = normalizeDirectWritePath(target.originalRelativePath || target.targetRelativePath);
          const key = rel ? rel.toLowerCase() : "";
          return { ...target, sourceHandle: key ? handleLookup.get(key) : undefined };
        });
        const rawResults = await writeDirectToFileSystem(handle, targetsWithHandles, {
          dropRootSegment: handle.name || null,
          preferMove,
          chunkSize,
          ensurePermission: async () => {
            const res = await ensureWritePermission(handle);
            logPermissionState("Write loop check", res);
            return res.state;
          },
          filesStatus: batchStatus?.files_status,
          resolveDownloadUrl: async (target) => {
            if (!target.blobPath) {
              return target.downloadUrl;
            }
            const normalized = target.blobPath.replace(/^\/+/, "");
            const cached = signedUrlCache.get(normalized);
            if (cached) return cached;

            const fetchSignedUrl = async (token: string) => {
              const resp = await fetch(`${HTTP_API_URL}/direct-write/signed-url`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ blob_path: normalized }),
              });
              if (!resp.ok) {
                const detail = await resp.text();
                throw new Error(`Signed URL request failed: ${resp.status} ${detail}`);
              }
              const payload = await resp.json();
              if (!payload?.url) {
                throw new Error("Signed URL response missing url");
              }
              return payload.url as string;
            };

            try {
              const signedUrl = await fetchSignedUrl(authToken);
              signedUrlCache.set(normalized, signedUrl);
              return signedUrl;
            } catch (err: any) {
              const message = (err?.message || "").toLowerCase();
              if (message.includes("401") || message.includes("403")) {
                authToken = await authService.requireAuthToken();
                const signedUrl = await fetchSignedUrl(authToken);
                signedUrlCache.set(normalized, signedUrl);
                return signedUrl;
              }
              throw err;
            }
          },
          onProgress: (processed) => {
            const percent = Math.min(100, Math.round((processed / targets.length) * 100));
            setDirectWriteWriteProgress(percent);
          },
        });
        const results: DirectWriteResult[] = rawResults;
        setDirectWriteWriteReport(results);
        const failed = results.filter((r) => r.status === "failed" && r.error).length;
        const written = results.length - failed;
        setDirectWriteHadFailure(failed > 0);
        if (failed > 0) {
          setDirectWriteWriterMessage(`Direct Write completed with warnings: ${written} succeeded, ${failed} failed. You can retry failed items.`);
          const permissionFail = results.some((r) => {
            const msg = (r.error || "").toLowerCase();
            return r.status === "failed" && (msg.includes("permission") || msg.includes("not allowed") || msg.includes("notallowed"));
          });
          if (permissionFail) {
            await clearDirectoryHandle();
            directWriteHandleRef.current = null;
            setDirectWriteHandleName(null);
            setDirectWriteStatus("Direct Write permission revoked. Pick the folder again.");
          }
        } else {
          setDirectWriteWriterMessage(`Direct Write completed: ${written} files updated in-place.`);
          setDirectWriteHadFailure(false);
          if (batchId) {
            try {
              await fetch(`${HTTP_API_URL}/batch/complete-direct-write`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ batch_id: batchId }),
              });
            } catch (err) {
              console.warn("Unable to finalize Direct Write batch state:", err);
            }
            if (!hasActiveSubscription) {
              await finalizeDownload(batchId);
            }

            // NEW: Explicitly clear frontend session state to prevent "Previous batch found"
            // or "Upload blocked" on next action.
            setBatchId(null);
            clearPersistedBatch();
          }
          await resetUploadSection({ preserveDirectWriteReport: true });
        }
        setDirectWriteReportOpen(true);
        setDirectWriteHighlight(false);
      } catch (err: any) {
        console.error("Direct Write failed", err);
        setDirectWriteWriterMessage("Direct Write failed.");
        setDirectWriteHadFailure(true);
        setDirectWriteReportOpen(true);
      } finally {
        setDirectWriteWriting(false);
      }
    },
    [
      batchId,
      batchStatus,
      buildDirectWriteTargets,
      directWriteEnabled,
      directWriteSupport.needsFilePickerForWrite,
      directWriteSupport.platform,
      directWriteSupport.reason,
      directWriteSupport.supported,
      finalizeDownload,
      hasActiveSubscription,
      normalizeDirectWritePath,
      resetUploadSection,
      resolveDirectWriteHandles,
    ]
  );

  async function handleStartBatch(options?: { skipQuotaCheck?: boolean; triggeredFromAuthModal?: boolean }) {
    const skipQuotaCheck = options?.skipQuotaCheck ?? false;
    const triggeredFromAuthModal = options?.triggeredFromAuthModal ?? false;
    console.log('🔄 handleStartBatch called, checking authentication...');
    console.log('🔐 isAuthenticated:', isAuthenticated);

    const prefetchedUser = authService.getCurrentUser();

    // Prima controllo se l'utente è autenticato o se esiste già un currentUser
    if (!isAuthenticated && !prefetchedUser) {
      if (!triggeredFromAuthModal) {
        console.log('❌ User not authenticated, showing authentication modal');
        setAuthModalIntent('start');
        setShowAuthModal(true);
      } else {
        console.log('⚠️ Authentication not completed yet, waiting without reopening the modal');
      }
      return;
    }

    if (!skipQuotaCheck && shouldWarnForDownloadQuota()) {
      setShowDownloadQuotaWarning(true);
      return;
    }

    console.log('✅ User authenticated, proceeding with analysis start');
    setLoading(true);
    setStartMsg(null);
    setErrorMsg(null);

    try {
      // Ottieni il token JWT dall'utente autenticato
      const currentUser = prefetchedUser ?? authService.getCurrentUser();
      if (!currentUser) {
        throw new Error("User not found after authentication");
      }

      console.log('👤 Current user:', currentUser.email);

      // Ottieni il token ID Firebase
      const idToken = await authService.requireAuthToken();
      console.log('🔑 Token fetched:', idToken ? 'Token present' : 'Token missing');

      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };

      const payload = mapUiSchemaToPayload(uiSchema, formState);
      if (!filesUploaded) {
        setErrorMsg("Drag & drop files first.");
        setLoading(false);
        return;
      }

      const activeBatchId = batchId ?? readStoredBatchId();
      if (!activeBatchId) {
        const missingBatchMsg =
          "Uploads are not finished yet. Please wait for completion before starting analysis.";
        setErrorMsg(missingBatchMsg);
        console.warn("⚠️ Batch ID mancante al momento della configurazione del batch");
        return;
      }

      // Check if neither AI nor automatic detection is enabled
      if (!payload.abilita_ai && !payload.ricerca_automatica) {
        setShowAnalysisModePopup(true);
        setLoading(false);
        return;
      }

      console.log('📤 Invio configurazione batch...');
      let resp = await fetch(`${HTTP_API_URL}/batch/config`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ batch_id: activeBatchId, config: payload })
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error('❌ Batch configuration error:', resp.status, errorText);
        throw new Error("Batch configuration error: " + errorText);
      }

      console.log('🚀 Avvio batch...');
      resp = await fetch(`${HTTP_API_URL}/batch/start`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ batch_id: activeBatchId })
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error('❌ Batch start error:', resp.status, errorText);

        if (resp.status === 402) {
          try {
            const errorJson = JSON.parse(errorText);
            const detail = errorJson.detail || "Insufficient credits.";
            const match = detail.match(/Necessari: (\d+), Disponibili: (\d+)/);
            if (match) {
              setCreditsInfo({ required: parseInt(match[1], 10), available: parseInt(match[2], 10) });
            }
            setShowInsufficientCreditsModal(true);
          } catch {
            setErrorMsg("Payment error: insufficient credits.");
          }
        } else {
          throw new Error("Batch start error: " + errorText);
        }
        return; // Non procedere oltre se c'è stato un errore
      }

      console.log('✅ Analysis started successfully');
      setStartMsg("Analysis started! Check progress bar below.");
    } catch (err) {
      console.error('❌ General error:', err);
      const code = (err as any)?.code;
      if (code === 'email-not-verified') {
        setErrorMsg('Please verify your email (check your inbox or use the magic link) before starting analysis.');
      } else {
        const message = err instanceof Error ? err.message : String(err);
        setErrorMsg(message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Funzione chiamata quando l'utente completa l'autenticazione
  const handleAuthSuccess = () => {
    console.log('✅ Authentication completed, closing modal');
    const intent = authModalIntent;
    setShowAuthModal(false);
    setAuthModalIntent(null);
    if (intent === 'start') {
      setTimeout(() => {
        handleStartBatch({ triggeredFromAuthModal: true });
      }, 500);
    }
  };

  // Funzione chiamata quando c'è un errore nell'autenticazione
  const handleAuthError = (error: string) => {
    console.error('❌ Authentication error:', error);
    // Non chiudiamo il modal in caso di errore, così l'utente può riprovare o leggere il messaggio
  };

  // Funzione per chiudere il modal di autenticazione
  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    setAuthModalIntent(null);
  };

  const handleRequireAuthForUpload = useCallback(() => {
    setAuthModalIntent('upload');
    setShowAuthModal(true);
  }, []);

  const handleRequireAuthFromAccount = useCallback(() => {
    setAuthModalIntent('account');
    setShowAuthModal(true);
  }, []);

  const handleSwitchToBetaSignup = () => {
    setShowAuthModal(false);
    setShowBetaSignupModal(true);
  };

  const handleConfirmDownloadQuotaWarning = () => {
    setShowDownloadQuotaWarning(false);
    handleStartBatch({ skipQuotaCheck: true });
  };

  const handleCancelDownloadQuotaWarning = () => {
    setShowDownloadQuotaWarning(false);
  };

  const buildUploadErrorInfo = useCallback((status?: number, detail?: unknown) => {
    const detailText =
      typeof detail === 'string'
        ? detail
        : detail
          ? String(detail)
          : '';
    if (status === 401) {
      return {
        title: "Authentication required",
        message: detailText || "Please log in and try again.",
      };
    }
    if (status === 403) {
      return {
        title: "Not authorized",
        message: detailText || "You don't have permission to upload these files.",
      };
    }
    if (status === 409) {
      return {
        title: "Upload blocked",
        message:
          detailText ||
          "Uploads are already pending. Finish the current batch (analysis + download or Direct Write) before starting a new upload.",
      };
    }
    if (status === 413) {
      const isStorageLimit = detailText.toLowerCase().includes("storage");
      return {
        title: isStorageLimit ? "Storage limit reached" : "File too large",
        message: detailText || "One or more files exceed the upload limits.",
      };
    }
    if (status === 422) {
      return {
        title: "Invalid upload request",
        message: detailText || "One or more files are missing required metadata.",
      };
    }
    if (status === 429) {
      return {
        title: "Uploads temporarily blocked",
        message: detailText || "Too many cancelled uploads. Try again later.",
      };
    }
    if (status && status >= 400 && status < 500) {
      return {
        title: "Upload not allowed",
        message: detailText || "One or more files do not meet the upload requirements.",
      };
    }
    if (status && status >= 500) {
      return {
        title: "Server error",
        message: detailText || "Unable to upload files right now. Please retry.",
      };
    }
    return {
      title: "Connection error",
      message: detailText || "Unstable connection. Please try again.",
    };
  }, []);

  const normalizedErrors = React.useMemo(() => {
    if (!batchStatus?.errors) {
      return undefined;
    }
    return batchStatus.errors.map((err) => ({
      file: err.file ?? 'Unknown file',
      message: err.message ?? 'Unknown error',
    }));
  }, [batchStatus?.errors]);

  function handleCloseAnalysisModePopup() {
    setShowAnalysisModePopup(false);
  }

  function handleCloseUploadValidationError() {
    setUploadValidationError(null);
    setUploadValidationTitle(null);
  }

  function toggleUploadPause() {
    setUploadPaused((prev) => {
      const next = !prev;
      uploadPausedRef.current = next;
      if (next && activeUploadControllerRef.current) {
        // Abort l'upload corrente per fermare subito il chunk in corso.
        activeUploadControllerRef.current.abort();
      }
      return next;
    });
  }

  function BatchUserPromptModal({
    prompt,
    promptError,
    onSend,
    selected,
    setSelected,
    setFullImageModal,
    processedCount,
    totalCount
  }: BatchUserPromptModalProps) {
    const [submitting, setSubmitting] = React.useState(false);
    const [clickInProgress, setClickInProgress] = React.useState(false);
    const [suppressWarning, setSuppressWarning] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const options = React.useMemo<PromptRenderOption[]>(() => {
      if (!prompt) {
        return [];
      }

      if (prompt.options && prompt.options.length > 0) {
        if (prompt.type === "choose_location") {
          const mapped = prompt.options.map((opt, idx) => {
            if (typeof opt === "object" && opt) {
              const token = 'token' in opt ? opt.token : undefined;
              const address = 'address' in opt ? opt.address : undefined;
              const latitude = 'latitude' in opt ? opt.latitude : undefined;
              const longitude = 'longitude' in opt ? opt.longitude : undefined;
              return {
                value: idx.toString(),
                label: `${token ?? idx}: ${address ?? "Unknown"} (${latitude ?? "?"}, ${longitude ?? "?"})`
              };
            }
            return { value: idx.toString(), label: String(opt) };
          });
          if (prompt.allow_manual) {
            mapped.push({ value: "manual", label: "[Type a location]" });
          }
          return mapped;
        }

        return prompt.options.map((opt, idx) => {
          if (typeof opt === "object" && opt) {
            const value = opt.value ?? idx.toString();
            const label = opt.label ?? String(value);
            return {
              value: String(value),
              label: String(label)
            };
          }
          return { value: String(opt), label: String(opt) };
        });
      }

      if (prompt.locations) {
        return prompt.locations.map((loc, idx) => ({
          value: idx.toString(),
          label: loc.address
        }));
      }

      if (prompt.modes) {
        return prompt.modes.map((mode, idx) => {
          if (typeof mode === "object" && mode) {
            const value = mode.value ?? idx.toString();
            const label = mode.label ?? String(value);
            return { value: String(value), label: String(label) };
          }
          return { value: String(mode), label: String(mode) };
        });
      }

      if (prompt.valid_options) {
        return prompt.valid_options.map((option) => ({ value: option, label: option }));
      }

      return [];
    }, [prompt]);

    // Mantieni il focus sull'input quando cambia il valore
    React.useEffect(() => {
      if (inputRef.current && options.length === 0) {
        inputRef.current.focus();
      }
    }, [options.length, selected, prompt]);

    const inputMaxLength = React.useMemo(() => {
      if (!prompt) return undefined;
      if (prompt.max_length && prompt.max_length > 0) {
        return prompt.max_length;
      }
      if (options.length === 0) {
        return 60;
      }
      return undefined;
    }, [prompt, options.length]);

    const handleSetSelected = React.useCallback((val: string) => {
      setSelected(val);
    }, [setSelected]);

    const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSelected(e.target.value);
    }, [setSelected]);

    const handleOk = async () => {
      if (
        !prompt ||
        !(
          selected ||
          prompt.type === "location_error" ||
          prompt.type === "no_location_found" ||
          prompt.type === "gps_warning"
        ) ||
        submitting ||
        clickInProgress
      ) {
        return;
      }

      setClickInProgress(true);
      setSubmitting(true);
      try {
        let choicePayload: PromptChoice = selected;
        if (prompt.type === 'gps_warning') {
          choicePayload = {
            value: selected || "Ok",
            suppress_warning: suppressWarning
          };
        }
        await onSend(prompt.type ?? "unknown", choicePayload);
      } finally {
        setSubmitting(false);
        setClickInProgress(false);
      }
    };

    const [imageLoading, setImageLoading] = React.useState(false);
    const [imageError, setImageError] = React.useState<string>("");
    const isManualTextPrompt = React.useMemo(
      () =>
        options.length === 0 &&
        (prompt?.type === "manual_input" || prompt?.type === "context_hint_manual_input"),
      [options.length, prompt?.type]
    );
    const thumbnailSrc = React.useMemo(
      () => prompt?.thumbnail_url || prompt?.thumbnail || null,
      [prompt?.thumbnail_url, prompt?.thumbnail]
    );

    const handleSkip = async () => {
      if (!prompt || submitting || clickInProgress) {
        return;
      }
      setClickInProgress(true);
      setSubmitting(true);
      try {
        await onSend(prompt.type ?? "unknown", "skip");
      } finally {
        setSubmitting(false);
        setClickInProgress(false);
      }
    };

    React.useEffect(() => {
      setImageError("");
      setImageLoading(false);
      setSuppressWarning(false);
    }, [prompt?.id]);

    const handleThumbnailClick = React.useCallback(async () => {
      if (imageLoading || !prompt) return;

      if (prompt.full_image_url) {
        setFullImageModal(prompt.full_image_url);
        return;
      }

      if (!prompt.full_image_blob) {
        setFullImageModal(prompt.full_image || thumbnailSrc || null);
        return;
      }

      try {
        setImageError("");
        setImageLoading(true);

        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          throw new Error("User not authenticated");
        }
        const idToken = await authService.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/prompt/image-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          },
          body: JSON.stringify({ blob_path: prompt.full_image_blob })
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image URL (status ${response.status})`);
        }

        const data = await response.json();
        if (!data?.url) {
          throw new Error("Malformed response from image endpoint");
        }

        setFullImageModal(data.url);
      } catch (err) {
        console.error("Unable to load full image", err);
        setImageError("Unable to load full image. Please try again.");
      } finally {
        setImageLoading(false);
      }
    }, [imageLoading, prompt, setFullImageModal, thumbnailSrc]);
    if (!prompt) {
      return null;
    }

    const fileLabel = React.useMemo(() => {
      if (!prompt?.current_file) return null;
      const parts = prompt.current_file.split(/[\\/]/);
      return parts[parts.length - 1] || prompt.current_file;
    }, [prompt?.current_file]);

    const positionLabel = React.useMemo(() => {
      if (typeof processedCount !== 'number' || typeof totalCount !== 'number' || totalCount <= 0) {
        return null;
      }
      const current = Math.min(processedCount + 1, totalCount);
      return `${current}/${totalCount}`;
    }, [processedCount, totalCount]);

    return (
      <>
        {(fileLabel || positionLabel) && (
          <div
            className="modal-handle"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              background: '#e5e7eb',
              borderRadius: 12,
              marginBottom: 12,
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#0f172a'
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fileLabel || 'Batch prompt'}
            </span>
            {positionLabel && (
              <span style={{ marginLeft: 12, color: '#334155', fontWeight: 600 }}>
                {positionLabel}
              </span>
            )}
          </div>
        )}
        {thumbnailSrc && (
          <div onClick={handleThumbnailClick} style={{ cursor: imageLoading ? 'progress' : 'pointer', textAlign: 'center', marginBottom: 10 }}>
            <img src={thumbnailSrc} alt="thumbnail" style={{ maxWidth: 150, maxHeight: 150, border: '1px solid #ccc', opacity: imageLoading ? 0.6 : 1 }} />
          </div>
        )}
        {imageLoading && <div style={{ textAlign: 'center', marginBottom: 10, fontSize: 12 }}>Loading full image…</div>}
        {imageError && <div style={{ color: 'red', textAlign: 'center', marginBottom: 10, fontSize: 12 }}>{imageError}</div>}
        <div style={{ marginBottom: 10, fontWeight: 'bold' }}>{prompt.message}</div>
        {promptError && <div style={{ color: 'red', marginBottom: 10 }}>{promptError}</div>}
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {options.length > 0 ? (
            options.map((opt) => (
              <div key={opt.value} style={{ marginBottom: 5 }}>
                <label>
                  <input
                    type="radio"
                    name="modalPrompt"
                    value={opt.value}
                    checked={selected === opt.value}
                    onChange={() => handleSetSelected(opt.value)}
                  />
                  {opt.label}
                </label>
              </div>
            ))
          ) : prompt.type !== 'gps_warning' ? (
            <>
              <input
                ref={inputRef}
                value={selected}
                onChange={handleInputChange}
                maxLength={inputMaxLength}
                style={{ width: '90%', padding: 8 }}
                placeholder="Inserisci valore..."
              />
              {inputMaxLength && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                  Max {inputMaxLength} characters
                </div>
              )}
            </>
          ) : null}
        </div>
        {prompt.type === 'gps_warning' && (
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={suppressWarning}
                onChange={(e) => setSuppressWarning(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Don't show this warning again for next images
            </label>
          </div>
        )}
        <div style={{ marginTop: 20, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {isManualTextPrompt && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              style={{ background: '#e2e8f0', color: '#0f172a' }}
            >
              Skip
            </button>
          )}
          <button
            onClick={handleOk}
            disabled={!(selected || ['location_error', 'no_location_found', 'gps_warning'].includes(prompt?.type ?? '')) || submitting}
          >
            {submitting ? "Invio..." : "OK"}
          </button>
        </div>
      </>
    );
  }

  const downloadSection = (
    <Suspense fallback={<div className="workspace__blank-state">Loading download panel…</div>}>
      <DownloadSection
        canDownload={canDownload}
        onDownloadClick={handleDownloadClick}
        compressionProgress={downloadBatchStatus?.compression_progress || 0}
        isDownloading={isDownloading}
        downloadAllIncludingHidden={downloadAllIncludingHidden}
        setDownloadAllIncludingHidden={setDownloadAllIncludingHidden}
        shouldShowDownloadAllToggle={shouldShowDownloadAllToggle}
        directWriteEnabled={isDirectWriteModeActive}
        directWriteReady={isDirectWriteReady}
        directWriteWriting={directWriteWriting}
        directWriteProgress={directWriteWriteProgress}
        directWriteFailures={directWriteFailures.length}
        directWriteHasFailure={directWriteHadFailure}
        directWriteFallback={isDirectWriteFallback}
        directWriteHighlight={directWriteHighlight}
        hasActiveSubscription={hasActiveSubscription}
        hasActiveBatch={Boolean(batchId)}
        onDirectWriteClick={() => handleDirectWriteApplyProcessed()}
        onRetryDirectWrite={() => handleDirectWriteApplyProcessed({ retryFailed: true })}
      />
    </Suspense>
  );

  const authModalSubtitle =
    authModalIntent === 'start'
      ? 'Please log in with one of the available methods before starting a new analysis.'
      : authModalIntent === 'upload'
        ? 'Please log in with one of the available methods before uploading files.'
        : 'Please log in with one of the available methods before continuing.';
  const analysisCompletionAction = isDirectWriteModeActive
    ? 'write the files on your device'
    : 'download the files';
  const analysisCompletionMessage = `Analysis completed. Review and adjust the markers on the map, then press the button to ${analysisCompletionAction}.`;
  const showStandardUploader = !directWriteEnabled && !showAdvancedUploader;
  const showAdvancedUploaderToggle = !directWriteEnabled && directWriteSupport.supported;
  const showDirectWriteUploader = directWriteSupport.supported && (directWriteEnabled || showAdvancedUploader);

  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const sideColumnWidth = isSideMenuCollapsed ? 'var(--side-width-collapsed)' : 'var(--side-width-expanded)';
  const downloadColumnWidth = isAccountCollapsed ? `${DOWNLOAD_BAR_COLLAPSED_WIDTH}px` : 'var(--download-width)';
  const appShellStyle = !isCompactLayout
    ? isMapFullscreen
      ? {
        gridTemplateColumns: `${sideColumnWidth} 1fr`,
        gridTemplateAreas: '"side workspace" "download download"',
      }
      : { gridTemplateColumns: `${sideColumnWidth} 1fr ${downloadColumnWidth}` }
    : undefined;

  return (
    <>
      {/* The prompt dialog is now rendered via ResponsiveModal. The backdrop close has been removed to prevent blocking the batch process when users accidentally dismiss the modal. */}
      <ResponsiveModal isOpen={hasPromptModal} variant="wide">
        <BatchUserPromptModal
          prompt={userPrompt}
          promptError={promptError}
          onSend={handleUserPromptChoice}
          selected={modalSelected}
          setSelected={setModalSelected}
          setFullImageModal={setModalImageUrl} // Temporarily reuse for prompts
          processedCount={batchStatus?.processed ?? null}
          totalCount={batchStatus?.total ?? batchStatus?.uploaded_files?.length ?? null}
        />
      </ResponsiveModal>

      {modalImageUrl && (
        <ImageModal imageUrl={modalImageUrl} onClose={handleCloseModal} />
      )}

      <Suspense fallback={null}>
        <GeminiProModal
          state={aiFallbackState}
          onPreviewFullImage={handleGeminiPreview}
        />
      </Suspense>

      <DraggableModal isOpen={!!uploadValidationError} onClose={handleCloseUploadValidationError}>
        <div className="modal-shell">
          <h4 className="modal-handle">{uploadValidationTitle || "Upload not allowed"}</h4>
          <p>
            {uploadValidationError ||
              "One or more files do not meet the upload requirements (size or type)."}
          </p>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--primary" onClick={handleCloseUploadValidationError}>
              Ok
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={!!draggedMarkerInfo} onClose={handleCancelDrag}>
        <div className="modal-shell">
          <h4 className="modal-handle">Confirm new location</h4>
          <p>Do you want to change GPS coordinates to new location?</p>
          <div className="modal-chip">{getChangeText(markerChangeCounts[draggedMarkerInfo?.markerKey || ''] || 0)}</div>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--ghost" onClick={handleCancelDrag}>
              Cancel
            </button>
            <button className="modal-btn modal-btn--primary" onClick={handleConfirmDrag}>
              Confirm
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={showAnalysisModePopup} onClose={handleCloseAnalysisModePopup}>
        <div className="modal-shell">
          <h4 className="modal-handle">Analysis mode required</h4>
          <p>Choose at least one image analysis mode (names recognition or AI Image Recognition) before starting.</p>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--primary" onClick={handleCloseAnalysisModePopup}>
              Understood
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={showAuthModal} onClose={handleCloseAuthModal}>
        <div className="modal-shell modal-shell--wide">
          <h4 className="modal-handle">🔐 Authentication Required</h4>
          <p>{authModalSubtitle}</p>
          <Suspense fallback={<div className="auth-modal__hint">Loading authentication…</div>}>
            <AuthModal
              onClose={handleCloseAuthModal}
              onLoginSuccess={handleAuthSuccess}
              onLoginError={handleAuthError}
              onSwitchToBetaSignup={handleSwitchToBetaSignup}
            />
          </Suspense>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={showBetaSignupModal} onClose={() => setShowBetaSignupModal(false)}>
        <div className="modal-shell">
          <Suspense fallback={<div>Loading…</div>}>
            <BetaSignupModal onClose={() => setShowBetaSignupModal(false)} />
          </Suspense>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={!!deleteConfirmAction} onClose={handleCloseDeleteConfirm}>
        {deleteConfirmAction && (
          <div className="modal-shell">
            <h4 className="modal-handle">Delete images</h4>
            <p>
              {deleteConfirmAction === "all"
                ? "Warning: you are about to delete all images on the server!"
                : "Warning: You are about to delete all images of the current session!"}
            </p>
            {deleteErrorMessage && (
              <p style={{ color: '#b91c1c', marginTop: 8 }}>{deleteErrorMessage}</p>
            )}
            <div className="modal-actions">
              <button className="modal-btn modal-btn--ghost" onClick={handleCloseDeleteConfirm} disabled={deleteLoading}>
                Cancel
              </button>
              <button className="modal-btn modal-btn--primary" onClick={handleConfirmDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Confirm'}
              </button>
            </div>
          </div>
        )}
      </DraggableModal>

      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onGoToPurchase={() => {
          setShowInsufficientCreditsModal(false);
          handleCloseDirectWritePicker();
          prefetchPurchaseModal();
          setShowPurchaseModal(true);
        }}
        requiredCredits={creditsInfo.required}
        availableCredits={creditsInfo.available}
      />

      <Suspense fallback={<div className="auth-modal__hint">Loading purchase options…</div>}>
        <PurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
        />
      </Suspense>

      <DraggableModal isOpen={!!historicalMovePrompt} onClose={handleHistoricalMovePromptCancel}>
        {historicalMovePrompt && (
          <div className="modal-shell">
            <h4 className="modal-handle">Move archived photo</h4>
            <p>
              This photo belongs to an earlier session. Use 1 credit to unlock up to 4 position changes.
              Do you want to continue?
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn--ghost" onClick={handleHistoricalMovePromptCancel}>
                Cancel
              </button>
              <button className="modal-btn modal-btn--primary" onClick={handleHistoricalMovePromptConfirm}>
                Confirm move
              </button>
            </div>
          </div>
        )}
      </DraggableModal>

      <DraggableModal
        isOpen={historicalMoveCreditsWarning}
        onClose={() => setHistoricalMoveCreditsWarning(false)}
      >
        <div className="modal-shell">
          <h4 className="modal-handle">Not enough credits</h4>
          <p>
            Not enough credits left.
            {' '}
            <button
              type="button"
              className="modal-link"
              onClick={() => {
                setHistoricalMoveCreditsWarning(false);
                prefetchPurchaseModal();
                setShowPurchaseModal(true);
              }}
            >
              Buy credits
            </button>
            {' '}
            to complete the action.
          </p>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--primary" onClick={() => setHistoricalMoveCreditsWarning(false)}>
              Close
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal isOpen={!!showLimitReached} onClose={handleCloseLimitReached}>
        <div className="modal-shell">
          <h4 className="modal-handle">Position change limit reached</h4>
          <p>Position changed 4 times. Analyse the file again to change position or use 1 credit to change position 4 more times.</p>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--ghost" onClick={handleCloseLimitReached}>
              Cancel
            </button>
            <button className="modal-btn modal-btn--primary" onClick={handleUseCreditAfterLimit}>
              Use 1 credit
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal
        isOpen={!!downloadLimitModalMessage}
        onClose={() => setDownloadLimitModalMessage(null)}
      >
        <div className="modal-shell">
          <h4 className="modal-handle">Download limit</h4>
          <p>{downloadLimitModalMessage ?? DEFAULT_DOWNLOAD_LIMIT_MESSAGE}</p>
          <div className="modal-actions">
            <button
              className="modal-btn modal-btn--primary"
              onClick={() => setDownloadLimitModalMessage(null)}
            >
              Got it
            </button>
          </div>
        </div>
      </DraggableModal>

      <DraggableModal
        isOpen={showDownloadQuotaWarning}
        onClose={handleCancelDownloadQuotaWarning}
      >
        <div className="modal-shell">
          <h4 className="modal-handle">Download quota warning</h4>
          <p>
            Warning: total file size exceeds the monthly download quota! Are you sure you want to proceed?
            You won't be able to download the files.
          </p>
          <p className="modal-note">
            Total files size: {formatBytesForDisplay(uploadedTotalBytes)} — Remaining download quota: {formatBytesForDisplay(downloadAllowanceBytes)}
          </p>
          <div className="modal-actions">
            <button className="modal-btn modal-btn--ghost" onClick={handleCancelDownloadQuotaWarning}>
              Cancel
            </button>
            <button className="modal-btn modal-btn--primary" onClick={handleConfirmDownloadQuotaWarning}>
              Proceed anyway
            </button>
          </div>
        </div>
      </DraggableModal>

      <BatchRestoreModal
        isOpen={showBatchRestoreModal}
        batchInfo={storedBatchInfo}
        onRestore={handleSecureBatchRestore}
        onClose={handleCloseBatchRestoreModal}
        onClearBatch={handleIgnoreStoredBatch}
      />

      <DirectWritePickerModal
        isOpen={directWritePickerOpen}
        entries={directWriteScanItems}
        scanning={directWriteScanStatus === 'scanning'}
        warnings={directWriteScanWarnings}
        ignoredFiles={directWriteIgnoredFiles}
        ignoredCount={directWriteIgnoredCount}
        rootName={directWriteHandleName}
        rootHandle={directWriteHandleRef.current}
        platform={directWriteSupport.platform}
        onClose={handleCloseDirectWritePicker}
        onConfirm={handleDirectWriteSelectionConfirm}
      />

      <DraggableModal
        isOpen={cancelUploadsConfirmOpen}
        onClose={() => setCancelUploadsConfirmOpen(false)}
      >
        <div className="modal-shell">
          <h4 className="modal-handle">Cancel current uploads</h4>
          <p>This will remove uploaded files for the current batch.</p>
          {uploadCancelCooldownMinutes ? (
            <p className="modal-note">
              Uploads are temporarily blocked. Try again in{' '}
              <span className="notranslate">{uploadCancelCooldownMinutes}</span> minutes.
            </p>
          ) : uploadCancelRemainingAfterConfirm !== null ? (
            <p className="modal-note">
              {uploadCancelRemainingAfterConfirm === 0 ? (
                <>If you cancel now, you will need to wait {UPLOAD_CANCEL_COOLDOWN_MINUTES} minutes before uploading new files.</>
              ) : (
                <>
                  After this cancellation, you can cancel uploads{' '}
                  <strong className="notranslate">{uploadCancelRemainingAfterConfirm}</strong>{' '}
                  {uploadCancelRemainingAfterConfirm === 1 ? 'more time' : 'more times'} before a{' '}
                  {UPLOAD_CANCEL_COOLDOWN_MINUTES}-minute cooldown starts.
                </>
              )}
            </p>
          ) : null}
          {cancelUploadsError && (
            <p style={{ color: '#b91c1c', marginTop: 8 }}>{cancelUploadsError}</p>
          )}
          <div className="modal-actions">
            <button
              className="modal-btn modal-btn--ghost"
              onClick={() => setCancelUploadsConfirmOpen(false)}
              disabled={cancelUploadsLoading}
            >
              Keep uploaded files
            </button>
            <button
              className="modal-btn modal-btn--primary"
              onClick={handleConfirmUploadCancel}
              disabled={cancelUploadsLoading}
            >
              {cancelUploadsLoading ? 'Cancelling…' : 'Ok'}
            </button>
          </div>
        </div>
      </DraggableModal>



      {!shouldHideShellForPrerender && (
        <div
          className={`App app-shell ${!isCompactLayout && isSideMenuCollapsed ? 'app-shell--collapsed' : ''} ${!isCompactLayout && isMapFullscreen ? 'app-shell--map-fullscreen' : ''}`}
          style={appShellStyle}
        >
          {isCompactLayout && (
            <header className="workspace__topbar">
              <ResponsiveTopbar
                onToggleConfigDrawer={() => setIsConfigDrawerOpen(!isConfigDrawerOpen)}
                onToggleDownloadDrawer={() => setIsDownloadDrawerOpen(!isDownloadDrawerOpen)}
              />
            </header>
          )}

          {(!isCompactLayout || isConfigDrawerOpen) && (
            <aside style={isCompactLayout ? {} : { gridArea: 'side' }}>
              <SideMenu
                formState={formState}
                onFormChange={setFormState}
                isCollapsed={isCompactLayout ? false : isSideMenuCollapsed}
                onToggleCollapsed={() => setIsSideMenuCollapsed(prev => !prev)}
                variant={isCompactLayout ? 'drawer' : 'fixed'}
                isOpen={!isCompactLayout || isConfigDrawerOpen}
                onClose={() => setIsConfigDrawerOpen(false)}
                autoZoomEnabled={autoZoomAfterAnalysis}
                onToggleAutoZoom={setAutoZoomAfterAnalysis}
                directWriteSupport={directWriteSupport}
                directWriteToggleBlocked={shouldBlockDirectWriteToggle}
                onDirectWriteToggleBlocked={handleDirectWriteToggleBlocked}
              />
            </aside>
          )}

          <main style={{ gridArea: 'workspace' }} className="workspace">
            <div className="workspace__grid">
              {/* Left Column: Upload + Downloads */}
              {/* Left Column Wrapper Removed - Items are now direct grid children */}
              <div className="workspace__uploader-area">
                <div className="workspace__logo-wrapper">
                  <img src={logoImg} alt="Smart Geotag" className="workspace__logo-banner" />
                </div>

                <div className="workspace__card workspace__card--uploader">
                  <div className="control-panel">
                    <div className="control-panel__upload">
                      <div className="workspace__card-header">
                        <div>
                          <p className="workspace__eyebrow">Upload</p>
                          <h3>Files</h3>
                        </div>
                        {uploadedFileCount > 0 && (
                          <div className="workspace__chip-row">
                            <span className="workspace__chip"><span className="notranslate">{uploadedFileCount}</span> files</span>
                            <span className="workspace__chip workspace__chip--soft"><span className="notranslate">{uploadedTotalSize}</span> MB</span>
                          </div>
                        )}
                      </div>
                      {showStandardUploader && (
                        <>
                          <DragDropUploader
                            onFilesUploaded={handleFilesUploaded}
                            onTriggerFolderPicker={handleTriggerFolderPicker}
                            onCheckCredits={checkCreditsBeforeUpload}
                            onCheckUploadsAllowed={checkUploadsAllowed}
                            clearToken={uploadClearToken}
                            disabled={loading}
                            isAuthenticated={isUserAuthenticated}
                            onRequireAuth={handleRequireAuthForUpload}
                          />
                          {showAdvancedUploaderToggle && (
                            <button
                              type="button"
                              className="upload-toggle-link"
                              onClick={() => setShowAdvancedUploader(true)}
                            >
                              Use Advanced Uploader
                            </button>
                          )}
                        </>
                      )}
                      {showStandardUploader && !isUploadSectionReset && (filesUploaded || batchId) && (
                        <button
                          type="button"
                          className="workspace__start-btn workspace__start-btn--ghost"
                          onClick={() => {
                            setCancelUploadsError(null);
                            setCancelUploadsConfirmOpen(true);
                          }}
                          disabled={loading || uploadStatus === 'uploading' || cancelUploadsLoading || !isUploadCancelable}
                          style={{ marginTop: '0.5rem' }}
                        >
                          Clear uploads
                        </button>
                      )}
                      {showDirectWriteUploader && (
                        <>
                          <div className="upload-direct-write">
                            <div className="upload-direct-write__header">
                              <span className="workspace__eyebrow">Direct Write</span>
                              <h4>Choose Folder to Geotag</h4>
                            </div>
                            <button
                              type="button"
                              className="workspace__start-btn workspace__start-btn--ghost"
                              onClick={handleLoadDirectWriteFiles}
                              disabled={
                                loading ||
                                uploadStatus === 'uploading' ||
                                directWriteLoading ||
                                directWritePreparing ||
                                cancelUploadsLoading
                              }
                            >
                              {directWriteLoading ? "Loading..." : "Choose Folder to Geotag"}
                            </button>
                            {(directWriteHandleName || filesUploaded) && (
                              <button
                                type="button"
                                className="workspace__start-btn workspace__start-btn--ghost"
                                onClick={() => {
                                  if (!filesUploaded && !batchId) {
                                    handleClearDirectWrite();
                                    return;
                                  }
                                  setCancelUploadsError(null);
                                  setCancelUploadsConfirmOpen(true);
                                }}
                                disabled={
                                  directWriteLoading ||
                                  directWritePreparing ||
                                  uploadStatus === 'uploading' ||
                                  cancelUploadsLoading ||
                                  !isUploadCancelable
                                }
                                style={{ marginTop: '0.5rem' }}
                              >
                                Clear uploads
                              </button>
                            )}
                            {directWriteStatus && (
                              <p className="upload-direct-write__status">{directWriteStatus}</p>
                            )}
                            {directWriteUploadsLocked && !directWriteStatus && (
                              <p className="upload-direct-write__status upload-direct-write__status--warning">
                                {uploadBlockInfo?.message ||
                                  "Uploads already loaded. Clear uploads to choose a new folder."}
                              </p>
                            )}
                            {directWriteSelectedCount > 0 && !directWritePreparing && (
                              <p className="upload-direct-write__status">
                                <span className="notranslate">{directWriteSelectedCount}</span> files selected for upload.
                              </p>
                            )}
                            {directWritePreparing && (
                              <div className="upload-progress" style={{ marginTop: '0.75rem' }}>
                                <div className="upload-progress__label">
                                  <span>Preparing files...</span>
                                  <span className="upload-progress__percent">
                                    <span className="notranslate">{directWritePrepareProgress}</span>%
                                  </span>
                                </div>
                                <div className="upload-progress__bar">
                                  <div
                                    className="upload-progress__fill"
                                    style={{ width: `${directWritePrepareProgress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            {!directWriteHandleName && !directWriteStatus && (
                              <p className="upload-direct-write__status upload-direct-write__status--warning">
                                Pick a folder to load files and write in-place.
                              </p>
                            )}
                          </div>
                          {showAdvancedUploaderToggle && showAdvancedUploader && (
                            <button
                              type="button"
                              className="upload-toggle-link"
                              onClick={() => setShowAdvancedUploader(false)}
                            >
                              Use Standard Uploader
                            </button>
                          )}
                        </>
                      )}
                      <div className="upload-status">
                        {uploadStatus === 'uploading' && (
                          <div className="upload-progress">
                            <div className="upload-progress__label">
                              <span>Uploading...</span>
                              <span className="upload-progress__percent">
                                <span className="notranslate">{uploadProgress}</span>%
                              </span>
                              {uploadPaused && <span className="upload-progress__paused">Paused</span>}
                              <button
                                type="button"
                                className={`upload-progress__toggle ${uploadPaused ? 'is-paused' : ''}`}
                                onClick={toggleUploadPause}
                                aria-label={uploadPaused ? 'Resume upload' : 'Pause upload'}
                                title={uploadPaused ? 'Resume upload' : 'Pause upload'}
                              >
                                {uploadPaused ? (
                                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path d="M8 5v14l11-7z"></path>
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path d="M7 5h4v14H7zM13 5h4v14h-4z"></path>
                                  </svg>
                                )}
                              </button>
                            </div>
                            <div className="upload-progress__bar">
                              <div
                                className="upload-progress__fill"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {uploadStatus === 'done' && (
                          <div className="upload-progress__done">Files uploaded!</div>
                        )}
                        {authError && (
                          <div className="error-message" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            {authError}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="control-panel__actions">
                      <div className="control-panel__cta" ref={startAnalysisRef} data-tour="start-analysis">
                        <div className="workspace__card-header">
                          <p className="workspace__eyebrow">Analysis</p>
                          <h3>Action</h3>
                        </div>
                        <button
                          className="workspace__start-btn"
                          onClick={() => handleStartBatch()}
                          disabled={loading || uploadStatus !== 'done'}
                        >
                          {loading ? "Starting..." : "Start analysis"}
                        </button>
                        <div className="workspace__status">
                          {startMsg && <span className="status-chip status-chip--success">{startMsg}</span>}
                          {errorMsg && <span className="status-chip status-chip--error">{errorMsg}</span>}
                        </div>
                      </div>

                      <div className="control-panel__progress">
                        <div className="workspace__card-header">
                          <div>
                            <p className="workspace__eyebrow">Progress</p>
                            <h3></h3>
                          </div>
                          {batchStatus?.processed !== undefined && !isUploadSectionReset && (
                            <span className="workspace__chip">
                              <span className="notranslate">{batchStatus.processed}/{batchStatus.total ?? '?'}</span>
                            </span>
                          )}
                        </div>
                        {batchStatus && !isUploadSectionReset ? (
                          <BatchProgressBar
                            progress={batchStatus.progress}
                            processed={batchStatus.processed}
                            total={batchStatus.total}
                            currentFile={batchStatus.currentFile}
                            errors={normalizedErrors}
                          />
                        ) : (
                          <div className="workspace__blank-state">Waiting...</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Downloads Section moved to Left Column */}
              <div className="workspace__downloads-area">
                <div className="workspace__download-card">
                  {downloadSection}
                </div>
              </div>


              {/* Right Column: Map + Footer */}
              <div className="workspace__map-column">
                <div className="workspace__map-area">
                  <section className="workspace__map-card" ref={mapSectionRef}>
                    <header className="workspace__card-header">
                      <div>
                        <p className="workspace__eyebrow">Geospatial review</p>
                        <h3>Interactive map</h3>
                      </div>
                      <Toolbar>
                        <span className="workspace__chip"><span className="notranslate">{visibleMarkers.length}</span>&nbsp;markers on map</span>

                        {/* Unmarked Images Dropdown */}
                        <div className="delete-options">
                          <span
                            className={`workspace__chip workspace__chip--action ${showUnmarkedDropdown ? 'is-active' : ''} ${unmarkedCount > 0 ? '' : 'workspace__chip--muted'}`}
                            onClick={() => unmarkedCount > 0 && setShowUnmarkedDropdown(!showUnmarkedDropdown)}
                            style={{ cursor: unmarkedCount > 0 ? 'pointer' : 'default' }}
                          >
                            <span className="notranslate">{unmarkedCount}</span>&nbsp;unmarked images
                            {unmarkedCount > 0 && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, transform: showUnmarkedDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            )}
                          </span>

                          {showUnmarkedDropdown && (
                            <div className="delete-options__menu">
                              <button
                                className="delete-options__item"
                                onClick={() => {
                                  setShowUnmarkedImagesModal(true);
                                  setShowUnmarkedDropdown(false);
                                }}
                              >
                                <span className="delete-options__icon">🖼️</span>
                                <div className="delete-options__text">
                                  <strong>Show images</strong>
                                  <small>View thumbnails of unmarked files</small>
                                </div>
                              </button>

                              <button
                                className="delete-options__item"
                                onClick={() => {
                                  setShowDeleteUnmarkedConfirm(true);
                                  setShowUnmarkedDropdown(false);
                                }}
                              >
                                <span className="delete-options__icon">🗑️</span>
                                <div className="delete-options__text">
                                  <strong>Delete unmarked images</strong>
                                  <small>Remove files without GPS</small>
                                </div>
                              </button>

                              <button
                                className="delete-options__item"
                                onClick={() => {
                                  setDownloadUnmarkedOnly(!downloadUnmarkedOnly);
                                  setShowUnmarkedDropdown(false);
                                }}
                              >
                                <span className="delete-options__icon">{downloadUnmarkedOnly ? '👁️' : '📥'}</span>
                                <div className="delete-options__text">
                                  <strong>{downloadUnmarkedOnly ? 'Show all files' : 'Download unmarked only'}</strong>
                                  <small>{downloadUnmarkedOnly ? 'Restore normal view' : 'Hide markers & download unmarked'}</small>
                                </div>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Delete Unmarked Confirmation Modal */}
                        {showDeleteUnmarkedConfirm && (
                          <div className="modal-overlay">
                            <div className="modal-shell">
                              <h3>Delete unmarked images?</h3>
                              <p>
                                Are you sure you want to delete all <strong>{unmarkedCount}</strong> images that don't have GPS coordinates?
                                <br /><br />
                                This action cannot be undone.
                              </p>
                              <div className="modal-actions">
                                <button
                                  className="modal-btn modal-btn--ghost"
                                  onClick={() => setShowDeleteUnmarkedConfirm(false)}
                                  disabled={isDeletingUnmarked}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="modal-btn modal-btn--primary status-chip--error"
                                  onClick={handleDeleteUnmarked}
                                  disabled={isDeletingUnmarked}
                                >
                                  {isDeletingUnmarked ? 'Deleting...' : 'Confirm Delete'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Unmarked Images Modal */}
                        {showUnmarkedImagesModal && (
                          <UnmarkedImagesModal
                            markers={unmarkedMarkers}
                            onClose={() => setShowUnmarkedImagesModal(false)}
                            onView={(marker) => {
                              handleOpenModal(marker);
                            }}
                            onDelete={(marker) => {
                              setDeleteDialog({ kind: "single", marker });
                            }}
                            onCreateMarker={(marker) => {
                              handleCreateMarkerFromUnmarked(marker);
                            }}
                          />
                        )}

                        {hasActiveSubscription && (
                          <span className="workspace__chip">
                            {historicalMarkersLoading ? 'Syncing history…' : `${markedCount} marked`}
                          </span>
                        )}
                        {historicalMarkersError && (
                          <span className="workspace__chip">History unavailable</span>
                        )}
                        {hasActiveSubscription && (
                          <button
                            type="button"
                            className={`workspace__chip workspace__chip--action ${isSessionFilterActive ? 'is-active' : ''}`}
                            onClick={handleToggleSessionFilter}
                          >
                            {isSessionFilterActive ? 'Show all sessions' : 'Current session only'}
                          </button>
                        )}
                        {hasActiveSubscription && (
                          <button
                            type="button"
                            className={`workspace__chip workspace__chip--action ${isDuplicateMode ? 'is-active' : ''}`}
                            onClick={handleToggleDuplicateMode}
                          >
                            {isDuplicateMode ? 'Exit duplicate mode' : 'Find identical images'}
                          </button>
                        )}
                        {hasActiveSubscription && (
                          <div className="delete-options">
                            <button
                              type="button"
                              className="workspace__chip workspace__chip--action"
                              onClick={() => setShowDeleteOptions(prev => !prev)}
                            >
                              Delete options
                            </button>
                            {showDeleteOptions && (
                              <div className="delete-options__menu">
                                <button
                                  type="button"
                                  className="workspace__chip workspace__chip--action delete-options__item"
                                  onClick={() => handleOpenDeleteConfirm("all")}
                                >
                                  Delete all images
                                </button>
                                <button
                                  type="button"
                                  className="workspace__chip workspace__chip--action delete-options__item"
                                  onClick={() => handleOpenDeleteConfirm("session")}
                                  disabled={!batchId}
                                >
                                  Delete images from this session
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {isDuplicateMode && (
                          <span className="workspace__chip">
                            {activeDuplicateGroup
                              ? `${activeDuplicateGroup.groupSize} photos selected`
                              : `${duplicateMarkersForDisplay.length} duplicate photos`}
                          </span>
                        )}
                        {draggedMarkerInfo && (
                          <span className="workspace__chip workspace__chip--warning">Relocating</span>
                        )}
                      </Toolbar>
                      {isDuplicateMode && (
                        <div className="duplicate-panel">
                          {duplicateGroupsLoading ? (
                            <p>Loading duplicate groups…</p>
                          ) : duplicateGroupsError ? (
                            <p className="status-chip status-chip--error">{duplicateGroupsError}</p>
                          ) : duplicateGroupsForDisplay.length ? (
                            <>
                              <div className="duplicate-panel__actions">
                                <button
                                  type="button"
                                  className={`workspace__chip workspace__chip--action ${activeDuplicateHash === null ? 'is-active' : ''}`}
                                  onClick={() => handleSelectDuplicateGroup(null)}
                                >
                                  Show all ({duplicateMarkersForDisplay.length})
                                </button>
                              </div>
                              <div className="duplicate-panel__groups">
                                {duplicateGroupsForDisplay.map((group) => (
                                  <button
                                    type="button"
                                    key={group.contentHash}
                                    className={`workspace__chip workspace__chip--action ${activeDuplicateHash === group.contentHash ? 'is-active' : ''
                                      }`}
                                    onClick={() => handleSelectDuplicateGroup(group.contentHash)}
                                  >
                                    {group.groupSize}× {group.contentHash.slice(0, 8)}
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : (
                            <p>No duplicate groups detected yet.</p>
                          )}
                        </div>
                      )}
                      {enableSemanticSearch && hasActiveSubscription && (
                        <div className="semantic-search-section">
                          {isMobileLayout && (
                            <button
                              type="button"
                              className={`workspace__chip workspace__chip--action ${isSemanticSearchExpanded ? 'is-active' : ''}`}
                              onClick={() => setIsSemanticSearchExpanded(!isSemanticSearchExpanded)}
                              style={{
                                marginBottom: isSemanticSearchExpanded ? 8 : 0,
                                width: '100%',
                                justifyContent: 'center',
                                ...(isSemanticSearchExpanded ? {
                                  background: 'rgba(255, 255, 255, 0.05)', // Very subtle transparent background
                                  borderColor: 'transparent',
                                  color: 'var(--color-primary)',
                                  boxShadow: 'none'
                                } : {})
                              }}
                            >
                              {isSemanticSearchExpanded ? 'Hide semantic search ▲' : 'Semantic search ▼'}
                            </button>
                          )}

                          {(!isMobileLayout || isSemanticSearchExpanded) && (
                            <>
                              <div className="semantic-search-bar">
                                <div className="semantic-search-bar__inputs">
                                  <input
                                    type="text"
                                    value={semanticQuery}
                                    onChange={(e) => setSemanticQuery(e.target.value)}
                                    placeholder="Search photos by meaning…"
                                    className="semantic-search-bar__input"
                                  />
                                  <select
                                    className="semantic-search-bar__select"
                                    value={semanticFilters.country || ''}
                                    onChange={(e) =>
                                      setSemanticFilters((prev) => ({
                                        ...prev,
                                        country: e.target.value || undefined,
                                      }))
                                    }
                                  >
                                    <option value="">Any country</option>
                                    {semanticOptions.countries.map((country) => (
                                      <option key={country} value={country}>
                                        {country}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    className="semantic-search-bar__select"
                                    value={semanticFilters.locality || ''}
                                    onChange={(e) =>
                                      setSemanticFilters((prev) => ({
                                        ...prev,
                                        locality: e.target.value || undefined,
                                      }))
                                    }
                                  >
                                    <option value="">Any locality</option>
                                    {semanticOptions.localities.map((loc) => (
                                      <option key={loc} value={loc}>
                                        {loc}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="workspace__chip workspace__chip--action"
                                    onClick={() => handleSemanticSearch(null, false)}
                                    disabled={semanticLoading}
                                  >
                                    {semanticLoading ? 'Searching…' : 'Search'}
                                  </button>
                                  {semanticModeActive && (
                                    <button
                                      type="button"
                                      className="workspace__chip workspace__chip--action"
                                      onClick={handleClearSemanticSearch}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="semantic-search-bar__meta">
                                  {semanticError && (
                                    <span className="status-chip status-chip--error">{semanticError}</span>
                                  )}
                                  {semanticModeActive && !semanticError && (
                                    <span className="workspace__chip workspace__chip--soft">
                                      {semanticResults.length
                                        ? `${semanticResults.length} results`
                                        : 'No matches yet'}
                                    </span>
                                  )}
                                  {semanticModeType === 'image' && moreLikeAnchor && (
                                    <>
                                      <span className="workspace__chip workspace__chip--soft">
                                        More like {moreLikeAnchor.filename} ({visibleMoreLikeCount} similar)
                                      </span>
                                      <button
                                        type="button"
                                        className="workspace__chip workspace__chip--action"
                                        onClick={handleShowMoreSimilar}
                                        disabled={visibleMoreLikeCount >= moreLikeThisCandidates.length || visibleMoreLikeCount >= 50}
                                      >
                                        {visibleMoreLikeCount >= moreLikeThisCandidates.length || visibleMoreLikeCount >= 50
                                          ? "No more similar images found"
                                          : "Even more like this"}
                                      </button>
                                    </>
                                  )}
                                  {semanticHasMore && semanticModeType !== 'image' && (
                                    <button
                                      type="button"
                                      className="workspace__chip workspace__chip--action"
                                      onClick={handleSemanticLoadMore}
                                      disabled={semanticLoading}
                                    >
                                      Load more
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div
                                className="date-filter-controls"
                                style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}
                              >
                                <span className="workspace__chip workspace__chip--soft">{dateFilterSummary}</span>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ fontSize: '0.85rem' }}>From</span>
                                  <input
                                    type="date"
                                    className="semantic-search-bar__input"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ fontSize: '0.85rem' }}>To</span>
                                  <input
                                    type="date"
                                    className="semantic-search-bar__input"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input
                                    type="checkbox"
                                    checked={includeUnknownDates}
                                    onChange={(e) => setIncludeUnknownDates(e.target.checked)}
                                  />
                                  <span style={{ fontSize: '0.85rem' }}>Include unknown dates</span>
                                </label>
                                <button
                                  type="button"
                                  className="workspace__chip workspace__chip--action"
                                  onClick={handleClearDateFilter}
                                >
                                  Clear date filter
                                </button>
                              </div>
                            </>
                          )}
                          {shouldShowDownloadAllToggle && null}
                        </div>
                      )}
                    </header>

                    <div className="map-shell">
                      <Suspense fallback={<div className="map-loading">Loading map…</div>}>
                        <MapView
                          markers={visibleMarkers}
                          onImageClick={handleOpenModal}
                          onMarkerDragEnd={handleMarkerDragEnd}
                          onRequestDeleteMarker={handleRequestDeleteMarker}
                          onRequestDeleteDuplicates={handleRequestDeleteDuplicates}
                          isDuplicateMode={isDuplicateMode}
                          onRequestMoveMarker={handleRequestMoveMarker}
                          onRequestMoreLikeThis={handleMoreLikeThis}
                          showMoreLikeThis={showMoreLikeThis}
                          onFullscreenChange={setIsMapFullscreen}
                          pendingMoveTarget={pendingMoveTarget}
                          onConsumePendingMove={() => setPendingMoveTarget(null)}
                          sourceMarkerId={moreLikeAnchor?.id}
                          onCrosshairModeChange={(active) => {
                            if (active && mapSectionRef.current) {
                              const rect = mapSectionRef.current.getBoundingClientRect();
                              const target = window.scrollY + rect.bottom - window.innerHeight;
                              if (target > 0) {
                                window.scrollTo({ top: target, behavior: 'smooth' });
                              }
                            }
                          }}
                          autoFitToken={mapAutoFitToken}
                          fitMarkers={mapAutoFitMarkers}
                          isAutoZoomEnabled={autoZoomAfterAnalysis}
                        />
                      </Suspense>
                    </div>
                  </section>
                </div >
              </div >
              <Suspense fallback={null}>
                <div className="workspace__footer-area">
                  <Footer onOpenDoc={handleOpenDoc} />
                </div>
              </Suspense>
            </div>
          </main >
          {(!isCompactLayout || isDownloadDrawerOpen) && (
            <aside style={isCompactLayout ? {} : { gridArea: 'download' }}>
              <DownloadBar
                setShowPurchaseModal={setShowPurchaseModal}
                remainingDownloadBytes={downloadAllowanceBytes}
                usedDownloadBytes={typeof monthlyDownloadUsage === 'number' ? monthlyDownloadUsage : null}
                downloadLimitBytes={DOWNLOAD_LIMIT_BYTES}
                variant={isCompactLayout ? 'drawer' : 'fixed'}
                isOpen={!isCompactLayout || isDownloadDrawerOpen}
                onClose={() => setIsDownloadDrawerOpen(false)}
                isCollapsed={isCompactLayout ? false : isAccountCollapsed}
                onToggleCollapsed={() => setIsAccountCollapsed(prev => !prev)}
                hasActiveSubscription={hasActiveSubscription}
                themePreference={themePreference}
                effectiveTheme={themePreference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themePreference}
                onThemeChange={setThemePreference}
                onRequestAuthModal={handleRequireAuthFromAccount}
                onPrefetchPurchaseModal={prefetchPurchaseModal}
                storageUsageBytes={storageUsageBytes}
                storageCapBytes={UPLOAD_STORAGE_CAP_BYTES}
              />
            </aside>
          )}
        </div >
      )}

      {directWriteReportOpen && (
        <div className="modal-overlay">
          <div className="modal-shell" role="dialog" aria-modal="true">
            <h4>Direct Write report</h4>
            {directWriteWriterMessage && (
              <p className="upload-direct-write__status">{directWriteWriterMessage}</p>
            )}
            <p className="upload-direct-write__status">
              {directWriteWriteReport.filter((r) => r.status === "written" || (r.status === "failed" && !r.error)).length} written
              {directWriteWriteReport.some((r) => r.status === "failed" && r.error)
                ? `, ${directWriteWriteReport.filter((r) => r.status === "failed" && r.error).length} failed`
                : ""}.
            </p>
            {directWriteFailures.length > 0 && (
              <ul className="upload-direct-write__status upload-direct-write__list">
                {directWriteFailures.map((res) => (
                  <li key={`${res.id}-${res.targetRelativePath}`}>{res.targetRelativePath}: {res.error || "Failed"}</li>
                ))}
              </ul>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn--primary"
                onClick={() => setDirectWriteReportOpen(false)}
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}

      {analysisCompleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-shell" role="dialog" aria-modal="true">
            <h4>Analysis completed</h4>
            <p>{analysisCompletionMessage}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn--primary"
                onClick={() => setAnalysisCompleteModalOpen(false)}
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <DocModal
          isOpen={isDocModalOpen}
          onClose={handleCloseDoc}
          docName={currentDoc?.name || ''}
          title={currentDoc?.title || ''}
        />
      </Suspense>

      <DraggableModal isOpen={!!deleteDialog} onClose={handleCloseDeleteDialog}>
        {deleteDialog && (
          <div className="modal-shell">
            <h4 className="modal-handle">
              {deleteDialog.kind === "single" ? "Delete photo" : "Delete duplicates"}
            </h4>
            <p>
              {deleteDialog.kind === "single"
                ? (deleteDialog.marker.unmarked
                  ? "Are you sure you want to delete this image? This action cannot be undone."
                  : "Choose whether to hide this marker or delete the processed image from storage.")
                : "Remove every duplicate that shares this content hash while keeping the selected photo."}
            </p>
            {deleteError && <p className="status-chip status-chip--error">{deleteError}</p>}
            {deleteDialog.kind === "single" ? (
              <div className="modal-actions modal-actions--stacked">
                <button
                  className="modal-btn modal-btn--ghost"
                  onClick={handleCloseDeleteDialog}
                  disabled={isDeletingMarker}
                >
                  Cancel
                </button>
                {!deleteDialog.marker.unmarked && (
                  <button
                    className="modal-btn modal-btn--ghost"
                    onClick={() => executeDeleteAction("marker_only")}
                    disabled={isDeletingMarker}
                  >
                    {isDeletingMarker ? "Removing…" : "Remove marker only"}
                  </button>
                )}
                <button
                  className="modal-btn modal-btn--primary"
                  onClick={() => executeDeleteAction("delete_blob")}
                  disabled={isDeletingMarker}
                >
                  {isDeletingMarker ? "Deleting…" : (deleteDialog.marker.unmarked ? "Confirm" : "Delete image from storage")}
                </button>
              </div>
            ) : (
              <div className="modal-actions modal-actions--stacked">
                <button
                  className="modal-btn modal-btn--ghost"
                  onClick={handleCloseDeleteDialog}
                  disabled={isDeletingMarker}
                >
                  Cancel
                </button>
                <button
                  className="modal-btn modal-btn--primary"
                  onClick={() => executeDeleteAction("delete_blob")}
                  disabled={isDeletingMarker}
                >
                  {isDeletingMarker ? "Deleting…" : "Delete all duplicates"}
                </button>
              </div>
            )}
          </div>
        )}
      </DraggableModal>
    </>
  );
};



// App principale - mostra sempre l'applicazione con autenticazione integrata nella barra destra
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/auth/complete" element={<AuthComplete />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/guide/:docId" element={<Dashboard />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/" element={<Dashboard />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
